import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, MoreVertical, Pencil, Trash2, Plus, Calendar, Package2, ShoppingCart, FileText, ClipboardList, MapPin, ChevronDown, ChevronRight, Image as ImageIcon, Mail, Send, TrendingUp } from 'lucide-react';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import BookingCustomerInfo from './booking/BookingCustomerInfo';
import SelectCustomerModal from './manage/SelectCustomerModal';
import EditCustomerModal from './manage/EditCustomerModal';
import CreateProposalModal from './CreateProposalModal';
import ProposalDetailsModal from './ProposalDetailsModal';
import ScheduleModal from './ScheduleModal';
import ScheduleFieldQuoteModal from './ScheduleFieldQuoteModal';
import SelectServiceModal from './manage/SelectServiceModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import CreateVehicleModal from './CreateVehicleModal';
import { getCustomer, Customer, CustomerVehicle } from '../lib/customers';
import { getProposal, Proposal } from '../lib/proposals';
import { formatCurrency } from '../lib/utils';
import { format } from 'date-fns';
import { useBusiness } from '../contexts/BusinessContext';
import { triggerGcalSync } from '../lib/bookings';
import { useAppSystem } from '../hooks/useAppSystem';
import { getProjectServices, addProjectService, removeProjectService, ProjectService } from '../lib/projectServices';
import RichTextDescription from './RichTextDescription';
import EditServiceModal from './booking/EditServiceModal';
import ProjectServiceItem from './ProjectServiceItem';
import { Clock } from 'lucide-react';
import { getProjectMeasurements, addProjectMeasurement, updateProjectMeasurement, deleteProjectMeasurement, ProjectMeasurement, AddProjectMeasurementParams, UpdateProjectMeasurementParams } from '../lib/projectMeasurements';
import MeasurementItem from './MeasurementItem';
import AddMeasurementModal from './AddMeasurementModal';
import ProjectPhotos from './ProjectPhotos';
import { Payment } from '../lib/payments';
import CreateBookingModal from './CreateBookingModal';
import CreateMobileBookingModal from './CreateMobileBookingModal';
import BookingTypeSelectionModal from './BookingTypeSelectionModal';
import { createBooking } from '../lib/bookings';
import { usePermissions } from '../contexts/PermissionContext';
import RequestQuoteModal from './RequestQuoteModal';
import RequestQuoteFromProjectModal from './RequestQuoteFromProjectModal';
import PlaceOrderFromMeasurementsModal from './PlaceOrderFromMeasurementsModal';
import CreateOrderModal from './CreateOrderModal';
import { getStockItems, StockItem, createStockItem } from '../lib/stockItems';
import { getQuoteRequests, QuoteRequest, getQuoteRequestEmailThread, linkEmailToQuoteRequestGroup, getUnreadCountForQuoteRequestGroup, updateQuoteRequest } from '../lib/quoteRequests';
import { sendGmailEmail } from '../lib/email';
import { Ruler } from 'lucide-react';
import { getOnOrderQuantities, getOrders, Order, createOrder, OrderItem as DBOrderItem, getOrderEmailThread, getUnreadCountForOrder, linkEmailToOrder, updateOrderEmailInfo, updateOrderItemStatus, markAllItemsAsReceived } from '../lib/purchaseOrders';
import { getMeasurementPhotoUrl } from '../services/measurementPhotoService';
import OrderSummaryPDFModal from './OrderSummaryPDFModal';
import { CustomerSourcesService, CustomerSource } from '../lib/customerSources';
import BookingMaterialsTab from './booking/BookingMaterialsTab';
import SelectMaterialModal from './manage/SelectMaterialModal';
import { BookingMaterial } from '../lib/bookings';

// Color mapping for each status
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    'draft': '#9CA3AF', // gray
    'Quote - Expired': '#EF4444', // red
    'Field Quote Scheduled': '#F97316', // orange
    'Field Quote Completed': '#10B981', // green
    'Quote - Pending Approval': '#FBBF24', // yellow
    'Quote - Approved': '#10B981', // green
    'Measurements scheduled': '#A855F7', // violet
    'Measurements Complete': '#8B5CF6', // darker purple
    'Pending': '#F59E0B', // amber
    'Converted to booking': '#059669', // emerald
  };
  return colorMap[status] || '#9CA3AF'; // default to gray
};

const PROJECT_STATUSES = [
  'draft',
  'Quote - Expired',
  'Field Quote Scheduled',
  'Field Quote Completed',
  'Quote - Pending Approval',
  'Quote - Approved',
  'Measurements scheduled',
  'Measurements Complete',
  'Pending',
  'Converted to booking'
] as const;

interface Project {
  id: string;
  business_id: string;
  title: string;
  resource_id?: string | null;
  proposal_id?: string | null;
  booking_id?: string | null;
  pipeline_opportunities_id?: number | null;
  customer_id?: string | null;
  customer_source?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface ProjectFieldQuote {
  id: string;
  project_id: string;
  business_id: string;
  customer_id: string | null;
  resource_id: string | null;
  field_quote_address: string | null;
  start_date: string | null;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  is_measurement?: boolean | null;
  status?: 'scheduled' | 'in progress' | 'completed' | string | null;
  created_at: string;
  updated_at: string;
  resource?: {
    first_name: string;
    last_name: string;
    avatar_url?: string | null;
  } | null;
}

type NavigateFn = (to: string, options?: { replace?: boolean; state?: unknown }) => void;

interface ProjectInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
  onUpdate?: () => void;
  readOnly?: boolean;
  /** Pass from parent via useNavigate() so modal works without Router context (e.g. portals) */
  navigate?: NavigateFn;
}

export default function ProjectInfoModal({ 
  isOpen, 
  onClose, 
  project, 
  onUpdate,
  readOnly = false,
  navigate: navigateProp
}: ProjectInfoModalProps) {
  
  const navigate: NavigateFn = useCallback((to: string, options?: { replace?: boolean; state?: unknown }) => {
    if (navigateProp) navigateProp(to, options);
    else window.location.href = to;
  }, [navigateProp]);
  const { selectedBusiness } = useBusiness();
  const { coreApps, installedAddons } = useAppSystem();
  const { currentResource } = usePermissions();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [isLoadingProposal, setIsLoadingProposal] = useState(false);
  const [linkedOpportunity, setLinkedOpportunity] = useState<{ id: number; title: string; value: number; status: string } | null>(null);
  const [isLoadingLinkedOpportunity, setIsLoadingLinkedOpportunity] = useState(false);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isEditTitleModalOpen, setIsEditTitleModalOpen] = useState(false);
  
  // Customer Source state
  const [customerSources, setCustomerSources] = useState<CustomerSource[]>([]);
  const [selectedCustomerSource, setSelectedCustomerSource] = useState<CustomerSource | null>(null);
  const [isCustomerSourceDropdownOpen, setIsCustomerSourceDropdownOpen] = useState(false);
  const [isCreateSourceModalOpen, setIsCreateSourceModalOpen] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [createSourceLoading, setCreateSourceLoading] = useState(false);
  const customerSourceButtonRef = useRef<HTMLButtonElement>(null);
  const customerSourceDropdownRef = useRef<HTMLDivElement>(null);
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const [isCreateProposalModalOpen, setIsCreateProposalModalOpen] = useState(false);
  const [isProposalDetailsModalOpen, setIsProposalDetailsModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isScheduleFieldQuoteModalOpen, setIsScheduleFieldQuoteModalOpen] = useState(false);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [currentVehicle, setCurrentVehicle] = useState<CustomerVehicle | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [localProject, setLocalProject] = useState<Project | null>(project);
  const [projectServices, setProjectServices] = useState<ProjectService[]>([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [editingService, setEditingService] = useState<ProjectService | null>(null);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [isDeleteServiceModalOpen, setIsDeleteServiceModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<ProjectService | null>(null);
  const [deleteServiceLoading, setDeleteServiceLoading] = useState(false);
  const [projectMeasurements, setProjectMeasurements] = useState<ProjectMeasurement[]>([]);
  const [isLoadingMeasurements, setIsLoadingMeasurements] = useState(false);
  const [isAddMeasurementModalOpen, setIsAddMeasurementModalOpen] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState<ProjectMeasurement | null>(null);
  const [isDeleteMeasurementModalOpen, setIsDeleteMeasurementModalOpen] = useState(false);
  const [measurementToDelete, setMeasurementToDelete] = useState<ProjectMeasurement | null>(null);
  const [deleteMeasurementLoading, setDeleteMeasurementLoading] = useState(false);
  const [isProposalCollapsed, setIsProposalCollapsed] = useState(false);
  const [fieldQuote, setFieldQuote] = useState<ProjectFieldQuote | null>(null);
  const [isLoadingFieldQuote, setIsLoadingFieldQuote] = useState(false);
  const [fieldQuoteRefreshKey, setFieldQuoteRefreshKey] = useState(0);
  const [measurementFieldQuote, setMeasurementFieldQuote] = useState<ProjectFieldQuote | null>(null);
  const [isLoadingMeasurementFieldQuote, setIsLoadingMeasurementFieldQuote] = useState(false);
  const [isFieldQuoteStatusDropdownOpen, setIsFieldQuoteStatusDropdownOpen] = useState(false);
  const [isSuppliesDropdownOpen, setIsSuppliesDropdownOpen] = useState(false);
  const suppliesDropdownRef = useRef<HTMLDivElement>(null);
  const [isRequestQuoteModalOpen, setIsRequestQuoteModalOpen] = useState(false);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [isPlaceOrderModalOpen, setIsPlaceOrderModalOpen] = useState(false);
  const [isPlaceOrderFromMeasurementsModalOpen, setIsPlaceOrderFromMeasurementsModalOpen] = useState(false);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [loadingStockItems, setLoadingStockItems] = useState(false);
  const [onOrderQuantities, setOnOrderQuantities] = useState<Record<string, number>>({});
  const [projectOrders, setProjectOrders] = useState<Order[]>([]);
  const [loadingProjectOrders, setLoadingProjectOrders] = useState(false);
  const [receivingOrderId, setReceivingOrderId] = useState<string | null>(null);
  const [receivingItemId, setReceivingItemId] = useState<string | null>(null);
  const [quoteRequests, setQuoteRequests] = useState<QuoteRequest[]>([]);
  const [loadingQuoteRequests, setLoadingQuoteRequests] = useState(false);
  const [expandedQuoteRequestIds, setExpandedQuoteRequestIds] = useState<Set<string>>(new Set());
  const [expandedQuoteGroupIds, setExpandedQuoteGroupIds] = useState<Set<string>>(new Set());
  const [editingPriceRequestId, setEditingPriceRequestId] = useState<string | null>(null);
  const [editingCost, setEditingCost] = useState<string>('');
  const [editingRetailPrice, setEditingRetailPrice] = useState<string>('');
  const [savingPrice, setSavingPrice] = useState(false);
  const [emailThreads, setEmailThreads] = useState<Record<string, Array<{
    id: string;
    direction: 'outbound' | 'inbound';
    subject: string | null;
    content: string;
    body_html: string | null;
    email_address: string;
    sent_at: string | null;
    received_at: string | null;
    created_at: string;
    external_id?: string | null;
    read?: 'read' | 'unread' | null;
    all_messages_id?: string | null;
  }>>>({});
  const [loadingEmailThreads, setLoadingEmailThreads] = useState<Record<string, boolean>>({});
  const [expandedEmailThreadGroupIds, setExpandedEmailThreadGroupIds] = useState<Set<string>>(new Set());
  const [emailThreadModalGroupId, setEmailThreadModalGroupId] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState<string>('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [locallyReadEmails, setLocallyReadEmails] = useState<Set<string>>(new Set());
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [isMeasurementStatusDropdownOpen, setIsMeasurementStatusDropdownOpen] = useState(false);
  
  // Project Materials state
  const [projectMaterials, setProjectMaterials] = useState<Array<{
    id: string;
    booking_id: string | null;
    project_id: string | null;
    material_id: string | null;
    item_id: string | null;
    material_name: string;
    material_description?: string;
    quantity: number;
    unit: string;
    cost: number;
    total_cost: number;
    created_at: string;
    updated_at: string;
  }>>([]);
  const [isLoadingProjectMaterials, setIsLoadingProjectMaterials] = useState(false);
  const [isSelectMaterialModalOpen, setIsSelectMaterialModalOpen] = useState(false);
  
  // Convert to Order state
  const [convertToOrderGroupId, setConvertToOrderGroupId] = useState<string | null>(null);
  const [convertToOrderData, setConvertToOrderData] = useState<{
    supplier: Customer;
    items: Array<{
      item: StockItem;
      quantityToOrder: number;
      totalCost: number;
      measurementId?: string | null;
      quoteRequestId?: string;
      measurement?: {
        name?: string;
        length?: number | string;
        width?: number | string;
        height?: number | string;
        area?: number | string;
        unit?: string;
        custom_fields?: Record<string, any>;
        notes?: string;
        photos?: Array<{ url: string }>;
      } | null;
    }>;
    totalAmount: number;
  } | null>(null);
  const [isConvertingToOrder, setIsConvertingToOrder] = useState(false);
  
  // Order email threads state
  const [orderEmailThreads, setOrderEmailThreads] = useState<Record<string, Array<{
    id: string;
    direction: 'outbound' | 'inbound';
    subject: string | null;
    content: string;
    body_html: string | null;
    email_address: string;
    sent_at: string | null;
    received_at: string | null;
    created_at: string;
    external_id?: string | null;
    read?: 'read' | 'unread' | null;
    all_messages_id?: string | null;
  }>>>({});
  const [loadingOrderEmailThreads, setLoadingOrderEmailThreads] = useState<Record<string, boolean>>({});
  const [orderEmailThreadModalId, setOrderEmailThreadModalId] = useState<string | null>(null);
  const [orderReplyMessage, setOrderReplyMessage] = useState<string>('');
  const [isSendingOrderReply, setIsSendingOrderReply] = useState(false);
  const [orderUnreadCounts, setOrderUnreadCounts] = useState<Record<string, number>>({});
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set());
  const orderEmailThreadScrollRef = useRef<HTMLDivElement>(null);
  const [isFieldQuoteCollapsed, setIsFieldQuoteCollapsed] = useState(false);
  const [isFieldQuoteMeasurementMode, setIsFieldQuoteMeasurementMode] = useState(false);
  const [fieldQuoteForSchedule, setFieldQuoteForSchedule] = useState<ProjectFieldQuote | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const emailThreadScrollRef = useRef<HTMLDivElement>(null);
  const [isProjectStatusDropdownOpen, setIsProjectStatusDropdownOpen] = useState(false);
  const [projectPayments, setProjectPayments] = useState<Payment[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [taxRates, setTaxRates] = useState<Array<{ id: string; name: string; rate: number }>>([]);
  const [additionalTaxLines, setAdditionalTaxLines] = useState<Array<{ name: string; rate: number; amount: number }>>([]);
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isCreateMobileBookingModalOpen, setIsCreateMobileBookingModalOpen] = useState(false);
  const [isBookingTypeSelectionOpen, setIsBookingTypeSelectionOpen] = useState(false);
  const [showLocationBookingSetting, setShowLocationBookingSetting] = useState(true);
  const [showMobileBookingSetting, setShowMobileBookingSetting] = useState(true);
  const [linkedBooking, setLinkedBooking] = useState<{ id: string; booking_number: number; title: string } | null>(null);

  // Track whether modal was open in the previous render cycle.
  // Individual data-fetching effects skip their initial run (batch loader handles it).
  const prevIsOpenRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Consolidated parallel data loader – runs ALL initial queries in one shot
  // via Promise.allSettled, replacing 15+ sequential round-trips with a single
  // parallel batch. State is set in one go → single re-render.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id || !project?.id) return;
    // Only run when modal first opens (prevIsOpenRef still false)
    if (prevIsOpenRef.current) return;

    const currentProject = project;
    const businessId = selectedBusiness.id;

    const fetchFQ = async (isMeasurement: boolean): Promise<ProjectFieldQuote | null> => {
      const { data, error } = await supabase
        .from('project_field_quotes')
        .select('*')
        .eq('project_id', currentProject.id)
        .eq('business_id', businessId)
        .eq('is_measurement', isMeasurement)
        .order('start_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error || !data) return null;
      let resource: ProjectFieldQuote['resource'] = null;
      if (data.resource_id) {
        const { data: res } = await supabase
          .from('resources')
          .select('first_name, last_name, avatar_url')
          .eq('id', data.resource_id)
          .maybeSingle();
        if (res) resource = res as any;
      }
      return { ...(data as any), resource } as ProjectFieldQuote;
    };

    const loadAll = async () => {
      const results = await Promise.allSettled([
        /* 0  customer       */ currentProject.customer_id ? getCustomer(currentProject.customer_id) : Promise.resolve(null),
        /* 1  proposal       */ currentProject.proposal_id ? getProposal(currentProject.proposal_id) : Promise.resolve(null),
        /* 2  field quote    */ fetchFQ(false),
        /* 3  meas fq        */ fetchFQ(true),
        /* 4  services       */ getProjectServices(currentProject.id),
        /* 5  measurements   */ getProjectMeasurements(currentProject.id),
        /* 6  tax rates      */ supabase.from('tax_rates').select('id, name, rate').eq('business_id', businessId),
        /* 7  payments       */ supabase.from('payments').select('*').eq('project_id', currentProject.id).order('created_at', { ascending: false }),
        /* 8  cust sources   */ CustomerSourcesService.getCustomerSources(businessId),
        /* 9  cal settings   */ Promise.all([
          supabase.rpc('get_app_setting', { p_business_id: businessId, p_app_slug: 'calendar', p_setting_key: 'show_location_booking_in_fab', p_default_value: true }),
          supabase.rpc('get_app_setting', { p_business_id: businessId, p_app_slug: 'calendar', p_setting_key: 'show_mobile_booking_in_fab', p_default_value: true }),
        ]),
        /* 10 linked booking */ currentProject.booking_id
          ? supabase.from('bookings').select('id, booking_number, title, is_deleted').eq('id', currentProject.booking_id).single()
          : Promise.resolve(null),
        /* 11 linked opp     */ currentProject.pipeline_opportunities_id
          ? supabase.from('pipeline_opportunities').select('id, title, value, status').eq('id', currentProject.pipeline_opportunities_id).single()
          : Promise.resolve(null),
        /* 12 orders         */ getOrders(businessId, { projectId: currentProject.id }),
        /* 13 quote requests */ getQuoteRequests(businessId, { projectId: currentProject.id }),
      ]);

      const v = <T,>(r: PromiseSettledResult<T>): T | undefined =>
        r.status === 'fulfilled' ? r.value : undefined;

      // Customer
      const cust = v(results[0]) as Customer | null | undefined;
      setCustomer(cust ?? null);
      setIsLoadingCustomer(false);

      // Proposal
      const prop = v(results[1]) as Proposal | null | undefined;
      setProposal(prop ?? null);
      setIsLoadingProposal(false);
      if (prop?.status === 'booked') setIsProposalCollapsed(true);

      // Field quote
      const fq = v(results[2]) as ProjectFieldQuote | null | undefined;
      setFieldQuote(fq ?? null);
      setIsLoadingFieldQuote(false);
      if (fq && (fq.status || '').toLowerCase() === 'completed') setIsFieldQuoteCollapsed(true);

      // Measurement field quote
      setMeasurementFieldQuote((v(results[3]) as ProjectFieldQuote | null | undefined) ?? null);
      setIsLoadingMeasurementFieldQuote(false);

      // Services
      setProjectServices((v(results[4]) as ProjectService[] | undefined) || []);
      setIsLoadingServices(false);

      // Measurements
      setProjectMeasurements((v(results[5]) as ProjectMeasurement[] | undefined) || []);
      setIsLoadingMeasurements(false);

      // Tax rates
      const taxRes = v(results[6]) as { data: any; error: any } | undefined;
      if (taxRes && !taxRes.error && taxRes.data) setTaxRates(taxRes.data);

      // Payments
      const payRes = v(results[7]) as { data: any; error: any } | undefined;
      if (payRes && !payRes.error) setProjectPayments((payRes.data || []) as Payment[]);
      setIsLoadingPayments(false);

      // Customer sources
      const srcData = v(results[8]) as CustomerSource[] | undefined;
      if (srcData) {
        const defaults: CustomerSource[] = [
          { id: 'repeat-customer', business_id: businessId, name: 'Repeat Customer', is_active: true, created_at: '', updated_at: '' },
          { id: 'referral', business_id: businessId, name: 'Referral', is_active: true, created_at: '', updated_at: '' },
        ];
        const all = [...defaults];
        srcData.forEach(s => {
          if (!defaults.some(d => d.name.toLowerCase() === s.name.toLowerCase())) all.push(s);
        });
        all.sort((a, b) => a.name.localeCompare(b.name));
        setCustomerSources(all);
      }

      // Calendar settings
      const calRes = v(results[9]) as [any, any] | undefined;
      if (calRes) {
        const [loc, mob] = calRes;
        if (!loc.error && loc.data !== null) setShowLocationBookingSetting(Boolean(loc.data));
        if (!mob.error && mob.data !== null) setShowMobileBookingSetting(Boolean(mob.data));
      }

      // Linked booking
      const bkRes = v(results[10]) as { data: any; error: any } | null | undefined;
      if (bkRes && bkRes.data && !bkRes.error && !bkRes.data.is_deleted) {
        setLinkedBooking({ id: bkRes.data.id, booking_number: bkRes.data.booking_number || 0, title: bkRes.data.title || 'Booking' });
      }

      // Linked opportunity
      const oppRes = v(results[11]) as { data: any; error: any } | null | undefined;
      if (oppRes && oppRes.data && !oppRes.error) {
        setLinkedOpportunity({ id: oppRes.data.id, title: oppRes.data.title || 'Opportunity', value: oppRes.data.value || 0, status: oppRes.data.status || 'open' });
      }
      setIsLoadingLinkedOpportunity(false);

      // Orders
      setProjectOrders((v(results[12]) as Order[] | undefined) || []);
      setLoadingProjectOrders(false);

      // Quote requests
      setQuoteRequests((v(results[13]) as QuoteRequest[] | undefined) || []);
      setLoadingQuoteRequests(false);
    };

    loadAll().catch(err => console.error('Error in batch project data load:', err));
  }, [isOpen, project?.id, selectedBusiness?.id]);

  useEffect(() => {
    if (!selectedBusiness?.id || !isOpen) return;
    if (!prevIsOpenRef.current) return; // Batch loader handles initial open

    const loadCalendarSettings = async () => {
      try {
        const { data: locationData, error: locationError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_location_booking_in_fab',
            p_default_value: true
          });

        if (!locationError && locationData !== null) {
          setShowLocationBookingSetting(Boolean(locationData));
        }

        const { data: mobileData, error: mobileError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_mobile_booking_in_fab',
            p_default_value: true
          });

        if (!mobileError && mobileData !== null) {
          setShowMobileBookingSetting(Boolean(mobileData));
        }
      } catch (error) {
        console.error('Error loading calendar settings:', error);
      }
    };

    loadCalendarSettings();
  }, [isOpen, selectedBusiness?.id]);

  useEffect(() => {
    const handleCalendarSettingChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === 'show_location_booking_in_fab') {
        setShowLocationBookingSetting(Boolean(customEvent.detail.value));
      }
      if (customEvent.detail?.key === 'show_mobile_booking_in_fab') {
        setShowMobileBookingSetting(Boolean(customEvent.detail.value));
      }
    };

    window.addEventListener('calendarSettingChanged', handleCalendarSettingChanged as EventListener);
    window.addEventListener('mobileCalendarSettingChanged', handleCalendarSettingChanged as EventListener);
    return () => {
      window.removeEventListener('calendarSettingChanged', handleCalendarSettingChanged as EventListener);
      window.removeEventListener('mobileCalendarSettingChanged', handleCalendarSettingChanged as EventListener);
    };
  }, []);

  // Auto-collapse field quote when completed
  useEffect(() => {
    if (fieldQuote && (fieldQuote.status || '').toLowerCase() === 'completed') {
      setIsFieldQuoteCollapsed(true);
    } else {
      setIsFieldQuoteCollapsed(false);
    }
  }, [fieldQuote?.status]);

  // Update local project when prop changes and reset all related state
  useEffect(() => {
    if (project) {
      // Preserve locally updated fields (like customer_source) if the incoming project doesn't have them
      setLocalProject(prev => {
        // If this is a new project (different ID), use the new project data
        if (!prev || prev.id !== project.id) {
          return project;
        }
        // Otherwise, merge: use incoming project data but preserve local customer_source if it was set
        return {
          ...project,
          customer_source: prev.customer_source || project.customer_source
        };
      });
      // Only reset state when project ID changes (not when proposal_id or other fields change)
      // This allows realtime updates to work without resetting state
      if (localProject?.id !== project.id) {
        setCustomer(null);
        setIsLoadingCustomer(false);
        setProposal(null);
        setIsLoadingProposal(false);
      }
    } else {
      setLocalProject(null);
      setCustomer(null);
      setIsLoadingCustomer(false);
      setProposal(null);
      setIsLoadingProposal(false);
    }
  }, [project?.id, project?.proposal_id, project?.customer_id, project?.status, project?.title]);

  // Check if proposals app is installed
  const isProposalsAppInstalled = coreApps.some(app => app.app?.slug === 'proposals') || 
                                  installedAddons.some(app => app.app?.slug === 'proposals');
  
  // Check if calendar app is installed
  const isCalendarAppInstalled = coreApps.some(app => app.app?.slug === 'calendar') || 
                                  installedAddons.some(app => app.app?.slug === 'calendar');

  // Check if expenses app is installed
  const isExpensesAppInstalled = coreApps.some(app => app.app?.slug === 'expenses') || 
                                 installedAddons.some(app => app.app?.slug === 'expenses');

  // Handle modal visibility animation
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        setIsModalVisible(true);
      });
    } else {
      setIsModalVisible(false);
    }
  }, [isOpen]);

  // Fetch customer data when customer_id changes while modal is already open
  useEffect(() => {
    const fetchCustomer = async () => {
      const currentProject = localProject || project;
      
      if (!currentProject?.customer_id) {
        setCustomer(null);
        setIsLoadingCustomer(false);
        return;
      }

      setIsLoadingCustomer(true);
      try {
        const customerData = await getCustomer(currentProject.customer_id);
        setCustomer(customerData);
      } catch (error) {
        console.error('Error fetching customer:', error);
        setCustomer(null);
      } finally {
        setIsLoadingCustomer(false);
      }
    };

    if (isOpen && (localProject || project)) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      fetchCustomer();
    } else {
      // Reset when modal closes
      setCustomer(null);
      setIsLoadingCustomer(false);
    }
  }, [localProject?.id, project?.id, localProject?.customer_id, project?.customer_id, isOpen]);

  // Fetch proposal data when proposal_id changes while modal is already open
  useEffect(() => {
    const fetchProposal = async () => {
      const currentProject = localProject || project;

      if (!currentProject?.proposal_id) {
        setProposal(null);
        setIsLoadingProposal(false);
        return;
      }

      setIsLoadingProposal(true);
      try {
        const proposalData = await getProposal(currentProject.proposal_id);
        setProposal(proposalData);
      } catch (error) {
        console.error('Error fetching proposal:', error);
        setProposal(null);
        if (currentProject?.id) {
          try {
            const { error: updateError } = await supabase
              .from('projects')
              .update({ proposal_id: null })
              .eq('id', currentProject.id);

            if (!updateError) {
              setLocalProject(prev => prev ? { ...prev, proposal_id: null } : null);
              onUpdate?.();
            }
          } catch (updateError) {
            console.error('Error clearing proposal_id from project:', updateError);
          }
        }
      } finally {
        setIsLoadingProposal(false);
      }
    };

    if (isOpen && (localProject || project)) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      fetchProposal();
    } else {
      // Reset when modal closes
      setProposal(null);
      setIsLoadingProposal(false);
    }
  }, [localProject?.id, project?.id, localProject?.proposal_id, project?.proposal_id, isOpen, onUpdate]);

  // Collapse the proposal card by default once it's booked
  useEffect(() => {
    if (proposal?.status === 'booked') {
      setIsProposalCollapsed(true);
    } else {
      setIsProposalCollapsed(false);
    }
  }, [proposal?.id, proposal?.status]);

  const formatTimeForDisplay = (time: string | null | undefined) => {
    if (!time) return '';
    // Expect formats like "09:00" or "09:00:00"
    const [hourStr, minuteStr] = time.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr || '0', 10);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return time;

    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = ((hour + 11) % 12) + 1; // 0/12 -> 12, 13 -> 1, etc.
    const displayMinute = minute.toString().padStart(2, '0');
    return `${displayHour}:${displayMinute} ${ampm}`;
  };

  const formatFieldQuoteDateTime = (date: string | null, time: string | null | undefined) => {
    if (!date && !time) return '';
    try {
      // Build a local Date from date + time (or fallback time)
      const safeDate = date || new Date().toISOString().split('T')[0];
      const safeTime = (time && time.length >= 4 ? time : '09:00').slice(0, 5);
      const [year, month, day] = safeDate.split('-').map(Number);
      const [hour, minute] = safeTime.split(':').map(Number);
      const dt = new Date(year, month - 1, day, hour, minute, 0);
      return format(dt, 'EEEE h:mma').replace('AM', 'am').replace('PM', 'pm');
    } catch {
      // Fallback to raw date + formatted time if parsing fails
      return `${date || ''} ${formatTimeForDisplay(time)}`.trim();
    }
  };

  const getNextFieldQuoteStatus = (currentStatus: string | null | undefined) => {
    const raw = (currentStatus || 'scheduled').toLowerCase();
    if (raw === 'scheduled') return 'in progress';
    if (raw === 'in progress') return 'completed';
    return 'completed';
  };

  const handleFieldQuoteStatusChange = async (newStatus: string) => {
    if (!fieldQuote || !supabase) return;

    const currentProject = localProject || project;
    if (!currentProject?.id) return;

    try {
      const { error } = await supabase
        .from('project_field_quotes')
        .update({ status: newStatus })
        .eq('id', fieldQuote.id);

      if (error) {
        console.error('Error updating field quote status:', error);
        toast.error('Failed to update field quote status');
        return;
      }

      if (selectedBusiness?.id) {
        triggerGcalSync(fieldQuote.id, 'update', selectedBusiness.id, 'field_quote');
      }

      setFieldQuote(prev =>
        prev ? { ...prev, status: newStatus } : prev
      );
      toast.success(`Field quote marked as ${newStatus}`);
      
      // Auto-update project status when field quote is marked as completed
      if (newStatus.toLowerCase() === 'completed' && !fieldQuote.is_measurement) {
        try {
          const { error: projectError } = await supabase
            .from('projects')
            .update({ status: 'Field Quote Completed' })
            .eq('id', currentProject.id);

          if (projectError) {
            console.error('Error updating project status to Field Quote Completed:', projectError);
            // Don't show error toast - this is a background update
          } else {
            // Update local project state
            setLocalProject(prev => 
              prev ? { ...prev, status: 'Field Quote Completed' } : prev
            );
          }
        } catch (statusErr) {
          console.error('Error updating project status:', statusErr);
        }
      }
      
      // Trigger update callback to refresh calendar
      onUpdate?.();
    } catch (err) {
      console.error('Error updating field quote status:', err);
      toast.error('Failed to update field quote status');
    }
  };

  const handleDeleteFieldQuote = async () => {
    if (!fieldQuote || !supabase) return;

    const confirmed = window.confirm('Delete this field quote? This cannot be undone.');
    if (!confirmed) return;

    try {
      if (selectedBusiness?.id) {
        triggerGcalSync(fieldQuote.id, 'delete', selectedBusiness.id, 'field_quote');
      }

      const { error } = await supabase
        .from('project_field_quotes')
        .delete()
        .eq('id', fieldQuote.id);

      if (error) {
        console.error('Error deleting field quote:', error);
        toast.error('Failed to delete field quote');
        return;
      }

      setFieldQuote(null);
      toast.success('Field quote deleted');
      onUpdate?.();
    } catch (err) {
      console.error('Error deleting field quote:', err);
      toast.error('Failed to delete field quote');
    }
  };

  const handleDeleteMeasurementFieldQuote = async () => {
    if (!measurementFieldQuote || !supabase) return;

    const confirmed = window.confirm('Delete this field measurement? This cannot be undone.');
    if (!confirmed) return;

    try {
      if (selectedBusiness?.id) {
        triggerGcalSync(measurementFieldQuote.id, 'delete', selectedBusiness.id, 'field_quote');
      }

      const { error } = await supabase
        .from('project_field_quotes')
        .delete()
        .eq('id', measurementFieldQuote.id);

      if (error) {
        console.error('Error deleting field measurement:', error);
        toast.error('Failed to delete field measurement');
        return;
      }

      setMeasurementFieldQuote(null);
      toast.success('Field measurement deleted');
      onUpdate?.();
    } catch (err) {
      console.error('Error deleting field measurement:', err);
      toast.error('Failed to delete field measurement');
    }
  };

  const handleMeasurementFieldQuoteStatusChange = async (newStatus: string) => {
    if (!measurementFieldQuote || !supabase) return;

    const currentProject = localProject || project;
    if (!currentProject?.id) return;

    try {
      const { error } = await supabase
        .from('project_field_quotes')
        .update({ status: newStatus })
        .eq('id', measurementFieldQuote.id);

      if (error) {
        console.error('Error updating measurement field quote status:', error);
        toast.error('Failed to update measurement status');
        return;
      }

      if (selectedBusiness?.id) {
        triggerGcalSync(measurementFieldQuote.id, 'update', selectedBusiness.id, 'field_quote');
      }

      setMeasurementFieldQuote(prev =>
        prev ? { ...prev, status: newStatus } : prev
      );
      toast.success(`Field measurement marked as ${newStatus}`);
      
      // Auto-update project status when measurement is marked as completed
      if (newStatus.toLowerCase() === 'completed') {
        try {
          const { error: projectError } = await supabase
            .from('projects')
            .update({ status: 'Measurements Complete' })
            .eq('id', currentProject.id);

          if (projectError) {
            console.error('Error updating project status to Measurements Complete:', projectError);
            // Don't show error toast - this is a background update
          } else {
            // Update local project state
            setLocalProject(prev => 
              prev ? { ...prev, status: 'Measurements Complete' } : prev
            );
          }
        } catch (statusErr) {
          console.error('Error updating project status:', statusErr);
        }
      }
      
      // Trigger update callback to refresh calendar
      onUpdate?.();
    } catch (err) {
      console.error('Error updating measurement field quote status:', err);
      toast.error('Failed to update measurement status');
    }
  };

  const handleAddServiceForEmptyProject = useCallback(() => {
    // Check industry - if Home Services, skip vehicle selection
    const isHomeServices = selectedBusiness?.industry === 'Home Services';

    if (isHomeServices) {
      // For Home Services, skip vehicle selection and go directly to service selection
      setCurrentVehicle(null);
      setIsSelectServiceModalOpen(true);
      return;
    }

    // For Automotive, require vehicle selection
    if (customer && customer.vehicles && customer.vehicles.length > 0) {
      setIsSelectVehicleModalOpen(true);
    } else if (customer) {
      setIsCreateVehicleModalOpen(true);
    } else {
      toast.error('Please add a customer first before adding services');
    }
  }, [
    selectedBusiness?.industry,
    customer,
    setCurrentVehicle,
    setIsSelectServiceModalOpen,
    setIsSelectVehicleModalOpen,
    setIsCreateVehicleModalOpen
  ]);

  // Fetch latest NON-measurement field quote for this project
  useEffect(() => {
    const fetchFieldQuote = async () => {
      const currentProject = localProject || project;

      if (!currentProject?.id || !selectedBusiness?.id) {
        setFieldQuote(null);
        setIsLoadingFieldQuote(false);
        return;
      }

      setIsLoadingFieldQuote(true);
      try {
        const { data, error } = await supabase
          .from('project_field_quotes')
          .select('*')
          .eq('project_id', currentProject.id)
          .eq('business_id', selectedBusiness.id)
          .eq('is_measurement', false)
          .order('start_date', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error fetching field quote:', error);
          setFieldQuote(null);
        } else if (data) {
          let resourceInfo: ProjectFieldQuote['resource'] = null;
          if (data.resource_id) {
            const { data: res, error: resError } = await supabase
              .from('resources')
              .select('first_name, last_name, avatar_url')
              .eq('id', data.resource_id)
              .maybeSingle();

            if (resError) {
              console.error('Error fetching field quote resource:', resError);
            } else if (res) {
              resourceInfo = res as any;
            }
          }

          setFieldQuote({
            ...(data as any),
            resource: resourceInfo,
          } as ProjectFieldQuote);
        } else {
          setFieldQuote(null);
        }
      } catch (error) {
        console.error('Error fetching field quote:', error);
        setFieldQuote(null);
      } finally {
        setIsLoadingFieldQuote(false);
      }
    };

    if (isOpen && (localProject || project)) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      fetchFieldQuote();
    } else {
      setFieldQuote(null);
      setIsLoadingFieldQuote(false);
    }
  }, [isOpen, localProject?.id, project?.id, selectedBusiness?.id, fieldQuoteRefreshKey]);

  // Fetch latest MEASUREMENT field quote (is_measurement = true) for this project
  useEffect(() => {
    const fetchMeasurementFieldQuote = async () => {
      const currentProject = localProject || project;

      if (!currentProject?.id || !selectedBusiness?.id) {
        setMeasurementFieldQuote(null);
        setIsLoadingMeasurementFieldQuote(false);
        return;
      }

      setIsLoadingMeasurementFieldQuote(true);
      try {
        const { data, error } = await supabase
          .from('project_field_quotes')
          .select('*')
          .eq('project_id', currentProject.id)
          .eq('business_id', selectedBusiness.id)
          .eq('is_measurement', true)
          .order('start_date', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error fetching measurement field quote:', error);
          setMeasurementFieldQuote(null);
        } else if (data) {
          let resourceInfo: ProjectFieldQuote['resource'] = null;
          if (data.resource_id) {
            const { data: res, error: resError } = await supabase
              .from('resources')
              .select('first_name, last_name, avatar_url')
              .eq('id', data.resource_id)
              .maybeSingle();

            if (resError) {
              console.error('Error fetching measurement field quote resource:', resError);
            } else if (res) {
              resourceInfo = res as any;
            }
          }

          setMeasurementFieldQuote({
            ...(data as any),
            resource: resourceInfo
          } as ProjectFieldQuote);
        } else {
          setMeasurementFieldQuote(null);
        }
      } catch (error) {
        console.error('Error fetching measurement field quote:', error);
        setMeasurementFieldQuote(null);
      } finally {
        setIsLoadingMeasurementFieldQuote(false);
      }
    };

    if (isOpen && (localProject || project)) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      fetchMeasurementFieldQuote();
    } else {
      setMeasurementFieldQuote(null);
      setIsLoadingMeasurementFieldQuote(false);
    }
  }, [isOpen, localProject?.id, project?.id, selectedBusiness?.id, fieldQuoteRefreshKey]);

  // Fetch project services when project changes
  useEffect(() => {
    const fetchServices = async () => {
      const currentProject = localProject || project;
      
      if (!currentProject?.id) {
        setProjectServices([]);
        setIsLoadingServices(false);
        return;
      }

      setIsLoadingServices(true);
      try {
        const services = await getProjectServices(currentProject.id);
        setProjectServices(services);
      } catch (error) {
        console.error('Error fetching project services:', error);
        setProjectServices([]);
      } finally {
        setIsLoadingServices(false);
      }
    };

    if (isOpen && (localProject || project)) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      fetchServices();
    } else {
      setProjectServices([]);
      setIsLoadingServices(false);
    }
  }, [localProject?.id, project?.id, isOpen]);

  // Listen for project services updates (e.g., when proposal is converted to project)
  useEffect(() => {
    const handleProjectServicesUpdated = async (event: CustomEvent) => {
      const currentProject = localProject || project;
      
      // Only refresh if this event is for the current project
      if (currentProject?.id && event.detail?.projectId === currentProject.id && isOpen) {
        console.log('📦 Refreshing project services after update:', currentProject.id);
        try {
          const services = await getProjectServices(currentProject.id);
          setProjectServices(services);
        } catch (error) {
          console.error('Error refreshing project services:', error);
        }
      }
    };

    window.addEventListener('projectServicesUpdated', handleProjectServicesUpdated as EventListener);

    return () => {
      window.removeEventListener('projectServicesUpdated', handleProjectServicesUpdated as EventListener);
    };
  }, [localProject?.id, project?.id, isOpen]);

  // Fetch project measurements when modal is open (always, not just when tab is active)
  // This ensures the count is always available for the tab label
  useEffect(() => {
    const fetchMeasurements = async () => {
      const currentProject = localProject || project;
      
      if (!currentProject?.id || !isOpen) {
        setProjectMeasurements([]);
        setIsLoadingMeasurements(false);
        return;
      }

      setIsLoadingMeasurements(true);
      try {
        const measurements = await getProjectMeasurements(currentProject.id);
        setProjectMeasurements(measurements);
      } catch (error) {
        console.error('Error fetching project measurements:', error);
        setProjectMeasurements([]);
      } finally {
        setIsLoadingMeasurements(false);
      }
    };

    if (isOpen) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      fetchMeasurements();
    } else {
      setProjectMeasurements([]);
      setIsLoadingMeasurements(false);
    }
  }, [localProject?.id, project?.id, isOpen]);

  // Set up realtime subscriptions to keep modal in sync with database
  useEffect(() => {
    const currentProject = localProject || project;
    if (!isOpen || !currentProject?.id || !selectedBusiness?.id) {
      return;
    }

    const channels: any[] = [];

    // Create main channel for project and related data
    const mainChannel = supabase
      .channel(`project_info_${currentProject.id}`)
      // Subscribe to project changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects',
          filter: `id=eq.${currentProject.id}`
        },
        async (payload) => {
          console.log('🔄 Project updated via realtime:', payload);
          if (payload.new) {
            const newProject = payload.new as Project;
            
            // Get current values before updating state
            setLocalProject(prev => {
              const oldProposalId = prev?.proposal_id || project?.proposal_id;
              const oldCustomerId = prev?.customer_id || project?.customer_id;
              
              // Refresh proposal if proposal_id changed
              if (newProject.proposal_id !== oldProposalId) {
                console.log('🔄 Proposal ID changed via realtime:', { old: oldProposalId, new: newProject.proposal_id });
                if (newProject.proposal_id) {
                  setIsLoadingProposal(true);
                  getProposal(newProject.proposal_id)
                    .then(proposalData => {
                      setProposal(proposalData);
                      setIsLoadingProposal(false);
                    })
                    .catch(error => {
                      console.error('Error fetching updated proposal:', error);
                      setProposal(null);
                      setIsLoadingProposal(false);
                    });
                } else {
                  setProposal(null);
                  setIsLoadingProposal(false);
                }
              }
              
              // Refresh customer if customer_id changed
              if (newProject.customer_id !== oldCustomerId) {
                if (newProject.customer_id) {
                  getCustomer(newProject.customer_id)
                    .then(customerData => {
                      setCustomer(customerData);
                    })
                    .catch(error => {
                      console.error('Error fetching updated customer:', error);
                    });
                } else {
                  setCustomer(null);
                }
              }
              
              return newProject;
            });
            
            onUpdate?.();
          }
        }
      )
      // Subscribe to project services changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_services',
          filter: `project_id=eq.${currentProject.id}`
        },
        async () => {
          console.log('🔄 Project services updated via realtime');
          try {
            const services = await getProjectServices(currentProject.id);
            setProjectServices(services);
          } catch (error) {
            console.error('Error refreshing project services:', error);
          }
        }
      )
      // Subscribe to project measurements changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_measurements',
          filter: `project_id=eq.${currentProject.id}`
        },
        async () => {
          console.log('🔄 Project measurements updated via realtime');
          // Always refresh measurements to keep count accurate in tab label
          try {
            const measurements = await getProjectMeasurements(currentProject.id);
            setProjectMeasurements(measurements);
          } catch (error) {
            console.error('Error refreshing project measurements:', error);
          }
        }
      )
      // Subscribe to project field quotes changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_field_quotes',
          filter: `project_id=eq.${currentProject.id}`
        },
        async () => {
          console.log('🔄 Project field quotes updated via realtime');
          // Refresh field quotes
          setFieldQuoteRefreshKey(prev => prev + 1);
        }
      )
      // Subscribe to customer changes (if customer is linked)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customers',
          filter: currentProject.customer_id ? `id=eq.${currentProject.customer_id}` : `id=eq.00000000-0000-0000-0000-000000000000`
        },
        async (payload) => {
          console.log('🔄 Customer updated via realtime:', payload);
          if (payload.new && currentProject.customer_id) {
            try {
              const customerData = await getCustomer(payload.new.id);
              setCustomer(customerData);
            } catch (error) {
              console.error('Error fetching updated customer:', error);
            }
          }
        }
      )
      .subscribe();

    channels.push(mainChannel);

    // Create separate channel for proposal (only if proposal_id exists)
    // This allows the subscription to be recreated when proposal_id changes
    if (currentProject.proposal_id) {
      const proposalChannel = supabase
        .channel(`proposal_${currentProject.proposal_id}_${currentProject.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'proposals',
            filter: `id=eq.${currentProject.proposal_id}`
          },
          async (payload: any) => {
            console.log('🔄 Proposal updated via realtime:', payload, 'eventType:', payload.eventType, 'hasOld:', !!payload.old, 'hasNew:', !!payload.new);
            
            // Handle DELETE event - Supabase sends DELETE events with old data but no new data
            const isDelete = payload.eventType === 'DELETE' || 
                            (payload.old && !payload.new) ||
                            (payload.old && payload.old.id === currentProject.proposal_id && !payload.new);
            
            if (isDelete) {
              console.log('🗑️ Proposal deleted via realtime, unlinking from project');
              setProposal(null);
              // Update local project to clear proposal_id
              setLocalProject(prev => {
                if (prev && prev.proposal_id) {
                  return { ...prev, proposal_id: null };
                }
                return prev;
              });
              // Also update in database to keep it in sync
              try {
                await supabase
                  .from('projects')
                  .update({ proposal_id: null })
                  .eq('id', currentProject.id);
              } catch (error) {
                console.error('Error unlinking deleted proposal from project:', error);
              }
              onUpdate?.();
            } 
            // Handle UPDATE or INSERT events
            else if (payload.new && payload.new.id === currentProject.proposal_id) {
              try {
                const proposalData = await getProposal(payload.new.id);
                setProposal(proposalData);
              } catch (error) {
                console.error('Error fetching updated proposal:', error);
                // If proposal fetch fails (e.g., it was deleted), clear it
                setProposal(null);
                setLocalProject(prev => {
                  if (prev && prev.proposal_id) {
                    return { ...prev, proposal_id: null };
                  }
                  return prev;
                });
                onUpdate?.();
              }
            }
          }
        )
        .subscribe();

      channels.push(proposalChannel);
    }

    return () => {
      channels.forEach(ch => supabase.removeChannel(ch));
    };
  }, [isOpen, localProject?.id, project?.id, localProject?.proposal_id, project?.proposal_id, localProject?.customer_id, project?.customer_id, activeTab, selectedBusiness?.id, onUpdate]);

  // Fetch tax rates for payment summary
  useEffect(() => {
    async function fetchTaxRates() {
      if (!selectedBusiness?.id) return;
      
      const { data, error } = await supabase
        .from('tax_rates')
        .select('id, name, rate')
        .eq('business_id', selectedBusiness.id);

      if (!error && data) {
        setTaxRates(data);
      }
    }

    if (isOpen) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      fetchTaxRates();
    }
  }, [selectedBusiness?.id, isOpen]);

  useEffect(() => {
    const serviceIds = [...new Set(projectServices.map(s => s.service_id).filter(Boolean))];
    if (serviceIds.length === 0) { setAdditionalTaxLines([]); return; }
    supabase
      .from('service_additional_taxes')
      .select('service_id, name, rate')
      .in('service_id', serviceIds)
      .then(({ data: taxes }) => {
        if (!taxes || taxes.length === 0) { setAdditionalTaxLines([]); return; }
        const taxMap = new Map<string, { name: string; rate: number; amount: number }>();
        for (const tax of taxes) {
          const matchingSvcs = projectServices.filter(s => s.service_id === tax.service_id);
          const svcTotal = matchingSvcs.reduce((sum, s) => sum + (s.price || 0), 0);
          const amt = svcTotal * tax.rate / 100;
          const key = `${tax.name}_${tax.rate}`;
          if (taxMap.has(key)) taxMap.get(key)!.amount += amt;
          else taxMap.set(key, { name: tax.name, rate: tax.rate, amount: amt });
        }
        setAdditionalTaxLines(Array.from(taxMap.values()).map(t => ({ ...t, amount: Math.round(t.amount * 100) / 100 })));
      });
  }, [projectServices]);

  // Fetch project payments (if project_id exists in payments table)
  useEffect(() => {
    const fetchPayments = async () => {
      const currentProject = localProject || project;
      
      if (!currentProject?.id || !selectedBusiness?.id) {
        setProjectPayments([]);
        setIsLoadingPayments(false);
        return;
      }

      setIsLoadingPayments(true);
      try {
        const { data, error } = await supabase
          .from('payments')
          .select('*')
          .eq('project_id', currentProject.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching project payments:', error);
          setProjectPayments([]);
        } else {
          setProjectPayments((data || []) as Payment[]);
        }
      } catch (error) {
        console.error('Error fetching project payments:', error);
        setProjectPayments([]);
      } finally {
        setIsLoadingPayments(false);
      }
    };

    if (isOpen) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      fetchPayments();
    }
  }, [localProject?.id, project?.id, selectedBusiness?.id, isOpen]);

  // Calculate payment summary values from project services
  const paymentSummary = useMemo(() => {
    // Calculate subtotal (sum of all service prices)
    const subtotal = projectServices.reduce((sum, service) => sum + (service.price || 0), 0);
    
    // Calculate total discounts (sum of all discount amounts)
    const totalDiscounts = projectServices.reduce((sum, service) => sum + (service.discount_amount || 0), 0);
    
    // Calculate total tax (sum of all tax amounts)
    const taxAmount = projectServices.reduce((sum, service) => sum + (service.tax_amount || 0), 0);
    
    // Calculate total amount (subtotal - discounts + tax)
    const totalAmount = subtotal - totalDiscounts + taxAmount;
    
    // Calculate payments (for now, payments are 0 since project_id doesn't exist in payments table)
    const totalPaid = projectPayments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + (p.base_amount || p.amount || 0), 0);
    
    const totalTips = projectPayments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + (p.tip_amount || 0), 0);
    
    const totalReceived = totalPaid + totalTips;
    const balanceDue = totalAmount - totalPaid;
    
    // Format tax label
    const taxLabel = taxRates.length > 0
      ? taxRates.map(rate => `${rate.name} (${rate.rate}%)`).join(' + ')
      : 'Tax';
    
    return {
      subtotal,
      totalDiscounts,
      taxAmount,
      totalAmount,
      totalPaid,
      totalTips,
      totalReceived,
      balanceDue,
      taxLabel
    };
  }, [projectServices, projectPayments, taxRates]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Close all modals event listener
  useEffect(() => {
    const handleCloseAllModals = () => {
      if (isOpen) {
        onClose();
      }
    };

    window.addEventListener('closeAllModals', handleCloseAllModals);
    return () => {
      window.removeEventListener('closeAllModals', handleCloseAllModals);
    };
  }, [isOpen, onClose]);

  // Handle click outside menu to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const menuElement = document.querySelector('[data-project-menu]');
      const isClickOnMenu = menuElement?.contains(target);
      
      if (menuRef.current && !menuRef.current.contains(target) && !isClickOnMenu && isMenuOpen) {
        setIsMenuOpen(false);
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [isMenuOpen]);

  // Handle edit project
  const handleEdit = () => {
    setIsMenuOpen(false);
    const currentProject = localProject || project;
    if (currentProject) {
      setEditTitle(currentProject.title);
      // Small delay to ensure menu closes before opening edit modal
      setTimeout(() => {
        setIsEditTitleModalOpen(true);
      }, 100);
    }
  };

  // Handle save project title
  const handleSaveTitle = async () => {
    const currentProject = localProject || project;
    if (!currentProject?.id || !editTitle.trim()) return;

    setIsSavingTitle(true);
    try {
      const { error } = await supabase
        .from('projects')
        .update({ title: editTitle.trim() })
        .eq('id', currentProject.id);

      if (error) {
        console.error('Error updating project title:', error);
        toast.error('Failed to update project title');
        setIsSavingTitle(false);
        return;
      }

      // Update local project state immediately
      setLocalProject(prev => prev ? { ...prev, title: editTitle.trim() } : (project ? { ...project, title: editTitle.trim() } : null));
      
      toast.success('Project title updated successfully');
      setIsEditTitleModalOpen(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating project title:', error);
      toast.error('Failed to update project title');
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Handle delete project
  const handleDelete = () => {
    setIsMenuOpen(false);
    setTimeout(() => {
      setIsDeleteModalOpen(true);
    }, 100);
  };

  // Confirm delete project (soft delete)
  const handleConfirmDelete = async () => {
    const currentProject = localProject || project;
    if (!currentProject?.id) return;

    setIsDeleting(true);
    try {
      // 1. Unlink any proposals linked to this project (DO NOT DELETE PROPOSAL)
      if (currentProject.proposal_id) {
        const { error: unlinkError } = await supabase
          .from('proposals')
          .update({ project_id: null })
          .eq('id', currentProject.proposal_id);

        if (unlinkError) {
          console.error('Error unlinking proposal:', unlinkError);
          // Continue with deletion even if unlink fails
        }
      }

      // 2. Hard delete any field quotes/measurements scheduled for this project
      // First trigger gcal delete for each one
      if (selectedBusiness?.id) {
        const { data: fqsToDelete } = await supabase
          .from('project_field_quotes')
          .select('id')
          .eq('project_id', currentProject.id);
        (fqsToDelete || []).forEach((fq: any) => {
          triggerGcalSync(fq.id, 'delete', selectedBusiness!.id, 'field_quote');
        });
      }

      const { error: deleteFieldQuotesError } = await supabase
        .from('project_field_quotes')
        .delete()
        .eq('project_id', currentProject.id);

      if (deleteFieldQuotesError) {
        console.error('Error deleting field quotes:', deleteFieldQuotesError);
      }

      // 3. SOFT DELETE: Mark project as deleted (preserve for historical records)
      const { error } = await supabase
        .from('projects')
        .update({
          is_deleted: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentProject.id);

      if (error) {
        console.error('Error soft deleting project:', error);
        toast.error('Failed to delete project');
        setIsDeleting(false);
        return;
      }

      toast.success('Project deleted successfully');
      setIsDeleteModalOpen(false);
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = async (selectedCustomer: Customer) => {
    const currentProject = localProject || project;
    if (!currentProject?.id) return;

    try {
      const { error } = await supabase
        .from('projects')
        .update({ customer_id: selectedCustomer.id })
        .eq('id', currentProject.id);

      if (error) {
        console.error('Error updating project customer:', error);
        toast.error('Failed to assign customer');
        return;
      }

      setCustomer(selectedCustomer);
      setIsSelectCustomerModalOpen(false);
      toast.success('Customer assigned successfully');
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating project customer:', error);
      toast.error('Failed to assign customer');
    }
  };

  // Handle edit customer
  const handleEditCustomer = () => {
    setIsEditCustomerModalOpen(true);
  };

  // Handle remove customer
  const handleRemoveCustomer = async () => {
    const currentProject = localProject || project;
    if (!currentProject?.id) return;

    try {
      const { error } = await supabase
        .from('projects')
        .update({ customer_id: null })
        .eq('id', currentProject.id);

      if (error) {
        console.error('Error removing project customer:', error);
        toast.error('Failed to remove customer');
        return;
      }

      setCustomer(null);
      setSelectedCustomerSource(null);
      toast.success('Customer removed successfully');
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error removing project customer:', error);
      toast.error('Failed to remove customer');
    }
  };

  // Load customer sources
  const loadCustomerSources = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const sources = await CustomerSourcesService.getCustomerSources(selectedBusiness.id);
      
      // Default sources that should always be available
      const defaultSources: CustomerSource[] = [
        {
          id: 'repeat-customer',
          business_id: selectedBusiness.id,
          name: 'Repeat Customer',
          is_active: true,
          created_at: '',
          updated_at: ''
        },
        {
          id: 'referral',
          business_id: selectedBusiness.id,
          name: 'Referral',
          is_active: true,
          created_at: '',
          updated_at: ''
        }
      ];
      
      // Merge default sources with database sources, avoiding duplicates
      const allSources = [...defaultSources];
      sources.forEach(source => {
        // Don't add if a default source with the same name exists
        if (!defaultSources.some(ds => ds.name.toLowerCase() === source.name.toLowerCase())) {
          allSources.push(source);
        }
      });
      
      // Sort alphabetically by name
      allSources.sort((a, b) => a.name.localeCompare(b.name));
      
      setCustomerSources(allSources);
    } catch (error) {
      console.error('Error loading customer sources:', error);
    }
  }, [selectedBusiness?.id]);

  // Load customer sources when business changes while modal is open
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      loadCustomerSources();
    }
  }, [isOpen, selectedBusiness?.id, loadCustomerSources]);

  // Initialize selected customer source from project data
  useEffect(() => {
    const currentProject = localProject || project;
    
    if (currentProject?.customer_source) {
      // Always set the source if it exists, even if customerSources haven't loaded yet
      if (customerSources.length > 0) {
        const source = customerSources.find(s => s.name === currentProject.customer_source);
        if (source) {
          setSelectedCustomerSource(source);
        } else {
          // Create a virtual source object for display
          setSelectedCustomerSource({
            id: 'custom',
            business_id: selectedBusiness?.id || '',
            name: currentProject.customer_source,
            is_active: true,
            created_at: '',
            updated_at: ''
          });
        }
      } else {
        // customerSources not loaded yet, but we have a customer_source - create virtual source
        setSelectedCustomerSource({
          id: 'custom',
          business_id: selectedBusiness?.id || '',
          name: currentProject.customer_source,
          is_active: true,
          created_at: '',
          updated_at: ''
        });
      }
    } else {
      setSelectedCustomerSource(null);
    }
  }, [localProject?.customer_source, project?.customer_source, customerSources, selectedBusiness?.id]);

  // Handle customer source selection
  const handleSelectCustomerSource = async (source: CustomerSource) => {
    const currentProject = localProject || project;
    
    if (!currentProject?.id) {
      toast.error('Cannot update: project not found');
      return;
    }

    // Update UI immediately for better UX
    setSelectedCustomerSource(source);
    setIsCustomerSourceDropdownOpen(false);

    try {
      const { error } = await supabase
        .from('projects')
        .update({ customer_source: source.name })
        .eq('id', currentProject.id);

      if (error) {
        console.error('Error saving customer source:', error);
        toast.error(`Failed to save: ${error.message}`);
        setSelectedCustomerSource(null);
        return;
      }

      toast.success('Customer source saved');
      
      // Update local project state so it persists if modal re-opens
      setLocalProject(prev => {
        if (prev) {
          return { ...prev, customer_source: source.name };
        } else if (project) {
          return { ...project, customer_source: source.name };
        }
        return null;
      });
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error: any) {
      console.error('Exception saving customer source:', error);
      toast.error('Failed to update customer source');
      setSelectedCustomerSource(null);
    }
  };

  // Handle creating a new customer source
  const handleCreateCustomerSource = async () => {
    if (!selectedBusiness?.id || !newSourceName.trim()) return;

    try {
      setCreateSourceLoading(true);
      const newSource = await CustomerSourcesService.createCustomerSource({
        business_id: selectedBusiness.id,
        name: newSourceName.trim()
      });

      // Add to list and select it
      setCustomerSources(prev => [...prev, newSource].sort((a, b) => a.name.localeCompare(b.name)));
      
      // Select the new source
      await handleSelectCustomerSource(newSource);
      
      // Close modal and reset
      setIsCreateSourceModalOpen(false);
      setNewSourceName('');
    } catch (error) {
      console.error('Error creating customer source:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create customer source');
    } finally {
      setCreateSourceLoading(false);
    }
  };

  // Use localProject if available, otherwise fall back to project prop
  // Use useMemo to ensure it updates when either changes
  // MUST be called before any early returns to follow Rules of Hooks
  const displayProject = useMemo(() => {
    return localProject || project;
  }, [localProject, project]);

  // Handle click outside supplies dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suppliesDropdownRef.current && !suppliesDropdownRef.current.contains(event.target as Node)) {
        setIsSuppliesDropdownOpen(false);
      }
    };

    if (isSuppliesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSuppliesDropdownOpen]);

  // Handle click outside customer source dropdown - using click event with delay
  useEffect(() => {
    if (!isCustomerSourceDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if click is inside button or dropdown using closest() for more reliable detection
      const isInsideDropdown = target.closest('[data-customer-source-dropdown]');
      const isInsideButton = target.closest('[data-customer-source-button]');
      
      if (!isInsideButton && !isInsideDropdown) {
        setIsCustomerSourceDropdownOpen(false);
      }
    };

    // Delay adding listener to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isCustomerSourceDropdownOpen]);

  // Load on-order quantities
  const loadOnOrderQuantities = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const quantities = await getOnOrderQuantities(selectedBusiness.id);
      setOnOrderQuantities(quantities);
    } catch (err) {
      console.error('Error loading on order quantities:', err);
    }
  }, [selectedBusiness?.id]);

  // Load stock items for CreateOrderModal
  const loadStockItems = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoadingStockItems(true);
      let quantities = onOrderQuantities;
      if (Object.keys(quantities).length === 0) {
        quantities = await getOnOrderQuantities(selectedBusiness.id);
        setOnOrderQuantities(quantities);
      }
      const data = await getStockItems(selectedBusiness.id, { 
        includePendingOneTimeItems: true,
        onOrderQuantities: quantities
      });
      setStockItems(data);
    } catch (err) {
      console.error('Error loading stock items:', err);
      toast.error('Failed to load stock items');
    } finally {
      setLoadingStockItems(false);
    }
  }, [selectedBusiness?.id, onOrderQuantities]);

  // Load project orders
  const loadProjectOrders = useCallback(async () => {
    if (!selectedBusiness?.id || !project?.id) return;
    
    try {
      setLoadingProjectOrders(true);
      const orders = await getOrders(selectedBusiness.id, { projectId: project.id });
      setProjectOrders(orders);
    } catch (err) {
      console.error('Error loading project orders:', err);
    } finally {
      setLoadingProjectOrders(false);
    }
  }, [selectedBusiness?.id, project?.id]);

  // Handle receiving individual order item
  const handleReceiveOrderItem = useCallback(async (itemId: string, itemName: string) => {
    try {
      setReceivingItemId(itemId);
      await updateOrderItemStatus(itemId, 'Received');
      toast.success(`Received: ${itemName}`);
      // Reload orders to reflect changes
      await loadProjectOrders();
    } catch (err) {
      console.error('Error receiving item:', err);
      toast.error('Failed to receive item');
    } finally {
      setReceivingItemId(null);
    }
  }, [loadProjectOrders]);

  // Handle receiving all items in an order
  const handleReceiveAllOrderItems = useCallback(async (orderId: string, orderNumber: string) => {
    try {
      setReceivingOrderId(orderId);
      await markAllItemsAsReceived(orderId);
      toast.success(`All items received for ${orderNumber}`);
      // Reload orders to reflect changes
      await loadProjectOrders();
    } catch (err) {
      console.error('Error receiving all items:', err);
      toast.error('Failed to receive items');
    } finally {
      setReceivingOrderId(null);
    }
  }, [loadProjectOrders]);

  // Load project materials
  const loadProjectMaterials = useCallback(async () => {
    if (!selectedBusiness?.id || !project?.id || !supabase) return;
    
    try {
      setIsLoadingProjectMaterials(true);
      
      // Query materials where project_id matches
      const { data: projectMaterials, error: projectError } = await supabase
        .from('booking_materials_with_details')
        .select('*')
        .eq('project_id', project.id)
        .order('created_at', { ascending: false });

      if (projectError) {
        console.error('Error loading project materials:', projectError);
        setIsLoadingProjectMaterials(false);
        return;
      }

      // Also query materials from linked booking (if project has booking_id)
      let bookingMaterials: any[] = [];
      if (project.booking_id) {
        const { data: bMaterials, error: bookingError } = await supabase
          .from('booking_materials_with_details')
          .select('*')
          .eq('booking_id', project.booking_id)
          .is('project_id', null) // Only get materials that aren't already linked to a project
          .order('created_at', { ascending: false });

        if (!bookingError && bMaterials) {
          bookingMaterials = bMaterials;
        }
      }

      // Combine and deduplicate by id
      const allMaterials = [...(projectMaterials || []), ...bookingMaterials];
      const uniqueMaterials = Array.from(
        new Map(allMaterials.map(m => [m.id, m])).values()
      );

      setProjectMaterials((uniqueMaterials || []) as Array<{
        id: string;
        booking_id: string | null;
        project_id: string | null;
        material_id: string | null;
        item_id: string | null;
        material_name: string;
        material_description?: string;
        quantity: number;
        unit: string;
        cost: number;
        total_cost: number;
        created_at: string;
        updated_at: string;
      }>);
    } catch (err) {
      console.error('Error loading project materials:', err);
    } finally {
      setIsLoadingProjectMaterials(false);
    }
  }, [selectedBusiness?.id, project?.id]);

  // Handle adding material to project
  const handleAddMaterial = useCallback(async (item: any, quantity: number, source: 'materials' | 'inventory' = 'materials') => {
    if (!project?.id || !supabase) return;

    try {
      const bookingId = project.booking_id || null; // If project is linked to a booking, include it
      
      if (source === 'inventory') {
        // Handle inventory item
        const stockItem = item as any; // StockItem type
        
        // 1. Create stock transaction to withdraw from inventory
        // Note: For now, we'll use booking_id if project is linked to a booking, otherwise null
        // When project converts to booking, we'll update these transactions
        const { createStockTransaction } = await import('../lib/stockItems');
        await createStockTransaction({
          item_id: stockItem.id,
          type: 'sale',
          quantity: quantity,
          price: stockItem.acquisition_cost,
          notes: `Added as material to project: ${project.id}`,
          booking_id: bookingId || undefined // Use booking_id if project is linked to booking
        });

        // 2. Insert project material using RPC function
        const { data: insertedId, error: addError } = await supabase
          .rpc('insert_booking_material', {
            p_booking_id: bookingId,
            p_project_id: project.id,
            p_material_id: null,
            p_item_id: stockItem.id,
            p_material_name: stockItem.name,
            p_material_description: stockItem.description || null,
            p_quantity: quantity,
            p_unit: stockItem.unit,
            p_cost: stockItem.acquisition_cost,
            p_total_cost: stockItem.acquisition_cost * quantity
          });

        if (addError) throw addError;
      } else {
        // Handle regular material
        const material = item as any; // Material type
        
        // Insert project material using RPC function
        const { data: insertedId, error: addError } = await supabase
          .rpc('insert_booking_material', {
            p_booking_id: bookingId,
            p_project_id: project.id,
            p_material_id: material.id,
            p_item_id: null,
            p_material_name: material.name,
            p_material_description: material.description || null,
            p_quantity: quantity,
            p_unit: material.unit,
            p_cost: material.cost,
            p_total_cost: material.cost * quantity
          });

        if (addError) throw addError;
      }

      await loadProjectMaterials();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error adding material:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to add material');
    }
  }, [project?.id, project?.booking_id, loadProjectMaterials, onUpdate]);

  // Handle removing material from project
  const handleRemoveMaterial = useCallback(async (id: string) => {
    if (!project?.id || !supabase) return;

    try {
      // 1. Fetch the project_material to check if it has item_id (inventory item)
      const { data: material, error: fetchError } = await supabase
        .from('booking_materials')
        .select('id, item_id, quantity, booking_id')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // 2. If it's an inventory item (item_id is not null), restore stock
      if (material.item_id) {
        // Find and delete the stock transaction for this project/item
        // If project is linked to booking, look for booking_id, otherwise look for project_id via notes
        let deleteQuery = supabase
          .from('stock_transactions')
          .delete()
          .eq('item_id', material.item_id)
          .eq('type', 'sale');

        // Try to match by booking_id if material has one, or by notes containing project_id
        if (material.booking_id) {
          deleteQuery = deleteQuery.eq('booking_id', material.booking_id);
        } else {
          // Match by notes containing the project id
          deleteQuery = deleteQuery.ilike('notes', `%project: ${project.id}%`);
        }

        const { error: transactionError } = await deleteQuery;

        // The deletion trigger will automatically restore stock quantity
        if (transactionError) {
          console.error('Error removing stock transaction:', transactionError);
          // Continue with material deletion even if transaction deletion fails
          // but show a warning
          toast.error('Material removed but stock may not have been restored. Please check inventory.');
        }
      }

      // 3. Delete the project_material entry
      const { error: deleteError } = await supabase
        .from('booking_materials')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      await loadProjectMaterials();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error removing material:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to remove material');
    }
  }, [project?.id, loadProjectMaterials, onUpdate]);

  // Load project materials when project opens or supplies tab is active
  useEffect(() => {
    if (isOpen && project?.id && (activeTab === 'supplies' || activeTab === 'materials')) {
      loadProjectMaterials();
    }
  }, [isOpen, project?.id, activeTab, loadProjectMaterials]);

  // Load project orders when project opens or supplies tab is active
  useEffect(() => {
    if (isOpen && project?.id && activeTab === 'supplies') {
      loadProjectOrders();
    }
  }, [isOpen, project?.id, activeTab, loadProjectOrders]);

  // Load order email thread
  const loadOrderEmailThread = useCallback(async (orderId: string) => {
    if (!selectedBusiness?.id) return;
    
    setLoadingOrderEmailThreads(prev => ({ ...prev, [orderId]: true }));
    
    try {
      const thread = await getOrderEmailThread(selectedBusiness.id, orderId);
      setOrderEmailThreads(prev => ({ ...prev, [orderId]: thread }));
    } catch (err) {
      console.error('Error loading order email thread:', err);
    } finally {
      setLoadingOrderEmailThreads(prev => ({ ...prev, [orderId]: false }));
    }
  }, [selectedBusiness?.id]);

  // Load order unread counts
  const loadOrderUnreadCounts = useCallback(async () => {
    if (!selectedBusiness?.id || !projectOrders.length) return;
    
    try {
      const counts: Record<string, number> = {};
      
      await Promise.all(
        projectOrders.map(async (order) => {
          if (order.id) {
            const count = await getUnreadCountForOrder(selectedBusiness.id, order.id);
            counts[order.id] = count;
          }
        })
      );
      
      setOrderUnreadCounts(counts);
    } catch (err) {
      console.error('Error loading order unread counts:', err);
    }
  }, [selectedBusiness?.id, projectOrders]);

  // Load order unread counts when project orders change
  useEffect(() => {
    if (projectOrders.length > 0 && selectedBusiness?.id) {
      loadOrderUnreadCounts();
    }
  }, [projectOrders, selectedBusiness?.id, loadOrderUnreadCounts]);

  // Load quote requests
  const loadQuoteRequests = useCallback(async () => {
    if (!selectedBusiness?.id || !project?.id) return;
    
    try {
      setLoadingQuoteRequests(true);
      const requests = await getQuoteRequests(selectedBusiness.id, { projectId: project.id });
      setQuoteRequests(requests || []);
    } catch (err) {
      console.error('Error loading quote requests:', err);
    } finally {
      setLoadingQuoteRequests(false);
    }
  }, [selectedBusiness?.id, project?.id]);

  const updateProjectStatus = useCallback(async (nextStatus: string) => {
    if (!selectedBusiness?.id) return;

    const currentProject = localProject || project;
    if (!currentProject?.id) return;

    if (currentProject.status === nextStatus) return;

    try {
      const { error } = await supabase
        .from('projects')
        .update({ status: nextStatus })
        .eq('id', currentProject.id)
        .eq('business_id', selectedBusiness.id);

      if (error) {
        console.error('Error updating project status:', error);
        return;
      }

      setLocalProject(prev =>
        prev ? { ...prev, status: nextStatus } : prev
      );

      onUpdate?.();
    } catch (err) {
      console.error('Error updating project status:', err);
    }
  }, [selectedBusiness?.id, localProject, project, onUpdate]);

  // Convert quote group to order
  const convertQuoteGroupToOrder = useCallback(async (groupId: string, batchRequests: QuoteRequest[]) => {
    if (!selectedBusiness?.id || !project?.id) return;
    
    setIsConvertingToOrder(true);
    setConvertToOrderGroupId(groupId);
    
    try {
      // Get the supplier from the first request
      const supplier = batchRequests[0]?.supplier;
      if (!supplier) {
        toast.error('Supplier information not found');
        return;
      }
      
      // Create one-time stock items for each quote request
      const orderItems: Array<{
        item: StockItem;
        quantityToOrder: number;
        totalCost: number;
        measurementId?: string | null;
        quoteRequestId?: string;
        measurement?: {
          name?: string;
          length?: number | string;
          width?: number | string;
          height?: number | string;
          area?: number | string;
          unit?: string;
          custom_fields?: Record<string, any>;
          notes?: string;
          photos?: Array<{ url: string }>;
        } | null;
      }> = [];
      
      for (const request of batchRequests) {
        // Build the description with measurement info
        let description = request.description || '';
        
        if (request.measurement) {
          const m = request.measurement;
          const measurementParts: string[] = [];
          
          if (m.length) measurementParts.push(`L: ${m.length}${m.unit}`);
          if (m.width) measurementParts.push(`W: ${m.width}${m.unit}`);
          if (m.height) measurementParts.push(`H: ${m.height}${m.unit}`);
          if (m.area) measurementParts.push(`Area: ${m.area}${m.unit}²`);
          
          if (m.custom_fields && Object.keys(m.custom_fields).length > 0) {
            Object.entries(m.custom_fields).forEach(([key, value]) => {
              measurementParts.push(`${key}: ${value}`);
            });
          }
          
          if (m.notes) measurementParts.push(`Notes: ${m.notes}`);
          
          if (measurementParts.length > 0) {
            description = description 
              ? `${description}\n\nMeasurement (${m.name}): ${measurementParts.join(' | ')}`
              : `Measurement (${m.name}): ${measurementParts.join(' | ')}`;
          }
        }
        
        // Create a one-time stock item for this quote request
        const stockItem = await createStockItem({
          business_id: selectedBusiness.id,
          name: request.item_name,
          description: description,
          category_id: null,
          supplier_id: request.supplier_id,
          unit: request.unit || 'unit',
          sku: null,
          price: request.retail_price || 0,
          is_sellable: false,
          acquisition_cost: request.estimated_cost || 0,
          tax_rate: 0,
          quantity: 0, // One-time items start at 0
          reorder_point: null,
          max_quantity: null,
          is_one_time: true
        });
        
        // Convert photo file_paths to actual URLs
        const photosWithUrls = request.measurement?.photos?.map(photo => ({
          url: getMeasurementPhotoUrl(photo.file_path)
        })) || [];
        
        orderItems.push({
          item: stockItem,
          quantityToOrder: request.estimated_quantity || 1,
          totalCost: (request.estimated_cost || 0) * (request.estimated_quantity || 1),
          measurementId: request.measurement_id,
          quoteRequestId: request.id,
          measurement: request.measurement ? {
            name: request.measurement.name,
            length: request.measurement.length || undefined,
            width: request.measurement.width || undefined,
            height: request.measurement.height || undefined,
            area: request.measurement.area || undefined,
            unit: request.measurement.unit,
            custom_fields: request.measurement.custom_fields || undefined,
            notes: request.measurement.notes || undefined,
            photos: photosWithUrls
          } : null
        });
      }
      
      // Calculate total
      const totalAmount = orderItems.reduce((sum, item) => sum + item.totalCost, 0);
      
      // Set the order data to trigger the modal
      setConvertToOrderData({
        supplier: supplier as Customer,
        items: orderItems,
        totalAmount
      });
      
    } catch (error) {
      console.error('Error converting quote to order:', error);
      toast.error('Failed to convert quote to order');
    } finally {
      setIsConvertingToOrder(false);
    }
  }, [selectedBusiness?.id, project?.id]);

  // Ref to track the last created order for email linking (ref avoids stale closure issues)
  const lastCreatedOrderRef = useRef<{ id: string; supplierId: string; supplierEmail: string } | null>(null);
  
  // Handle sending the converted order
  const handleSendConvertedOrder = useCallback(async (orderNumber: string): Promise<boolean | string> => {
    if (!selectedBusiness?.id || !convertToOrderData || !project?.id) return false;
    
    try {
      // Use the orderNumber passed from OrderSummaryPDFModal (ensures email and DB match)
      
      // Create the order
      const orderData: Order = {
        business_id: selectedBusiness.id,
        supplier_id: convertToOrderData.supplier.id,
        order_number: orderNumber,
        total_amount: convertToOrderData.totalAmount,
        status: 'pending',
        project_id: project.id,
        notes: `Converted from quote request`
      };
      
      const orderItems: DBOrderItem[] = convertToOrderData.items.map(item => ({
        item_id: item.item.id,
        quantity: item.quantityToOrder,
        unit_price: item.item.acquisition_cost,
        total_price: item.totalCost,
        item_name: item.item.name,
        item_unit: item.item.unit,
        notes: item.measurementId ? `Measurement ID: ${item.measurementId}` : undefined
      }));
      
      const createdOrder = await createOrder(orderData, orderItems);
      await updateProjectStatus('Pending');
      
      // Store the created order info in ref for email linking (avoids stale closure)
      lastCreatedOrderRef.current = {
        id: createdOrder.id,
        supplierId: convertToOrderData.supplier.id,
        supplierEmail: convertToOrderData.supplier.email || ''
      };
      
      console.log('📦 Order created, storing ref for email linking:', lastCreatedOrderRef.current);
      
      // Update quote request statuses to 'ordered'
      for (const item of convertToOrderData.items) {
        if (item.quoteRequestId) {
          await updateQuoteRequest(item.quoteRequestId, {
            status: 'ordered'
          });
        }
      }
      
      toast.success('Order created successfully');
      
      // Refresh quote requests data (order status updated)
      await loadQuoteRequests();
      
      // Note: Don't close modal or refresh orders yet - 
      // handleOrderEmailSent will do that after email is linked
      // If email isn't sent, OrderSummaryPDFModal will close itself
      
      // Return the DB-assigned PO number so the email/SMS use it.
      return createdOrder.order_number || true;
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order');
      return false;
    }
  }, [selectedBusiness?.id, convertToOrderData, project?.id, loadQuoteRequests]);

  // Handle email sent callback - link email to order
  const handleOrderEmailSent = useCallback(async (orderNumber: string, messageId: string, threadId: string, emailBody: string) => {
    if (!selectedBusiness?.id) return;
    
    console.log('📧 handleOrderEmailSent called:', { orderNumber, messageId, threadId });
    console.log('📧 lastCreatedOrderRef:', lastCreatedOrderRef.current);
    
    // Use the ref first (most reliable), then try to find by order number
    const orderFromRef = lastCreatedOrderRef.current;
    const orderFromList = projectOrders.find(o => o.order_number === orderNumber);
    
    const orderId = orderFromRef?.id || orderFromList?.id;
    const supplierId = orderFromRef?.supplierId || orderFromList?.supplier_id;
    const supplierEmail = orderFromRef?.supplierEmail || orderFromList?.supplier?.email;
    
    if (orderId && supplierId && supplierEmail) {
      try {
        // Update order with email tracking info
        await updateOrderEmailInfo(orderId, messageId, threadId);
        
        // Store the outgoing email in order_email_threads
        await linkEmailToOrder(
          orderId,
          selectedBusiness.id,
          supplierId,
          supplierEmail,
          `[ORDER-${orderNumber}] ${orderNumber}`,
          emailBody,
          messageId
        );
        
        console.log('✅ Order email linked successfully:', orderId, messageId, threadId);
        
        // Clear the ref
        lastCreatedOrderRef.current = null;
        
        // Now refresh data and close modal
        await loadQuoteRequests();
        await loadProjectOrders();
        await loadOrderUnreadCounts();
        
        // Close the convert to order modal
        setConvertToOrderData(null);
        setConvertToOrderGroupId(null);
      } catch (error) {
        console.error('Error linking email to order:', error);
      }
    } else {
      console.warn('Could not find order ID to link email:', { orderNumber, orderId, supplierId, supplierEmail, orderFromRef, orderFromList });
    }
  }, [selectedBusiness?.id, projectOrders, loadQuoteRequests, loadProjectOrders, loadOrderUnreadCounts]);

  // Function to strip quoted/forwarded content from email replies
  const stripQuotedContent = useCallback((content: string, isHtml: boolean): string => {
    if (!content) return '';
    
    if (isHtml) {
      // For HTML emails, remove blockquotes and quoted sections
      let cleaned = content;
      
      // Remove blockquote elements (most common way to quote in HTML emails)
      cleaned = cleaned.replace(/<blockquote[^>]*>[\s\S]*?<\/blockquote>/gi, '');
      
      // Remove divs with border-left styling (common for quoted content)
      cleaned = cleaned.replace(/<div[^>]*style="[^"]*border-left[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
      
      // Find the position of "On ... wrote:" pattern and remove everything after it
      const onWroteMatch = cleaned.match(/On\s+.*?\s+wrote:/i);
      if (onWroteMatch && onWroteMatch.index !== undefined) {
        cleaned = cleaned.substring(0, onWroteMatch.index);
      }
      
      // Remove content after common reply separators
      const fromMatch = cleaned.match(/<[^>]*>[\s\S]*?From:\s*[^<]*@[^<]*/i);
      if (fromMatch && fromMatch.index !== undefined) {
        // Check if this appears after some content (not at the start)
        if (fromMatch.index > 100) {
          cleaned = cleaned.substring(0, fromMatch.index);
        }
      }
      
      // Remove any remaining quoted content patterns
      cleaned = cleaned.replace(/<[^>]*>[\s\S]*?Sent:\s*.*?[\s\S]*$/i, '');
      
      return cleaned.trim();
    } else {
      // For plain text emails, remove quoted lines
      const lines = content.split(/\r?\n/);
      const result: string[] = [];
      
      for (const line of lines) {
        // Stop at common quote markers
        if (/^On\s+.*?\s+wrote:/i.test(line.trim())) {
          break;
        }
        if (line.trim().startsWith('>')) {
          break;
        }
        if (/^-{3,}/.test(line.trim())) {
          break;
        }
        if (/^From:\s*/i.test(line.trim()) && result.length > 5) {
          break;
        }
        if (/^Sent:\s*/i.test(line.trim()) && result.length > 5) {
          break;
        }
        
        result.push(line);
      }
      
      return result.join('\n').trim();
    }
  }, []);

  // Auto-scroll email thread to bottom when modal opens or thread loads
  // Use useLayoutEffect to scroll synchronously before paint (no visible scroll animation)
  useLayoutEffect(() => {
    if (emailThreadModalGroupId && emailThreadScrollRef.current) {
      const element = emailThreadScrollRef.current;
      
      // If content is already loaded, scroll immediately
      if (emailThreads[emailThreadModalGroupId] && !loadingEmailThreads[emailThreadModalGroupId]) {
        // Set scroll position synchronously before paint
        element.scrollTop = element.scrollHeight;
      }
    }
  }, [emailThreadModalGroupId, emailThreads, loadingEmailThreads]);
  
  // Also handle scrolling when content finishes loading
  useEffect(() => {
    if (emailThreadModalGroupId && emailThreadScrollRef.current) {
      const element = emailThreadScrollRef.current;
      
      // When loading finishes and content is available, scroll immediately
      if (emailThreads[emailThreadModalGroupId] && !loadingEmailThreads[emailThreadModalGroupId]) {
        // Use requestAnimationFrame to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          if (element) {
            element.scrollTop = element.scrollHeight;
          }
        });
      }
    }
  }, [emailThreadModalGroupId, emailThreads, loadingEmailThreads]);

  // Auto-scroll order email thread to bottom when modal opens or thread loads
  useLayoutEffect(() => {
    if (orderEmailThreadModalId && orderEmailThreadScrollRef.current) {
      const element = orderEmailThreadScrollRef.current;
      
      if (orderEmailThreads[orderEmailThreadModalId] && !loadingOrderEmailThreads[orderEmailThreadModalId]) {
        element.scrollTop = element.scrollHeight;
      }
    }
  }, [orderEmailThreadModalId, orderEmailThreads, loadingOrderEmailThreads]);
  
  useEffect(() => {
    if (orderEmailThreadModalId && orderEmailThreadScrollRef.current) {
      const element = orderEmailThreadScrollRef.current;
      
      if (orderEmailThreads[orderEmailThreadModalId] && !loadingOrderEmailThreads[orderEmailThreadModalId]) {
        requestAnimationFrame(() => {
          if (element) {
            element.scrollTop = element.scrollHeight;
          }
        });
      }
    }
  }, [orderEmailThreadModalId, orderEmailThreads, loadingOrderEmailThreads]);

  // Load email thread for a quote request group
  const loadEmailThread = useCallback(async (groupId: string) => {
    if (!selectedBusiness?.id || !groupId || groupId === 'ungrouped') return;
    
    // Don't reload if already loaded
    if (emailThreads[groupId]) return;
    
    try {
      setLoadingEmailThreads(prev => ({ ...prev, [groupId]: true }));
      const thread = await getQuoteRequestEmailThread(selectedBusiness.id, groupId);
      setEmailThreads(prev => ({ ...prev, [groupId]: thread }));
      
      // Update unread count based on loaded thread data
      const unreadCount = thread.filter(email => 
        email.direction === 'inbound' && 
        email.read === 'unread' && 
        !locallyReadEmails.has(email.id)
      ).length;
      
      setUnreadCounts(prev => ({
        ...prev,
        [groupId]: unreadCount
      }));
    } catch (err) {
      console.error('Error loading email thread:', err);
    } finally {
      setLoadingEmailThreads(prev => ({ ...prev, [groupId]: false }));
    }
  }, [selectedBusiness?.id, emailThreads, locallyReadEmails]);

  // Mark email as read (for inbound emails only)
  const markEmailAsRead = useCallback(async (emailId: string, allMessagesId: string | null | undefined, externalId?: string | null) => {
    if (!selectedBusiness?.id) return;
    
    try {
      // Immediately update local state for instant UI update
      setLocallyReadEmails(prev => new Set([...prev, emailId]));
      
      let updateSuccessful = false;
      
      console.log('markEmailAsRead called with:', { emailId, allMessagesId, externalId });
      
      // Try to update by all_messages_id first
      if (allMessagesId) {
        const { error, count } = await supabase
          .from('all_messages')
          .update({ read: 'read' })
          .eq('id', allMessagesId)
          .eq('business_id', selectedBusiness.id)
          .select();
        
        console.log('Update by all_messages_id result:', { error, count });
        
        if (!error) {
          updateSuccessful = true;
        } else {
          console.warn('Failed to update by all_messages_id, trying external_id:', error);
        }
      }
      
      // If that didn't work and we have external_id, try that
      if (!updateSuccessful && externalId) {
        const { error, data } = await supabase
          .from('all_messages')
          .update({ read: 'read' })
          .eq('twilio_message_sid', externalId) // twilio_message_sid stores gmail_id for emails
          .eq('business_id', selectedBusiness.id)
          .select();
        
        console.log('Update by external_id result:', { error, data, externalId });
        
        if (!error && data && data.length > 0) {
          updateSuccessful = true;
        } else if (!error && (!data || data.length === 0)) {
          console.warn('No rows updated by external_id - message may not exist in all_messages yet');
        } else {
          console.warn('Failed to update by external_id:', error);
        }
      }
      
      if (!updateSuccessful) {
        console.error('Could not mark email as read - no valid ID found or no rows updated');
        console.error('Debug info:', { emailId, allMessagesId, externalId, businessId: selectedBusiness.id });
        // Still keep it marked locally since the email exists - just might not be synced yet
        // Don't revert the local state - this provides better UX
      }
      
      // Update email thread state
      if (emailThreadModalGroupId && emailThreads[emailThreadModalGroupId]) {
        setEmailThreads(prev => {
          const updated = { ...prev };
          if (updated[emailThreadModalGroupId]) {
            updated[emailThreadModalGroupId] = updated[emailThreadModalGroupId].map(email =>
              email.id === emailId ? { ...email, read: 'read' as const } : email
            );
          }
          return updated;
        });
      }
      
      // Update unread count for this group
      if (emailThreadModalGroupId) {
        setUnreadCounts(prev => {
          const current = prev[emailThreadModalGroupId] || 0;
          return {
            ...prev,
            [emailThreadModalGroupId]: Math.max(0, current - 1)
          };
        });
      }
      
      // Dispatch custom event to notify navigation badge
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking email as read:', error);
      toast.error('Failed to mark email as read');
    }
  }, [selectedBusiness?.id, emailThreadModalGroupId, emailThreads]);

  // Load unread counts for all quote request groups
  const loadUnreadCounts = useCallback(async () => {
    if (!selectedBusiness?.id || !quoteRequests.length) return;
    
    try {
      const counts: Record<string, number> = {};
      
      // Get unique group IDs
      const groupIds = [...new Set(
        quoteRequests
          .map(qr => qr.quote_request_group_id)
          .filter((id): id is string => id !== null && id !== undefined)
      )];
      
      // Fetch unread counts for each group
      await Promise.all(
        groupIds.map(async (groupId) => {
          const count = await getUnreadCountForQuoteRequestGroup(selectedBusiness.id, groupId);
          counts[groupId] = count;
        })
      );
      
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Error loading unread counts:', error);
    }
  }, [selectedBusiness?.id, quoteRequests]);

  // Count unread messages for a quote request group (uses cached count or thread data)
  const getUnreadCountForGroup = useCallback((groupId: string | null | undefined): number => {
    if (!groupId) return 0;
    
    // First, check loaded thread data (most accurate and real-time)
    if (emailThreads[groupId]) {
      const thread = emailThreads[groupId];
      const count = thread.filter(email => 
        email.direction === 'inbound' && 
        email.read === 'unread' && 
        !locallyReadEmails.has(email.id)
      ).length;
      return count;
    }
    
    // Fallback to cached count from database query
    if (unreadCounts[groupId] !== undefined) {
      return unreadCounts[groupId];
    }
    
    return 0;
  }, [emailThreads, locallyReadEmails, unreadCounts]);

  // Get unread count for order (used for display)
  const getOrderUnreadCount = useCallback((orderId: string | null | undefined): number => {
    if (!orderId) return 0;
    
    // First, check loaded thread data
    if (orderEmailThreads[orderId]) {
      const thread = orderEmailThreads[orderId];
      const count = thread.filter(email => 
        email.direction === 'inbound' && 
        email.read === 'unread' && 
        !locallyReadEmails.has(email.id)
      ).length;
      return count;
    }
    
    // Fallback to cached count
    if (orderUnreadCounts[orderId] !== undefined) {
      return orderUnreadCounts[orderId];
    }
    
    return 0;
  }, [orderEmailThreads, locallyReadEmails, orderUnreadCounts]);

  // Send reply to supplier for order
  const handleSendOrderReply = useCallback(async () => {
    if (!orderEmailThreadModalId || !selectedBusiness?.id || !orderReplyMessage.trim()) return;
    
    // Get order info
    const order = projectOrders.find(o => o.id === orderEmailThreadModalId);
    if (!order?.supplier?.email) {
      toast.error('Supplier email not found');
      return;
    }
    
    // Get the original subject and thread ID from the first email
    const thread = orderEmailThreads[orderEmailThreadModalId] || [];
    const firstEmail = thread[0];
    const lastEmail = thread[thread.length - 1];
    const originalSubject = firstEmail?.subject || `Re: Order ${order.order_number}`;
    const replySubject = originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`;
    
    // Get thread ID and message ID for proper threading
    const threadId = order.outgoing_email_thread_id || firstEmail?.external_id || null;
    const inReplyTo = lastEmail?.external_id || order.outgoing_email_message_id || null;
    const references = thread
      .map(e => e.external_id)
      .filter(Boolean)
      .join(' ') || inReplyTo;
    
    setIsSendingOrderReply(true);
    
    try {
      const result = await sendGmailEmail({
        businessId: selectedBusiness.id,
        to: order.supplier.email,
        subject: replySubject,
        body: orderReplyMessage,
        threadId: threadId || undefined,
        inReplyTo: inReplyTo || undefined,
        references: references || undefined
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send reply');
      }
      
      // Store the reply in order_email_threads
      if (result.messageId) {
        await linkEmailToOrder(
          orderEmailThreadModalId,
          selectedBusiness.id,
          order.supplier_id,
          order.supplier.email,
          replySubject,
          orderReplyMessage,
          result.messageId
        );
      }
      
      toast.success('Reply sent successfully');
      setOrderReplyMessage('');
      
      // Reload thread
      await loadOrderEmailThread(orderEmailThreadModalId);
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setIsSendingOrderReply(false);
    }
  }, [orderEmailThreadModalId, selectedBusiness?.id, orderReplyMessage, projectOrders, orderEmailThreads, loadOrderEmailThread]);

  // Send reply to supplier
  const handleSendReply = useCallback(async () => {
    if (!emailThreadModalGroupId || !selectedBusiness?.id || !replyMessage.trim()) return;
    
    // Get supplier info from quote request
    const quoteRequest = quoteRequests.find(qr => qr.quote_request_group_id === emailThreadModalGroupId);
    if (!quoteRequest?.supplier?.email) {
      toast.error('Supplier email not found');
      return;
    }
    
    // Get the original subject and thread ID from the first email
    const thread = emailThreads[emailThreadModalGroupId] || [];
    const firstEmail = thread[0];
    const lastEmail = thread[thread.length - 1];
    const originalSubject = firstEmail?.subject || 'Re: Quote Request';
    const replySubject = originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`;
    
    // Get thread ID from quote request
    const threadId = quoteRequest.outgoing_email_thread_id || undefined;
    
    // Get message ID to reply to (use the last email's external_id, or first if no last)
    const inReplyTo = lastEmail?.external_id || firstEmail?.external_id || undefined;
    
    // Build references header (list of all message IDs in the thread)
    const references = thread
      .map(email => email.external_id)
      .filter(id => id)
      .join(' ');
    
    setIsSendingReply(true);
    try {
      // Send the reply email
      const result = await sendGmailEmail({
        businessId: selectedBusiness.id,
        to: quoteRequest.supplier.email,
        subject: replySubject.replace(/\[QUOTE-[^\]]+\]/gi, '').trim(),
        body: replyMessage.trim(),
        threadId: threadId,
        inReplyTo: inReplyTo,
        references: references || undefined,
        attachments: undefined
      });
      
      if (result.success && result.messageId) {
        // Link the reply to the quote request group
        if (emailThreadModalGroupId && quoteRequest.supplier_id) {
          await linkEmailToQuoteRequestGroup(
            selectedBusiness.id,
            emailThreadModalGroupId,
            result.messageId,
            result.threadId,
            quoteRequest.supplier.email,
            replySubject,
            replyMessage.trim(),
            quoteRequest.supplier_id
          );
        }
        
        // Clear reply message
        setReplyMessage('');
        
        // Refresh email thread
        setEmailThreads(prev => {
          const updated = { ...prev };
          delete updated[emailThreadModalGroupId]; // Force reload
          return updated;
        });
        await loadEmailThread(emailThreadModalGroupId);
        
        toast.success('Reply sent successfully');
      } else {
        toast.error(result.error || 'Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setIsSendingReply(false);
    }
  }, [emailThreadModalGroupId, selectedBusiness?.id, replyMessage, quoteRequests, emailThreads, loadEmailThread]);

  // Load project orders and quote requests when project changes (while modal is open)
  useEffect(() => {
    if (project?.id && selectedBusiness?.id) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      loadProjectOrders();
      loadQuoteRequests();
    }
  }, [project?.id, selectedBusiness?.id, loadProjectOrders, loadQuoteRequests]);

  // Load unread counts and pre-load email threads when quote requests change
  useEffect(() => {
    if (quoteRequests.length > 0 && selectedBusiness?.id) {
      loadUnreadCounts();
      
      // Pre-load email threads for all groups to get accurate unread counts
      const groupIds = [...new Set(
        quoteRequests
          .map(qr => qr.quote_request_group_id)
          .filter((id): id is string => id !== null && id !== undefined)
      )];
      
      // Load threads in parallel (but don't wait for them)
      groupIds.forEach(groupId => {
        if (!emailThreads[groupId]) {
          loadEmailThread(groupId);
        }
      });
    }
  }, [quoteRequests, selectedBusiness?.id, loadUnreadCounts, emailThreads, loadEmailThread]);

  // Load unread counts when quote requests change
  useEffect(() => {
    if (quoteRequests.length > 0 && selectedBusiness?.id) {
      loadUnreadCounts();
    }
  }, [quoteRequests, selectedBusiness?.id, loadUnreadCounts]);

  // Reload project orders when CreateOrderModal closes
  useEffect(() => {
    if (!isCreateOrderModalOpen && project?.id && selectedBusiness?.id) {
      loadProjectOrders();
    }
  }, [isCreateOrderModalOpen, project?.id, selectedBusiness?.id, loadProjectOrders]);

  // Load on-order quantities and stock items when CreateOrderModal is about to open
  useEffect(() => {
    if (isCreateOrderModalOpen && selectedBusiness?.id) {
      loadOnOrderQuantities();
      if (stockItems.length === 0) {
        loadStockItems();
      }
    }
  }, [isCreateOrderModalOpen, stockItems.length, selectedBusiness?.id, loadStockItems, loadOnOrderQuantities]);

  // Load linked booking info when project has booking_id
  // MUST be called before any early returns to follow Rules of Hooks
  useEffect(() => {
    const loadLinkedBooking = async () => {
      const currentProject = localProject || project;
      if (currentProject?.booking_id && supabase) {
        try {
          const { data: bookingData, error } = await supabase
            .from('bookings')
            .select('id, booking_number, title, is_deleted')
            .eq('id', currentProject.booking_id)
            .single();
          
          if (!error && bookingData && !bookingData.is_deleted) {
            setLinkedBooking({
              id: bookingData.id,
              booking_number: bookingData.booking_number || 0,
              title: bookingData.title || 'Booking'
            });
          } else {
            setLinkedBooking(null);
            if (bookingData?.is_deleted) {
              setLocalProject(prev => prev ? { ...prev, booking_id: null } : prev);
            }
          }
        } catch (error) {
          console.error('Error loading linked booking:', error);
          setLinkedBooking(null);
        }
      } else {
        setLinkedBooking(null);
      }
    };

    if (isOpen) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      loadLinkedBooking();
    }
  }, [isOpen, localProject?.booking_id, project?.booking_id]);

  useEffect(() => {
    const currentProject = localProject || project;
    if (!isOpen || !currentProject?.booking_id || !supabase) return;

    const channel = supabase
      .channel(`project-booking-${currentProject.booking_id}`)
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'bookings',
          filter: `id=eq.${currentProject.booking_id}`
        },
        async () => {
          try {
            const { data: updatedProject, error } = await supabase
              .from('projects')
              .select('*')
              .eq('id', currentProject.id)
              .single();

            if (!error && updatedProject) {
              setLocalProject(updatedProject);
            } else {
              setLocalProject(prev => prev ? { ...prev, booking_id: null } : prev);
            }
          } catch (error) {
            console.error('Error refreshing project after booking delete:', error);
            setLocalProject(prev => prev ? { ...prev, booking_id: null } : prev);
          } finally {
            setLinkedBooking(null);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bookings',
          filter: `id=eq.${currentProject.booking_id}`
        },
        async (payload) => {
          if ((payload.new as any)?.is_deleted) {
            try {
              const { data: updatedProject, error } = await supabase
                .from('projects')
                .select('*')
                .eq('id', currentProject.id)
                .single();

              if (!error && updatedProject) {
                setLocalProject(updatedProject);
              } else {
                setLocalProject(prev => prev ? { ...prev, booking_id: null } : prev);
              }
            } catch (error) {
              console.error('Error refreshing project after booking soft delete:', error);
              setLocalProject(prev => prev ? { ...prev, booking_id: null } : prev);
            } finally {
              setLinkedBooking(null);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isOpen, localProject?.booking_id, project?.booking_id]);

  // Load linked opportunity info when project has pipeline_opportunities_id
  useEffect(() => {
    const loadLinkedOpportunity = async () => {
      const currentProject = localProject || project;
      if (currentProject?.pipeline_opportunities_id && supabase) {
        setIsLoadingLinkedOpportunity(true);
        try {
          const { data: opportunityData, error } = await supabase
            .from('pipeline_opportunities')
            .select('id, title, value, status')
            .eq('id', currentProject.pipeline_opportunities_id)
            .single();
          
          if (!error && opportunityData) {
            setLinkedOpportunity({
              id: opportunityData.id,
              title: opportunityData.title || 'Opportunity',
              value: opportunityData.value || 0,
              status: opportunityData.status || 'open'
            });
          } else {
            setLinkedOpportunity(null);
          }
        } catch (error) {
          console.error('Error loading linked opportunity:', error);
          setLinkedOpportunity(null);
        } finally {
          setIsLoadingLinkedOpportunity(false);
        }
      } else {
        setLinkedOpportunity(null);
        setIsLoadingLinkedOpportunity(false);
      }
    };

    if (isOpen) {
      if (!prevIsOpenRef.current) return; // Batch loader handles initial open
      loadLinkedOpportunity();
    }
  }, [isOpen, localProject?.pipeline_opportunities_id, project?.pipeline_opportunities_id]);

  // Listen for bookingCreated event to refresh project data after conversion
  // MUST be called before any early returns to follow Rules of Hooks
  useEffect(() => {
    const handleBookingCreated = async (event: CustomEvent) => {
      // Only handle if this booking was created from our project
      const currentProject = localProject || project;
      if (!currentProject?.id) {
        return;
      }

      const bookingId = event.detail?.bookingId;
      const projectId = event.detail?.projectId;

      // Only proceed if this booking is linked to our project
      if (bookingId && projectId === currentProject.id) {
        // Refresh project data to get updated booking_id
        setTimeout(async () => {
          if (supabase && currentProject.id) {
            const { data: updatedProject } = await supabase
              .from('projects')
              .select('*')
              .eq('id', currentProject.id)
              .single();

            if (updatedProject) {
              setLocalProject(updatedProject as Project);
            }

            // Load linked booking info
            const { data: bookingData } = await supabase
              .from('bookings')
              .select('id, booking_number, title')
              .eq('id', bookingId)
              .single();

            if (bookingData) {
              setLinkedBooking({
                id: bookingData.id,
                booking_number: bookingData.booking_number || 0,
                title: bookingData.title || 'Booking'
              });
            }

            // Refresh payments
            const { data: updatedPayments } = await supabase
              .from('payments')
              .select('*')
              .eq('project_id', currentProject.id)
              .order('created_at', { ascending: false });

            if (updatedPayments) {
              setProjectPayments(updatedPayments as Payment[]);
            }

            if (onUpdate) {
              onUpdate();
            }
          }
        }, 1000);
      }
    };

    if (isOpen) {
      window.addEventListener('bookingCreated', handleBookingCreated as EventListener);
      return () => {
        window.removeEventListener('bookingCreated', handleBookingCreated as EventListener);
      };
    }
  }, [isOpen, localProject?.id, project?.id, onUpdate]);

  // Early returns - MUST be after all hooks
  // Don't render if modal is not open
  // Track whether modal was open so individual effects can distinguish
  // "initial open" (batch loader handles) from "data changed while open".
  // This runs AFTER all other effects in the same render cycle.
  useEffect(() => {
    prevIsOpenRef.current = isOpen;
  });

  if (!isOpen && !isModalVisible) return null;
  
  // Don't render if we don't have project data
  if (!isOpen) return null;
  if (!project && !localProject) {
    console.warn('⚠️ ProjectInfoModal: No project data available', { isOpen, project, localProject });
    return null;
  }
  
  // Validate project has required fields
  const currentProject = localProject || project;
  if (!currentProject?.id || !currentProject?.business_id || !currentProject?.title) {
    console.error('❌ ProjectInfoModal: Invalid project data - missing required fields', {
      id: currentProject?.id,
      business_id: currentProject?.business_id,
      title: currentProject?.title,
      fullProject: currentProject
    });
    return null;
  }

  // Function to convert project services to booking services format
  const convertProjectServicesToBookingFormat = () => {
    if (!projectServices || projectServices.length === 0) {
      return [];
    }

    // Group services by vehicle
    const servicesByVehicle = projectServices.reduce<Record<string, ProjectService[]>>((acc, service) => {
      const vehicleId = service.vehicle_id || 'no-vehicle';
      if (!acc[vehicleId]) {
        acc[vehicleId] = [];
      }
      acc[vehicleId].push(service);
      return acc;
    }, {});

    // Convert to booking format
    return Object.entries(servicesByVehicle).map(([vehicleId, services]) => ({
      vehicle_id: vehicleId === 'no-vehicle' ? null : vehicleId,
      services: services.map(service => ({
        service_id: service.service_id,
        name: service.name,
        description: service.description || '',
        price: service.price,
        tax_exempt: service.tax_exempt || false,
        tax_rate: service.tax_rate || 5,
        tax_amount: service.tax_amount || 0,
        discount_amount: service.discount_amount || 0,
        total_amount: service.total_amount || service.price,
        duration_minutes: service.duration_minutes
      }))
    }));
  };

  // Handle Convert to Booking button click
  const handleConvertToBooking = () => {
    console.log('🔄 Convert to Booking clicked', {
      displayProject: displayProject?.id,
      customer: customer?.id,
      projectServices: projectServices.length
    });

    if (!displayProject) {
      toast.error('Project not found');
      return;
    }

    if (!customer) {
      toast.error('Project must have a customer to convert to booking');
      return;
    }

    if (projectServices.length === 0) {
      toast.error('Project must have at least one service to convert to booking');
      return;
    }

    if (showLocationBookingSetting && !showMobileBookingSetting) {
      console.log('✅ Opening CreateBookingModal (location only)');
      setIsCreateBookingModalOpen(true);
      return;
    }

    if (!showLocationBookingSetting && showMobileBookingSetting) {
      console.log('✅ Opening CreateMobileBookingModal (mobile only)');
      setIsCreateMobileBookingModalOpen(true);
      return;
    }

    if (showLocationBookingSetting && showMobileBookingSetting) {
      setIsBookingTypeSelectionOpen(true);
      return;
    }

    toast.error('Enable location or mobile booking in calendar settings to convert');
  };

  // Convert project data to proposal-like format for CreateBookingModal
  const convertProjectToProposalFormat = () => {
    if (!displayProject || !customer || projectServices.length === 0) {
      return null;
    }

    // Convert project services to proposal services format
    const proposalServices = projectServices.map(service => ({
      id: service.id,
      service_id: service.service_id,
      vehicle_id: service.vehicle_id || null,
      name: service.name,
      description: service.description || '',
      price: service.price,
      discount_amount: service.discount_amount || 0,
      discount_type: service.discount_amount > 0 ? 'dollar' as const : undefined,
      discount_value: service.discount_amount || 0,
      tax_exempt: service.tax_exempt || false,
      tax_rate: service.tax_rate || 5,
      tax_amount: service.tax_amount || 0,
      total_amount: service.total_amount || service.price,
      duration_minutes: service.duration_minutes,
      is_selected: true,
      isSelectable: false,
      vehicle_make: service.vehicle?.make || '',
      vehicle_model: service.vehicle?.model || '',
      vehicle_year: service.vehicle?.year || 0,
      vehicle_color: service.vehicle?.color || ''
    }));

    // Create a proposal-like object with project_id flag
    return {
      id: displayProject.id,
      business_id: displayProject.business_id,
      customer_id: displayProject.customer_id,
      title: displayProject.title,
      status: 'approved',
      project_id: displayProject.id, // Flag to indicate this is a project
      isProject: true, // Additional flag
      pipeline_opportunities_id: displayProject.pipeline_opportunities_id || null, // Include opportunity link
      offers: [{
        id: 'project-offer',
        title: 'Project Services',
        status: 'approved',
        services: proposalServices,
        deposit_percentage: null
      }],
      customer: customer
    };
  };

  const buildVehicleFromProjectService = (service: ProjectService): CustomerVehicle => {
    if (service.vehicle) {
      return {
        id: service.vehicle_id || service.vehicle.id,
        customer_id: customer?.id || '',
        make: service.vehicle.make || '',
        model: service.vehicle.model || '',
        year: service.vehicle.year || 0,
        vin: service.vehicle.vin || null,
        color: service.vehicle.color || null,
        license_plate: service.vehicle.license_plate || null,
        mileage: service.vehicle.mileage || null,
        notes: service.vehicle.notes || null,
        is_primary: service.vehicle.is_primary || false,
        last_service: service.vehicle.last_service || null,
        created_at: service.vehicle.created_at || new Date().toISOString(),
        updated_at: service.vehicle.updated_at || new Date().toISOString()
      };
    }

    return {
      id: service.vehicle_id || 'no-vehicle',
      customer_id: customer?.id || '',
      make: '',
      model: '',
      year: 0,
      vin: null,
      color: null,
      license_plate: null,
      mileage: null,
      notes: null,
      is_primary: false,
      last_service: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  };

  const convertProjectToMobilePrefill = () => {
    if (!displayProject || !customer || projectServices.length === 0) {
      return null;
    }

    return {
      customer,
      services: projectServices.map(service => ({
        vehicle: buildVehicleFromProjectService(service),
        service: {
          id: service.service_id,
          name: service.name,
          description: service.description ?? '',
          duration_minutes: service.duration_minutes,
          tax_rate: service.tax_rate || 5,
          tax_exempt: service.tax_exempt || false
        },
        priceOption: {
          price: service.price,
          duration_minutes: service.duration_minutes
        }
      })),
      bookingAddress: customer.address || '',
      bookingSource: (localProject || project)?.customer_source || null
    };
  };

  // Early return check - MUST be after all hooks
  if (!displayProject) return null;
  
  // Detect if we're on iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  // Modal content to render
  const modalContent = (
    <div
      className={`fixed inset-0 z-[9999] project-info-modal ios-modal-fix ${isModalVisible ? '' : 'pointer-events-none'}`}
      aria-modal="true"
      role="dialog"
      style={{
        zIndex: 99999,
        isolation: 'isolate',
        WebkitOverflowScrolling: 'touch',
        WebkitBackfaceVisibility: 'hidden',
        position: 'fixed',
        top: 'env(safe-area-inset-top, 0px)',
        left: 'env(safe-area-inset-left, 0px)',
        right: 'env(safe-area-inset-right, 0px)',
        bottom: 'env(safe-area-inset-bottom, 0px)',
        height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        width: 'calc(100% - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))'
      }}
    >
      <div 
        className={`fixed inset-0 z-[99900] ios-modal-backdrop ${
          isModalVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          position: 'fixed',
          zIndex: 99900,
          top: 'env(safe-area-inset-top, 0px)',
          left: 'env(safe-area-inset-left, 0px)',
          right: 'env(safe-area-inset-right, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          pointerEvents: isModalVisible ? 'auto' : 'none'
        }}
        onClick={handleBackdropClick}
      >
        <div 
          className={`fixed inset-0 ios-modal-content transition-colors duration-200 ${
            isModalVisible ? 'bg-gray-50' : 'bg-black'
          }`}
          style={{ 
            WebkitBackfaceVisibility: 'hidden',
            zIndex: 99950,
            position: 'fixed',
            top: 'env(safe-area-inset-top, 0px)',
            left: 'env(safe-area-inset-left, 0px)',
            right: 'env(safe-area-inset-right, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            width: 'calc(100% - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))',
            height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            display: isOpen ? 'block' : 'none'
          }}
        >
          <div className={`h-screen flex flex-col transition-opacity duration-200 ${
            isModalVisible ? 'opacity-100' : 'opacity-0'
          }`} style={{ 
            paddingTop: 0,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            position: 'relative',
            zIndex: 9999
          }}>
            {/* Header - Matching BookingInfoModal style */}
            <div 
              className="relative bg-black border-b border-gray-200 shadow-sm"
              style={{
                zIndex: 10,
                width: '100%'
              }}
            >
              <div className="flex items-start justify-between px-4 py-3">
                {/* Left side - Close button */}
                <div className="flex items-center flex-shrink-0">
                  <button
                    onClick={onClose}
                    className="flex items-center justify-center w-8 h-8 rounded-full text-white transition-all duration-200 active:scale-95 touch-manipulation"
                    style={{
                      WebkitTapHighlightColor: 'transparent',
                      backgroundColor: 'transparent'
                    }}
                    onMouseDown={(e) => {
                      if (navigator.vibrate) navigator.vibrate(10);
                    }}
                    onTouchStart={(e) => {
                      if (navigator.vibrate) navigator.vibrate(10);
                    }}
                    aria-label="Close modal"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Center - Project title */}
                <div className="flex-1 flex justify-center px-2 min-w-0">
                  <div className="flex-1 min-w-0 w-full">
                    <div className="text-center">
                      {/* Linked Opportunity Badge */}
                      {linkedOpportunity && (
                        <button
                          onClick={() => {
                            if (linkedOpportunity?.id) {
                              // Close this modal first
                              onClose();
                              // Navigate to CRM page and open the opportunity modal
                              navigate('/crm', { 
                                state: { openOpportunityId: linkedOpportunity.id } 
                              });
                            }
                          }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-1.5 bg-blue-500/20 hover:bg-blue-500/30 active:bg-blue-500/40 rounded-full transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <TrendingUp className="h-3 w-3 text-blue-400 flex-shrink-0" />
                          <span className="text-[11px] font-medium text-blue-300 whitespace-nowrap">
                            {linkedOpportunity.title || 'Opportunity'}
                          </span>
                        </button>
                      )}
                      <h2 
                        className="text-lg font-semibold text-white break-words"
                        style={{
                          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                          letterSpacing: '-0.01em',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto'
                        }}
                      >
                        {displayProject.title || 'Untitled Project'}
                      </h2>
                      <div className="flex items-center justify-center mt-0.5">
                        <div className="relative inline-block">
                          <button
                            ref={statusButtonRef}
                            type="button"
                            disabled={readOnly}
                            onClick={() => {
                              if (readOnly) return;
                              setIsProjectStatusDropdownOpen(prev => !prev);
                            }}
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                              readOnly
                                ? 'cursor-default opacity-70'
                                : 'cursor-pointer hover:bg-white/10'
                            }`}
                            style={{
                              borderColor: getStatusColor(displayProject.status),
                              color: '#E5E7EB',
                              backgroundColor: 'rgba(17,24,39,0.4)'
                            }}
                          >
                            <span
                              className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full mr-1.5 sm:mr-2 shadow-sm"
                              style={{ backgroundColor: getStatusColor(displayProject.status) }}
                            />
                            <span className="truncate capitalize">{displayProject.status}</span>
                          </button>
                        </div>
                      </div>

                      {/* Status Dropdown - Rendered via Portal */}
                      {isProjectStatusDropdownOpen && !readOnly && createPortal(
                        <>
                          {/* Backdrop */}
                          <div 
                            className="fixed inset-0 z-[99998]"
                            onClick={() => setIsProjectStatusDropdownOpen(false)}
                          />
                          {/* Dropdown */}
                          <div 
                            className="fixed z-[99999] w-56 rounded-md bg-white shadow-lg border border-gray-200 text-left"
                            style={{
                              top: statusButtonRef.current 
                                ? `${statusButtonRef.current.getBoundingClientRect().bottom + 4}px`
                                : '0',
                              left: statusButtonRef.current
                                ? `${statusButtonRef.current.getBoundingClientRect().right - 224}px`
                                : '0'
                            }}
                          >
                            {PROJECT_STATUSES.map(status => (
                              <button
                                key={status}
                                type="button"
                                onClick={async () => {
                                  setIsProjectStatusDropdownOpen(false);
                                  if (!displayProject?.id || !selectedBusiness?.id) return;
                                  try {
                                    const { error } = await supabase
                                      .from('projects')
                                      .update({ status })
                                      .eq('id', displayProject.id)
                                      .eq('business_id', selectedBusiness.id);
                                    if (error) {
                                      console.error('Error updating project status:', error);
                                      toast.error('Failed to update project status');
                                      return;
                                    }
                                    // Update local project state
                                    setLocalProject(prev =>
                                      prev ? { ...prev, status } : prev
                                    );
                                    onUpdate?.();
                                    toast.success(`Project status updated to "${status}"`);
                                  } catch (err) {
                                    console.error('Error updating project status:', err);
                                    toast.error('Failed to update project status');
                                  }
                                }}
                                className={`w-full px-3 py-1.5 text-xs text-left hover:bg-gray-50 ${
                                  displayProject.status === status
                                    ? 'font-semibold text-blue-600'
                                    : 'text-gray-700'
                                }`}
                              >
                                <span
                                  className="inline-block h-2 w-2 rounded-full mr-2 align-middle"
                                  style={{ backgroundColor: getStatusColor(status) }}
                                />
                                <span className="align-middle">{status}</span>
                              </button>
                            ))}
                          </div>
                        </>,
                        document.body
                      )}
                      
                      {/* Quick Actions - Below Status */}
                      {activeTab === 'overview' && (
                        <div className="flex items-center justify-center gap-2 mt-2">
                          {/* Schedule Button - Only show if calendar app is installed */}
                          {isCalendarAppInstalled && (
                            <button
                              type="button"
                              onClick={() => {
                                setIsScheduleModalOpen(true);
                              }}
                              disabled={readOnly}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 text-white text-xs font-medium rounded-lg hover:bg-white/20 transition-colors active:scale-95 touch-manipulation whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none border border-white/20"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <Calendar className="h-3 w-3" />
                              <span>Schedule</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right side - Menu button */}
                <div className="flex items-center flex-shrink-0">
                  {!readOnly && (
                    <div className="relative" ref={menuRef}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (navigator.vibrate) navigator.vibrate(10);
                          setIsMenuOpen(!isMenuOpen);
                        }}
                        className="inline-flex items-center justify-center w-8 h-8 text-white rounded-full transition-all duration-200 active:scale-95 touch-manipulation"
                        style={{
                          WebkitTapHighlightColor: 'transparent',
                          backgroundColor: 'transparent'
                        }}
                        title="More Options"
                        onMouseDown={(e) => {
                          if (navigator.vibrate) navigator.vibrate(10);
                        }}
                        onTouchStart={(e) => {
                          if (navigator.vibrate) navigator.vibrate(10);
                        }}
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>

                      {/* Menu Dropdown - Matching BookingInfoModal style */}
                      {isMenuOpen && createPortal(
                        <>
                          {/* Backdrop Overlay */}
                          <div
                            className="fixed inset-0"
                            style={{
                              zIndex: 100000,
                              background: 'rgba(0, 0, 0, 0.4)',
                              backdropFilter: 'blur(8px)',
                              WebkitBackdropFilter: 'blur(8px)',
                              opacity: 0,
                              animation: 'fadeIn 0.2s ease-out forwards',
                              cursor: 'pointer',
                            }}
                            onMouseDown={(e) => {
                              const menuElement = document.querySelector('[data-project-menu]');
                              const isClickOnMenu = menuElement?.contains(e.target as Node);
                              if (e.target === e.currentTarget && !isClickOnMenu) {
                                setIsMenuOpen(false);
                              }
                            }}
                            onClick={(e) => {
                              const menuElement = document.querySelector('[data-project-menu]');
                              const isClickOnMenu = menuElement?.contains(e.target as Node);
                              if (e.target === e.currentTarget && !isClickOnMenu) {
                                setIsMenuOpen(false);
                              }
                            }}
                          />

                          {/* Centered Menu - iOS Style */}
                          <div
                            data-project-menu
                            className="fixed bg-white rounded-[14px] shadow-lg"
                            style={{
                              zIndex: 100001,
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: 'calc(100% - 32px)',
                              maxWidth: '320px',
                              opacity: 0,
                              animation: 'fadeIn 0.2s ease-out 0.05s forwards',
                              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
                              pointerEvents: 'auto',
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <div className="py-1" role="menu" aria-orientation="vertical">
                              {/* Edit Project Button */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleEdit();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                className="w-full px-4 py-3.5 text-[17px] text-left text-gray-900 flex items-center transition-colors active:bg-gray-100 touch-manipulation"
                                role="menuitem"
                                style={{ WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto', zIndex: 100002 }}
                              >
                                <Pencil className="h-5 w-5 mr-3 text-gray-600" />
                                <span className="font-normal">Edit</span>
                              </button>

                              {/* Delete Button */}
                              <div className="h-[0.5px] bg-gray-200 mx-4 my-1"></div>
                              <div className="flex gap-2 px-4 pb-1">
                                <div className="flex-1 relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete();
                                    }}
                                    className="w-full px-4 py-3.5 text-[17px] text-left text-red-600 flex items-center transition-colors active:bg-red-50 touch-manipulation relative"
                                    role="menuitem"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    <Trash2 className="h-5 w-5 mr-3" />
                                    <span className="font-normal flex-1">Delete</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>,
                        document.body
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs - Matching BookingInfoModal style */}
            <div className="bg-white border-b border-gray-200">
              <div className="max-w-5xl mx-auto">
                <nav className="flex overflow-x-auto scrollbar-hide" aria-label="Tabs">
                  {[
                    { id: 'overview', label: 'Overview' },
                    { id: 'measurements', label: 'Measurements' },
                    { id: 'supplies', label: 'Expenses' }
                  ].map((tab) => {
                    // Get the label with count for measurements and supplies tabs
                    const getTabLabel = () => {
                      if (tab.id === 'measurements' && projectMeasurements.length > 0) {
                        return `Measurements (${projectMeasurements.length})`;
                      }
                      if (tab.id === 'supplies') {
                        // Calculate total unread count across all quote request groups
                        const quoteUnreadCount = quoteRequests.reduce((total, qr) => {
                          if (qr.quote_request_group_id) {
                            return total + getUnreadCountForGroup(qr.quote_request_group_id);
                          }
                          return total;
                        }, 0);
                        
                        // Also add unread counts from purchase orders
                        const orderUnreadCount = Object.values(orderUnreadCounts).reduce((sum, count) => sum + count, 0);
                        
                        const totalUnreadCount = quoteUnreadCount + orderUnreadCount;
                        
                        if (totalUnreadCount > 0) {
                          return (
                            <span className="flex items-center justify-center gap-2">
                              <span>{tab.label}</span>
                              <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                                {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                              </span>
                            </span>
                          );
                        }
                      }
                      return tab.label;
                    };

                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 min-w-[80px] py-3 sm:py-4 px-2 sm:px-4 border-b-2 text-xs sm:text-sm font-bold text-center whitespace-nowrap transition-colors ${
                          activeTab === tab.id
                            ? 'border-blue-600 text-blue-700 bg-blue-50'
                            : 'border-transparent text-gray-500 hover:text-blue-700 hover:border-blue-400 hover:bg-blue-50'
                        }`}
                        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                      >
                        {getTabLabel()}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>


            {/* Content Area - Based on active tab */}
            <div 
              className="flex-1 overflow-y-auto relative"
              style={{
                WebkitOverflowScrolling: 'touch',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                touchAction: 'pan-y pinch-zoom',
                overflowX: 'hidden',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(156, 163, 175, 0.3) transparent',
              }}
            >
              <div className="max-w-5xl mx-auto py-1 px-2 sm:py-6 sm:px-6">
                {activeTab === 'overview' && (
                  <div 
                    className="space-y-2"
                    style={{
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                    }}
                  >
                    {/* Customer Section */}
                    {isLoadingCustomer ? (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <div className="p-2">
                          <div className="text-sm text-gray-500">Loading customer...</div>
                        </div>
                      </div>
                    ) : customer ? (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]" style={{ overflow: 'visible' }}>
                        <div className="p-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                          <BookingCustomerInfo
                            customer={customer}
                            onEdit={!readOnly ? handleEditCustomer : undefined}
                            mobile_booking={false}
                            canEdit={!readOnly}
                            businessId={selectedBusiness?.id}
                          />
                            </div>
                            {!readOnly && (
                              <button
                                type="button"
                                onClick={handleRemoveCustomer}
                                className="flex-shrink-0 p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-150 active:scale-95 touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                                title="Remove customer"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          
                          {/* Customer Source Dropdown */}
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <div style={{ position: 'relative', zIndex: 100 }}>
                              <button
                                ref={customerSourceButtonRef}
                                data-customer-source-button
                                type="button"
                                onClick={() => {
                                  console.log('📍 Main button clicked, readOnly:', readOnly, 'current state:', isCustomerSourceDropdownOpen);
                                  if (!readOnly) {
                                    setIsCustomerSourceDropdownOpen(!isCustomerSourceDropdownOpen);
                                  }
                                }}
                                disabled={readOnly}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                                  readOnly 
                                    ? 'opacity-50 cursor-not-allowed bg-gray-50' 
                                    : selectedCustomerSource 
                                      ? 'bg-gray-50 hover:bg-gray-100 cursor-pointer' 
                                      : 'bg-red-50 hover:bg-red-100 border border-red-300 cursor-pointer'
                                }`}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <div className="flex items-center gap-2">
                                  <span className={`text-[12px] ${selectedCustomerSource ? 'text-gray-500' : 'text-red-500 font-medium'}`}>Source:</span>
                                  <span className={`text-[13px] font-medium ${selectedCustomerSource ? 'text-gray-900' : 'text-red-600'}`}>
                                    {selectedCustomerSource?.name || 'Select source'}
                                  </span>
                                </div>
                                {!readOnly && (
                                  <ChevronDown className={`h-4 w-4 transition-transform ${selectedCustomerSource ? 'text-gray-400' : 'text-red-400'} ${isCustomerSourceDropdownOpen ? 'rotate-180' : ''}`} />
                                )}
                              </button>

                              {/* Dropdown Menu */}
                              {isCustomerSourceDropdownOpen && (
                                <div 
                                  ref={customerSourceDropdownRef}
                                  data-customer-source-dropdown
                                  className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200"
                                  style={{ 
                                    zIndex: 99999,
                                    maxHeight: '200px', 
                                    overflowY: 'auto'
                                  }}
                                  onClick={() => console.log('📦 Dropdown container clicked')}
                                >
                                  {customerSources.length > 0 ? (
                                    <div className="py-1">
                                      {customerSources.map(source => (
                                        <div
                                          key={source.id}
                                          data-customer-source-dropdown
                                          onClick={() => {
                                            handleSelectCustomerSource(source);
                                          }}
                                          className={`w-full px-3 py-2 text-left text-[13px] hover:bg-gray-50 transition-colors cursor-pointer ${
                                            selectedCustomerSource?.id === source.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                                          }`}
                                          style={{ pointerEvents: 'auto' }}
                                        >
                                          {source.name}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="px-3 py-2 text-[12px] text-gray-500">
                                      No sources available
                                    </div>
                                  )}
                                  
                                  {/* Add New Source */}
                                  <div className="border-t border-gray-100">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setIsCustomerSourceDropdownOpen(false);
                                        setIsCreateSourceModalOpen(true);
                                      }}
                                      className="w-full px-3 py-2 text-left text-[13px] text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-1"
                                    >
                                      <Plus className="h-3.5 w-3.5" />
                                      Add New Source
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <div className="p-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (!readOnly) {
                                setIsSelectCustomerModalOpen(true);
                              }
                            }}
                            disabled={readOnly}
                            className="w-full px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <div className="flex items-center justify-center">
                              <Plus className="h-4 w-4 mr-2" />
                              Add Customer
                            </div>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Proposal Section - Conditionally Rendered - Only show if proposal_id exists */}
                    {displayProject?.proposal_id && displayProject.proposal_id.trim() !== '' && (
                      <>
                        {isLoadingProposal ? (
                          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                            <div className="p-2">
                              <div className="text-sm text-gray-500">Loading proposal...</div>
                            </div>
                          </div>
                        ) : proposal ? (
                          (() => {
                            const getDisplayStatus = () => {
                              if (proposal.status === 'approved' || proposal.status === 'booked') return proposal.status;
                              if (proposal.valid_until && new Date(proposal.valid_until) < new Date()) return 'expired';
                              return proposal.status;
                            };
                            const displayStatus = getDisplayStatus();
                            const isExpired = displayStatus === 'expired';

                            const calcProposalDisplayTotal = (): number => {
                              if (proposal.status === 'approved' || proposal.status === 'booked') {
                                const approvedOffer = (proposal as any).offers?.find((o: any) => o.status === 'approved');
                                if (approvedOffer) {
                                  const svcs = (approvedOffer.services || []).filter((s: any) => {
                                    const sel = s.isSelectable || s.is_selectable;
                                    return !sel || (sel && s.is_selected);
                                  });
                                  const prods = (approvedOffer.products || []).filter((p: any) => {
                                    const sel = p.isSelectable || p.is_selectable;
                                    return !sel || (sel && p.is_selected);
                                  });
                                  const sub = svcs.reduce((s: number, x: any) => s + (x.price || 0), 0)
                                    + prods.reduce((s: number, x: any) => s + ((x.price || 0) * (x.quantity || 1)), 0);
                                  const disc = svcs.reduce((s: number, x: any) => s + (x.discount_amount || 0), 0)
                                    + prods.reduce((s: number, x: any) => s + (x.discount_amount || 0), 0);
                                  const discSub = sub - disc;
                                  const tax = svcs.reduce((s: number, x: any) => s + (x.tax_exempt ? 0 : ((x.price || 0) - (x.discount_amount || 0)) * ((x.tax_rate || 0) / 100)), 0)
                                    + prods.reduce((s: number, x: any) => s + (x.tax_exempt ? 0 : (((x.price || 0) * (x.quantity || 1)) - (x.discount_amount || 0)) * ((x.tax_rate || 0) / 100)), 0);
                                  return Math.round((discSub + tax) * 100) / 100;
                                }
                              }
                              const offers = (proposal as any).offers;
                              if (offers && offers.length > 0) {
                                return Math.max(...offers.map((offer: any) => {
                                  const svcs = offer.services || [];
                                  const prods = offer.products || [];
                                  const sub = svcs.reduce((s: number, x: any) => s + (x.price || 0), 0)
                                    + prods.reduce((s: number, x: any) => s + ((x.price || 0) * (x.quantity || 1)), 0);
                                  const disc = svcs.reduce((s: number, x: any) => s + (x.discount_amount || 0), 0)
                                    + prods.reduce((s: number, x: any) => s + (x.discount_amount || 0), 0);
                                  const discSub = sub - disc;
                                  const tax = svcs.reduce((s: number, x: any) => s + (x.tax_exempt ? 0 : ((x.price || 0) - (x.discount_amount || 0)) * ((x.tax_rate || 0) / 100)), 0)
                                    + prods.reduce((s: number, x: any) => s + (x.tax_exempt ? 0 : (((x.price || 0) * (x.quantity || 1)) - (x.discount_amount || 0)) * ((x.tax_rate || 0) / 100)), 0);
                                  return Math.round((discSub + tax) * 100) / 100;
                                }));
                              }
                              return proposal.total_amount || 0;
                            };
                            const proposalDisplayTotal = calcProposalDisplayTotal();
                            const isMaxPotential = (proposal.status !== 'approved' && proposal.status !== 'booked') && ((proposal as any).offers?.length || 0) > 1;

                            const getStatusBadgeColor = (status: string) => {
                              switch (status) {
                                case 'approved': return 'bg-green-50 text-green-700';
                                case 'sent': return 'bg-blue-50 text-blue-700';
                                case 'rejected': case 'declined': return 'bg-red-50 text-red-700';
                                case 'expired': return 'bg-gray-100 text-gray-600';
                                case 'draft': return 'bg-gray-100 text-gray-600';
                                default: return 'bg-gray-100 text-gray-600';
                              }
                            };

                            const formatDate = (dateString: string) => {
                              return new Date(dateString).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric'
                              });
                            };

                            if (displayStatus === 'booked') {
                              return (
                                <button
                                  type="button"
                                  onClick={() => setIsProposalDetailsModalOpen(true)}
                                  className="w-full bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] px-4 py-2.5 flex items-center gap-3 active:bg-gray-50 transition-colors touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-3.5 h-3.5 text-green-600" />
                                  </div>
                                  <div className="flex-1 min-w-0 text-left">
                                    <p className="text-xs font-semibold text-gray-900 truncate">{proposal.title}</p>
                                  </div>
                                  <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-50 text-green-700 flex-shrink-0">Booked</span>
                                  <span className="text-xs font-bold text-gray-900 tabular-nums flex-shrink-0">${proposalDisplayTotal.toLocaleString()}</span>
                                  <ChevronDown className="w-3.5 h-3.5 text-gray-300 -rotate-90 flex-shrink-0" />
                                </button>
                              );
                            }

                            return (
                              <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                                <div className="p-2">
                                  <div className={`flex flex-col transition-all duration-150 ${isExpired ? 'opacity-60' : ''}`}
                                    style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif', WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    <div className="flex-1">
                                      <button
                                        type="button"
                                        onClick={() => setIsProposalDetailsModalOpen(true)}
                                        className="w-full active:scale-[0.98] transition-transform duration-150 cursor-pointer"
                                      >
                                        <div className="px-3 pt-3 pb-2 flex items-center justify-between">
                                          <div className="flex items-center space-x-2.5 flex-1 min-w-0">
                                            <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-semibold text-white flex-shrink-0">
                                              {proposal.customer?.first_name?.[0] || ''}{proposal.customer?.last_name?.[0] || ''}
                                            </div>
                                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                                              <h3 className="text-xs font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                                                {proposal.customer?.first_name} {proposal.customer?.last_name}
                                              </h3>
                                              <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-[4px] flex-shrink-0 ${getStatusBadgeColor(displayStatus)}`} style={{ letterSpacing: '-0.01em' }}>
                                                {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                                              </span>
                                            </div>
                                          </div>
                                          <div className="text-right flex-shrink-0 ml-3">
                                            <p className="text-[10px] text-gray-500 mb-0.5" style={{ letterSpacing: '-0.01em' }}>
                                              {isMaxPotential ? 'Max Potential' : 'Total'}
                                            </p>
                                            <p className="text-sm font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                                              ${proposalDisplayTotal.toLocaleString()}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="px-3 pb-2">
                                          <h2 className="text-xs font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                                            {proposal.title}
                                          </h2>
                                        </div>
                                        <div className="px-3 pb-3 flex items-center justify-between border-t border-gray-100 pt-2">
                                          {proposal.valid_until && (
                                            <div>
                                              <p className="text-[10px] text-gray-500 mb-0.5" style={{ letterSpacing: '-0.01em' }}>Valid until</p>
                                              <p className="text-[11px] font-medium text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                                {formatDate(proposal.valid_until)}
                                              </p>
                                            </div>
                                          )}
                                          <div className={`text-[10px] text-blue-600 font-semibold ${proposal.valid_until ? 'ml-auto' : ''}`}>
                                            View full proposal →
                                          </div>
                                        </div>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                            <div className="p-2">
                              <div className="text-sm text-gray-500">Unable to load proposal</div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Field Quote Section - show latest scheduled field quote for this project */}
                    {isLoadingFieldQuote ? (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)] mt-3">
                        <div className="p-2">
                          <div className="text-sm text-gray-500">Loading field quote...</div>
                        </div>
                      </div>
                    ) : fieldQuote ? (
                      (() => {
                        const rawStatus = (fieldQuote.status || 'scheduled').toLowerCase();
                        const isCompleted = rawStatus === 'completed';

                        // If completed and collapsed, show a minimal row similar to the booked proposal
                        if (isCompleted && isFieldQuoteCollapsed) {
                          return (
                            <button
                              type="button"
                              onClick={() => setIsFieldQuoteCollapsed(false)}
                              className="w-full mt-3 flex items-center justify-between px-4 py-3 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-transform duration-150 cursor-pointer"
                              style={{
                                fontFamily:
                                  '-apple-system, BlinkMacSystemFont, \"SF Pro Display\", \"SF Pro Text\", system-ui, sans-serif',
                                WebkitTapHighlightColor: 'transparent'
                              }}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="h-2 w-2 rounded-full bg-green-500" />
                                <span className="text-sm font-semibold text-gray-900">
                                  Field Quote
                                </span>
                                <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-green-50 text-green-700">
                                  Completed
                                </span>
                              </div>
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-gray-500 text-xs">
                                ▾
                              </span>
                            </button>
                          );
                        }

                        // Default: full field quote card
                        return (
                          <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] mt-3 overflow-visible">
                            <div
                              className="flex items-stretch cursor-default"
                              style={{
                                fontFamily:
                                  '-apple-system, BlinkMacSystemFont, \"SF Pro Display\", \"SF Pro Text\", system-ui, sans-serif',
                                WebkitTapHighlightColor: 'transparent'
                              }}
                            >
                              {/* Status Bar - orange (scheduled), yellow (in progress), green (completed) */}
                              <div
                                className="w-1 rounded-l-[12px]"
                                style={{
                                  backgroundColor:
                                    rawStatus === 'completed'
                                      ? '#22c55e'
                                      : rawStatus === 'in progress'
                                      ? '#eab308'
                                      : '#f97316'
                                }}
                              />

                              <div className="flex-1 p-2 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                                {/* Left: Title, status, address */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start space-x-2 sm:space-x-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-1 sm:mb-0">
                                        <div className="text-xs sm:text-sm font-semibold text-gray-900">
                                          Field Quote
                                        </div>
                                        {isCompleted && (
                                          <button
                                            type="button"
                                            onClick={() => setIsFieldQuoteCollapsed(true)}
                                            className="inline-flex h-4 w-4 sm:h-6 sm:w-6 items-center justify-center rounded-full border border-gray-300 text-gray-500 text-[10px] sm:text-xs hover:bg-gray-50 flex-shrink-0"
                                          >
                                            ▴
                                          </button>
                                        )}
                                      </div>
                                      {(() => {
                                        const displayStatus =
                                          rawStatus === 'in progress'
                                            ? 'In Progress'
                                            : rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);

                                        let badgeClasses =
                                          'mt-0.5 inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full text-[9px] sm:text-[11px] font-semibold cursor-pointer select-none ';
                                        if (rawStatus === 'completed') {
                                          // green
                                          badgeClasses += 'bg-green-50 text-green-700 hover:bg-green-100';
                                        } else if (rawStatus === 'in progress') {
                                          // yellow
                                          badgeClasses += 'bg-amber-50 text-amber-700 hover:bg-amber-100';
                                        } else {
                                          // scheduled / fallback - orange
                                          badgeClasses += 'bg-orange-50 text-orange-700 hover:bg-orange-100';
                                        }

                                      const statuses = ['scheduled', 'in progress', 'completed'] as const;

                                      return (
                                        <div className="relative inline-block">
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setIsFieldQuoteStatusDropdownOpen(prev => !prev)
                                            }
                                            className={badgeClasses}
                                          >
                                            {displayStatus}
                                          </button>

                                          {isFieldQuoteStatusDropdownOpen && (
                                            <div className="absolute z-20 mt-1 w-40 rounded-md bg-white shadow-lg border border-gray-200">
                                              {statuses.map(status => {
                                                const label =
                                                  status === 'in progress'
                                                    ? 'In Progress'
                                                    : status.charAt(0).toUpperCase() +
                                                      status.slice(1);
                                                return (
                                                  <button
                                                    key={status}
                                                    type="button"
                                                    onClick={() => {
                                                      setIsFieldQuoteStatusDropdownOpen(false);
                                                      handleFieldQuoteStatusChange(status);
                                                    }}
                                                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
                                                      rawStatus === status
                                                        ? 'font-semibold text-blue-600'
                                                        : 'text-gray-700'
                                                    }`}
                                                  >
                                                    {label}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                      })()}
                                    </div>
                                  </div>

                                  {fieldQuote.field_quote_address && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Reuse the same Google Maps modal pattern as MobileCalendar day view
                                        const encodedAddress = encodeURIComponent(
                                          fieldQuote.field_quote_address || ''
                                        );
                                        const mapsUrl = `https://maps.google.com/?q=${encodedAddress}`;
                                        window.open(mapsUrl, '_blank');
                                      }}
                                      className="mt-1 sm:mt-3 flex items-center space-x-1 sm:space-x-2 text-[9px] sm:text-xs text-gray-600 hover:text-blue-600"
                                    >
                                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                                      <span className="truncate text-left">
                                        {fieldQuote.field_quote_address}
                                      </span>
                                    </button>
                                  )}
                                  {fieldQuote.resource && (
                                    <div className="mt-1 sm:mt-2 flex items-center space-x-1 sm:space-x-2 text-[9px] sm:text-xs text-gray-600">
                                      <div className="h-4 w-4 sm:h-6 sm:w-6 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                                        {fieldQuote.resource.avatar_url ? (
                                          <img
                                            src={fieldQuote.resource.avatar_url}
                                            alt={`${fieldQuote.resource.first_name} ${fieldQuote.resource.last_name}`}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <div className="h-4 w-4 sm:h-6 sm:w-6 rounded-full bg-blue-500 flex items-center justify-center">
                                            <span className="text-[8px] sm:text-[10px] font-semibold text-white">
                                              {fieldQuote.resource.first_name?.[0]}
                                              {fieldQuote.resource.last_name?.[0]}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      <span className="truncate">
                                        {fieldQuote.resource.first_name} {fieldQuote.resource.last_name}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Right: Date/Time and action buttons */}
                                <div className="flex flex-col sm:items-end space-y-1 sm:space-y-1 text-left sm:text-right flex-shrink-0 mt-1 sm:mt-0">
                                  <div className="flex items-center space-x-1 sm:space-x-2 mb-0 sm:mb-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setFieldQuoteForSchedule(fieldQuote);
                                        setIsFieldQuoteMeasurementMode(!!fieldQuote.is_measurement);
                                        setIsScheduleFieldQuoteModalOpen(true);
                                      }}
                                      className="text-[9px] sm:text-[11px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full transition-colors"
                                    >
                                      Reschedule
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleDeleteFieldQuote}
                                      className="text-[9px] sm:text-[11px] font-semibold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                  {(fieldQuote.start_date || fieldQuote.start_time) && (
                                    <div>
                                      <div className="text-[9px] sm:text-xs text-gray-500">Scheduled For</div>
                                      <div className="text-[10px] sm:text-sm font-medium text-gray-900">
                                        {formatFieldQuoteDateTime(
                                          fieldQuote.start_date,
                                          fieldQuote.start_time
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()
                    ) : null}

                    {/* Measurement Field Quote Section - show latest measurement visit for this project */}
                    {isLoadingMeasurementFieldQuote ? (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)] mt-3">
                        <div className="p-2">
                          <div className="text-sm text-gray-500">Loading field measurements...</div>
                        </div>
                      </div>
                    ) : measurementFieldQuote ? (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] mt-3 overflow-visible">
                        <div
                          className="flex items-stretch cursor-default"
                          style={{
                            fontFamily:
                              '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                            WebkitTapHighlightColor: 'transparent'
                          }}
                        >
                          {/* Status Bar - purple (scheduled), yellow (in progress), green (completed) */}
                          <div
                            className="w-1 rounded-l-[12px]"
                            style={{
                              backgroundColor: (() => {
                                const status = (measurementFieldQuote.status || 'scheduled').toLowerCase();
                                if (status === 'completed') return '#22c55e';
                                if (status === 'in progress') return '#eab308';
                                return '#8b5cf6';
                              })()
                            }}
                          />

                          <div className="flex-1 p-2 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                            {/* Left: Title, status, address */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start space-x-2 sm:space-x-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1 sm:mb-0">
                                    <div className="text-xs sm:text-sm font-semibold text-gray-900">
                                      Field Measurements
                                    </div>
                                  </div>
                                  {(() => {
                                    const rawStatus = (
                                      measurementFieldQuote.status || 'scheduled'
                                    ).toLowerCase();
                                    const displayStatus =
                                      rawStatus === 'in progress'
                                        ? 'In Progress'
                                        : rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);

                                    let badgeClasses =
                                      'mt-0.5 inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full text-[9px] sm:text-[11px] font-semibold cursor-pointer select-none ';
                                    if (rawStatus === 'completed') {
                                      // green
                                      badgeClasses += 'bg-green-50 text-green-700';
                                    } else if (rawStatus === 'in progress') {
                                      // yellow
                                      badgeClasses += 'bg-amber-50 text-amber-700';
                                    } else {
                                      // scheduled / fallback - purple
                                      badgeClasses += 'bg-purple-50 text-purple-700';
                                    }

                                    const statuses = ['scheduled', 'in progress', 'completed'] as const;

                                    return (
                                      <div className="relative inline-block">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setIsMeasurementStatusDropdownOpen(prev => !prev)
                                          }
                                          className={badgeClasses}
                                        >
                                          {displayStatus}
                                        </button>

                                        {isMeasurementStatusDropdownOpen && (
                                          <div className="absolute z-20 mt-1 w-40 rounded-md bg-white shadow-lg border border-gray-200">
                                            {statuses.map(status => {
                                              const label =
                                                status === 'in progress'
                                                  ? 'In Progress'
                                                  : status.charAt(0).toUpperCase() +
                                                    status.slice(1);
                                              return (
                                                <button
                                                  key={status}
                                                  type="button"
                                                  onClick={() => {
                                                    setIsMeasurementStatusDropdownOpen(false);
                                                    handleMeasurementFieldQuoteStatusChange(status);
                                                  }}
                                                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
                                                    rawStatus === status
                                                      ? 'font-semibold text-blue-600'
                                                      : 'text-gray-700'
                                                  }`}
                                                >
                                                  {label}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>

                              {measurementFieldQuote.field_quote_address && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const encodedAddress = encodeURIComponent(
                                      measurementFieldQuote.field_quote_address || ''
                                    );
                                    const mapsUrl = `https://maps.google.com/?q=${encodedAddress}`;
                                    window.open(mapsUrl, '_blank');
                                  }}
                                  className="mt-1 sm:mt-3 flex items-center space-x-1 sm:space-x-2 text-[9px] sm:text-xs text-gray-600 hover:text-blue-600"
                                >
                                  <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                                  <span className="truncate text-left">
                                    {measurementFieldQuote.field_quote_address}
                                  </span>
                                </button>
                              )}
                              {measurementFieldQuote.resource && (
                                <div className="mt-1 sm:mt-2 flex items-center space-x-1 sm:space-x-2 text-[9px] sm:text-xs text-gray-600">
                                  <div className="h-4 w-4 sm:h-6 sm:w-6 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                                    {measurementFieldQuote.resource.avatar_url ? (
                                      <img
                                        src={measurementFieldQuote.resource.avatar_url}
                                        alt={`${measurementFieldQuote.resource.first_name} ${measurementFieldQuote.resource.last_name}`}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="h-4 w-4 sm:h-6 sm:w-6 rounded-full bg-blue-500 flex items-center justify-center">
                                        <span className="text-[8px] sm:text-[10px] font-semibold text-white">
                                          {measurementFieldQuote.resource.first_name?.[0]}
                                          {measurementFieldQuote.resource.last_name?.[0]}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <span className="truncate">
                                    {measurementFieldQuote.resource.first_name}{' '}
                                    {measurementFieldQuote.resource.last_name}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Right: Date/Time and action buttons */}
                            <div className="flex flex-col sm:items-end space-y-1 sm:space-y-1 text-left sm:text-right flex-shrink-0 mt-1 sm:mt-0">
                              <div className="flex items-center space-x-1 sm:space-x-2 mb-0 sm:mb-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFieldQuoteForSchedule(measurementFieldQuote);
                                    setIsFieldQuoteMeasurementMode(true);
                                    setIsScheduleFieldQuoteModalOpen(true);
                                  }}
                                  className="text-[9px] sm:text-[11px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full transition-colors"
                                >
                                  Reschedule
                                </button>
                                <button
                                  type="button"
                                  onClick={handleDeleteMeasurementFieldQuote}
                                  className="text-[9px] sm:text-[11px] font-semibold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full"
                                >
                                  Delete
                                </button>
                              </div>
                              {(measurementFieldQuote.start_date || measurementFieldQuote.start_time) && (
                                <div>
                                  <div className="text-[9px] sm:text-xs text-gray-500">Scheduled For</div>
                                  <div className="text-[10px] sm:text-sm font-medium text-gray-900">
                                    {formatFieldQuoteDateTime(
                                      measurementFieldQuote.start_date,
                                      measurementFieldQuote.start_time
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* Service Section - Hide if project is linked to booking */}
                    {!displayProject.booking_id && (
                      <>
                        {/* Add Proposal Button - Show if no proposal linked */}
                        {!readOnly && isProposalsAppInstalled && !displayProject?.proposal_id && (
                          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-2">
                            <button
                              type="button"
                              onClick={() => setIsCreateProposalModalOpen(true)}
                              className="w-full flex items-center justify-center px-3 py-2 border-2 border-dashed border-gray-300 rounded-[10px] text-[13px] font-semibold text-gray-700 hover:text-gray-900 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <Plus className="h-4 w-4 mr-1.5" />
                              Proposal
                            </button>
                          </div>
                        )}

                        {isLoadingServices || projectServices.length > 0 ? (
                        <>
                          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                            <div className="px-3 py-2 border-b border-gray-200/60">
                              <div className="text-[13px] font-semibold text-gray-900">Services</div>
                            </div>
                            
                            {/* Services List - Grouped by Vehicle (like BookingServiceList) */}
                            {isLoadingServices ? (
                              <div className="px-3 py-4 text-center text-sm text-gray-500">
                                Loading services...
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {(() => {
                                  // Group services by vehicle (or "no vehicle" for Home Services)
                                  const servicesByVehicle = projectServices.reduce<Record<string, ProjectService[]>>((acc, service) => {
                                    const vehicleId = service.vehicle_id || 'no-vehicle';
                                    if (!acc[vehicleId]) {
                                      acc[vehicleId] = [];
                                    }
                                    acc[vehicleId].push(service);
                                    return acc;
                                  }, {});

                                  return Object.entries(servicesByVehicle).map(([vehicleId, vehicleServices]) => {
                                    const firstService = vehicleServices[0];
                                    const vehicle = firstService.vehicle;
                                    const isHomeServices = selectedBusiness?.industry === 'Home Services';
                                    
                                    return (
                                      <div key={vehicleId} className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                                        {/* Vehicle Header - Only show for Automotive */}
                                        {!isHomeServices && vehicle && (
                                          <div className="bg-gray-50/50 px-3 py-2.5 border-b border-gray-200/60">
                                            <div className="text-[13px] font-semibold text-gray-900 truncate">
                                              {vehicle.year} {vehicle.make} {vehicle.model}
                                            </div>
                                            {vehicle.color && (
                                              <div className="text-[11px] text-gray-600 mt-0.5">{vehicle.color}</div>
                                            )}
                                          </div>
                                        )}

                                        {/* Services List */}
                                        <div className="divide-y divide-gray-200/60">
                                          {vehicleServices.map((projectService) => (
                                            <ProjectServiceItem
                                              key={projectService.id}
                                              service={projectService}
                                              onUpdate={async () => {
                                                if (displayProject?.id) {
                                                  const services = await getProjectServices(displayProject.id);
                                                  setProjectServices(services);
                                                  if (onUpdate) {
                                                    onUpdate();
                                                  }
                                                }
                                              }}
                                              onError={(error) => {
                                                toast.error(error);
                                              }}
                                              canEdit={!readOnly}
                                              displayProject={displayProject}
                                              onDelete={(service) => {
                                                setServiceToDelete(service);
                                                setIsDeleteServiceModalOpen(true);
                                              }}
                                            />
                                          ))}
                                        </div>
                                        
                                        {/* Add Service Button */}
                                        {!readOnly && (
                                          <div className="px-3 py-2 border-t border-gray-200/60">
                                            <button
                                              onClick={() => {
                                                if (isHomeServices) {
                                                  setCurrentVehicle(null);
                                                  setIsSelectServiceModalOpen(true);
                                                } else if (vehicle) {
                                                  setCurrentVehicle({
                                                    id: vehicle.id,
                                                    make: vehicle.make,
                                                    model: vehicle.model,
                                                    year: vehicle.year,
                                                    color: vehicle.color || '',
                                                    customer_id: customer?.id || '',
                                                    vin: '',
                                                    license_plate: '',
                                                    mileage: 0,
                                                    notes: '',
                                                    is_primary: false,
                                                    last_service: null,
                                                    created_at: new Date().toISOString(),
                                                    updated_at: new Date().toISOString(),
                                                    deleted_at: null
                                                  });
                                                  setIsSelectServiceModalOpen(true);
                                                } else {
                                                  // No vehicle - should not happen for Automotive, but handle gracefully
                                                  if (customer && customer.vehicles && customer.vehicles.length > 0) {
                                                    setIsSelectVehicleModalOpen(true);
                                                  } else if (customer) {
                                                    setIsCreateVehicleModalOpen(true);
                                                  } else {
                                                    toast.error('Please add a customer first before adding services');
                                                  }
                                                }
                                              }}
                                              className="w-full flex items-center justify-center px-3 py-2 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-[10px] text-[13px] font-medium text-gray-700 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                              style={{ WebkitTapHighlightColor: 'transparent' }}
                                            >
                                              <Plus className="h-4 w-4 mr-1.5" />
                                              Add Service
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            )}
                          </div>
                          
                          {/* Add Vehicle Button - Only for Automotive */}
                          {!readOnly && selectedBusiness?.industry === 'Automotive' && (
                            <div className="p-2 border-t border-gray-200/60">
                              <button
                                onClick={() => {
                                  if (customer && customer.vehicles && customer.vehicles.length > 0) {
                                    setIsSelectVehicleModalOpen(true);
                                  } else if (customer) {
                                    setIsCreateVehicleModalOpen(true);
                                  } else {
                                    toast.error('Please add a customer first before adding services');
                                  }
                                }}
                                className="w-full flex items-center justify-center px-3 py-2.5 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-[10px] text-[13px] font-medium text-gray-700 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Plus className="h-4 w-4 mr-1.5" />
                                Add Vehicle
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        !readOnly && (
                          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-2">
                            <button
                              type="button"
                              onClick={handleAddServiceForEmptyProject}
                              className="w-full flex items-center justify-center px-3 py-2 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-[10px] text-[13px] font-medium text-gray-700 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <Plus className="h-4 w-4 mr-1.5" />
                              Add Service
                            </button>
                          </div>
                        )
                      )}
                      </>
                    )}

                    {/* Linked Booking Info - Show when project is linked to booking */}
                    {displayProject.booking_id && linkedBooking && (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <h3 className="text-sm font-semibold text-gray-700">Linked Booking</h3>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {linkedBooking.title}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Booking #{linkedBooking.booking_number}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                // Close this modal first
                                onClose();
                                // Navigate to CalendarV2 and open the booking modal
                                navigate('/calendarV2', { 
                                  state: { selectedBookingId: linkedBooking.id } 
                                });
                              }}
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                            >
                              View Booking
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-3">
                            This project has been converted to a booking. All future payments and services should be managed through the booking.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Payment Summary Section - iOS Style - Hide if project is linked to booking */}
                    {!displayProject.booking_id && (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)] mt-3">
                      <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-gray-700">Payment Summary</h3>
                        <button
                          type="button"
                          onClick={() => {}}
                          className="text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                        >
                          Apply Discount
                        </button>
                      </div>
                      <div className="p-4">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Subtotal</span>
                            <span>${(Math.round((paymentSummary.subtotal || 0) * 100) / 100).toLocaleString()}</span>
                          </div>
                          {paymentSummary.totalDiscounts > 0 && (
                            <div className="flex justify-between text-sm text-gray-600">
                              <span className="text-red-600">Discount</span>
                              <span className="text-red-600">-${(Math.round(paymentSummary.totalDiscounts * 100) / 100).toLocaleString()}</span>
                            </div>
                          )}
                          {additionalTaxLines.length > 0 ? (
                            <>
                              <div className="flex justify-between text-sm text-gray-600">
                                <span>{paymentSummary.taxLabel}</span>
                                <span>${(Math.round(((paymentSummary.taxAmount || 0) - additionalTaxLines.reduce((s, l) => s + l.amount, 0)) * 100) / 100).toLocaleString()}</span>
                              </div>
                              {additionalTaxLines.map((line, i) => (
                                <div key={i} className="flex justify-between text-sm text-gray-600">
                                  <span>{line.name} ({line.rate}%)</span>
                                  <span>${line.amount.toLocaleString()}</span>
                                </div>
                              ))}
                            </>
                          ) : (
                            <div className="flex justify-between text-sm text-gray-600">
                              <span>{paymentSummary.taxLabel}</span>
                              <span>${(Math.round((paymentSummary.taxAmount || 0) * 100) / 100).toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm font-semibold text-gray-900 pt-2 border-t border-gray-200">
                            <span>Total</span>
                            <span>${(Math.round((paymentSummary.totalAmount || 0) * 100) / 100).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm text-green-600">
                            <span>Amount Paid</span>
                            <span>${(Math.round((paymentSummary.totalPaid || 0) * 100) / 100).toLocaleString()}</span>
                          </div>
                          {paymentSummary.totalTips > 0 && (
                            <div className="flex justify-between text-sm text-green-600">
                              <span>Tips Received</span>
                              <span>${(Math.round((paymentSummary.totalTips || 0) * 100) / 100).toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm font-medium text-gray-900 pt-2 border-t border-gray-200">
                            <span>Total Received</span>
                            <span>${(Math.round((paymentSummary.totalReceived || 0) * 100) / 100).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm font-medium text-gray-900">
                            <span>Balance Due</span>
                            <span>${(Math.round((paymentSummary.balanceDue || 0) * 100) / 100).toLocaleString()}</span>
                          </div>
                          <div className="pt-3 flex flex-col gap-2" style={{
                            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                            marginBottom: 'env(safe-area-inset-bottom, 0px)'
                          }}>
                            {!displayProject.booking_id && (
                              <button
                                type="button"
                                onClick={handleConvertToBooking}
                                className="w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                              >
                                Convert to Booking
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Payment History - Hide if project is linked to booking */}
                    {!displayProject.booking_id && projectPayments.length > 0 && (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)] mt-3">
                        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                          <h3 className="text-sm font-semibold text-gray-700">Payment History</h3>
                        </div>
                        <div className="divide-y divide-gray-200">
                          {projectPayments.map((payment) => {
                            const isDeposit = payment.payment_type === 'deposit';
                            const isCompleted = payment.status === 'completed';
                            const isPending = payment.status === 'pending';
                            
                            let statusConfig = {
                              color: 'text-gray-600',
                              bgColor: 'bg-gray-50',
                              borderColor: 'border-gray-200',
                              label: payment.status || 'Unknown'
                            };
                            
                            if (isCompleted) {
                              statusConfig = {
                                color: 'text-green-600',
                                bgColor: 'bg-green-50',
                                borderColor: 'border-green-200',
                                label: 'Completed'
                              };
                            } else if (isPending) {
                              statusConfig = {
                                color: 'text-yellow-600',
                                bgColor: 'bg-yellow-50',
                                borderColor: 'border-yellow-200',
                                label: payment.stripe_checkout_session_id ? 'Pending Stripe Pay' : 'Pending'
                              };
                            } else if (payment.status === 'failed') {
                              statusConfig = {
                                color: 'text-red-600',
                                bgColor: 'bg-red-50',
                                borderColor: 'border-red-200',
                                label: 'Failed'
                              };
                            }
                            
                            return (
                              <div key={payment.id} className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`text-sm font-medium ${statusConfig.color}`}>
                                        {statusConfig.label}
                                      </span>
                                      {isDeposit && (
                                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                          Deposit
                                        </span>
                                      )}
                                      {payment.method && (
                                        <span className="text-xs text-gray-500">
                                          • {payment.method.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                        </span>
                                      )}
                                    </div>
                                    {payment.notes && (
                                      <p className="text-xs text-gray-500 mt-1">{payment.notes}</p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-1">
                                      {new Date(payment.created_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <div className={`text-base font-semibold ${isCompleted ? 'text-gray-900' : statusConfig.color}`}>
                                      ${(payment.amount || 0).toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {paymentSummary.totalPaid > 0 && (
                          <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">Total Paid</span>
                              <span className="text-base font-bold text-gray-900">${paymentSummary.totalPaid.toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                )}

                {activeTab === 'measurements' && (
                  <div 
                    className="space-y-2"
                    style={{
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                    }}
                  >
                    {/* Project Photos Section */}
                    <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="px-3 py-2 border-b border-gray-200/60">
                        <div className="text-[13px] font-semibold text-gray-900">Project Photos</div>
                      </div>
                      <div className="p-3">
                        {displayProject?.id && (
                          <ProjectPhotos projectId={displayProject.id} readOnly={readOnly} />
                        )}
                      </div>
                    </div>

                    {/* Measurements Section */}
                    <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="px-3 py-2 border-b border-gray-200/60">
                        <div className="text-[13px] font-semibold text-gray-900">Measurements</div>
                      </div>
                      
                      {/* Measurements List */}
                      {isLoadingMeasurements ? (
                        <div className="px-3 py-4 text-center text-sm text-gray-500">
                          Loading measurements...
                        </div>
                      ) : projectMeasurements.length === 0 ? (
                        <>
                          <div className="px-3 py-4 text-center text-sm text-gray-500">
                            No measurements recorded yet
                          </div>
                          {!readOnly && (
                            <div className="px-3 py-2 border-t border-gray-200/60">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingMeasurement(null);
                                  setIsAddMeasurementModalOpen(true);
                                }}
                                className="w-full flex items-center justify-center px-3 py-2 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-[10px] text-[13px] font-medium text-gray-700 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Plus className="h-4 w-4 mr-1.5" />
                                Add Measurement
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="divide-y divide-gray-200/60">
                          {projectMeasurements.map((measurement) => (
                            <MeasurementItem
                              key={measurement.id}
                              measurement={measurement}
                              onEdit={() => {
                                setEditingMeasurement(measurement);
                                setIsAddMeasurementModalOpen(true);
                              }}
                              onDelete={() => {
                                setMeasurementToDelete(measurement);
                                setIsDeleteMeasurementModalOpen(true);
                              }}
                              canEdit={!readOnly}
                              businessId={selectedBusiness?.id || ''}
                            />
                          ))}
                        </div>
                      )}
                      
                      {/* Add Measurement Button */}
                      {!readOnly && projectMeasurements.length > 0 && (
                        <div className="px-3 py-2 border-t border-gray-200/60">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingMeasurement(null);
                              setIsAddMeasurementModalOpen(true);
                            }}
                            className="w-full flex items-center justify-center px-3 py-2 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-[10px] text-[13px] font-medium text-gray-700 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <Plus className="h-4 w-4 mr-1.5" />
                            Add Measurement
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'supplies' && (
                  <div 
                    className="space-y-4"
                    style={{
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                    }}
                  >
                    {/* Header Section */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Project Supplies</h2>
                        <p className="text-sm text-gray-500 mt-0.5">Manage supplies and orders for this project</p>
                      </div>
                      {!readOnly && (
                        <div className="relative" ref={suppliesDropdownRef}>
                        <button
                            onClick={() => setIsSuppliesDropdownOpen(!isSuppliesDropdownOpen)}
                          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-[10px] text-[13px] font-medium hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] transition-all duration-150 touch-manipulation shadow-sm"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Plus className="h-4 w-4 mr-1.5" />
                            <ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${isSuppliesDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                          {isSuppliesDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-[10px] border border-gray-200/60 shadow-lg z-50 overflow-hidden">
                              <button
                                onClick={() => {
                                  setIsSuppliesDropdownOpen(false);
                                  setIsRequestQuoteModalOpen(true);
                                }}
                                className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Request Quote
                              </button>
                              <button
                                onClick={() => {
                                  setIsSuppliesDropdownOpen(false);
                                  setIsPlaceOrderModalOpen(true);
                                }}
                                className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors border-t border-gray-100"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Place Order
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Materials Section */}
                    <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <BookingMaterialsTab
                        materials={projectMaterials as BookingMaterial[]}
                        onAddMaterial={handleAddMaterial}
                        onRemoveMaterial={handleRemoveMaterial}
                        onUpdate={async () => {
                          await loadProjectMaterials();
                          if (onUpdate) onUpdate();
                        }}
                        onError={(error: string) => toast.error(error)}
                        onOpenMaterialModal={() => setIsSelectMaterialModalOpen(true)}
                        booking={{ status: displayProject.status || 'draft' }}
                        isExpensesAppInstalled={isExpensesAppInstalled}
                        projectId={displayProject.id}
                        businessId={selectedBusiness?.id}
                      />
                    </div>

                    {/* Purchase Orders Section */}
                    <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="px-4 py-3 border-b border-gray-200/60">
                        <h3 className="text-[15px] font-semibold text-gray-900">Purchase Orders</h3>
                      </div>
                      <div className="p-3">
                        {loadingProjectOrders ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600"></div>
                      </div>
                        ) : projectOrders.length === 0 ? (
                          <div className="text-center py-8">
                            <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm text-gray-500">No purchase orders yet</p>
                            <p className="text-xs text-gray-400 mt-1">Orders created from this project will appear here</p>
                      </div>
                        ) : (() => {
                          // Group orders by supplier
                          const groupedBySupplier = projectOrders.reduce((acc, order) => {
                            const supplierId = order.supplier_id;
                            if (!acc[supplierId]) {
                              acc[supplierId] = [];
                            }
                            acc[supplierId].push(order);
                            return acc;
                          }, {} as Record<string, Order[]>);

                          return (
                            <div className="space-y-3">
                              {Object.entries(groupedBySupplier).map(([supplierId, orders]) => {
                                const supplier = orders[0]?.supplier;
                                const supplierName = supplier 
                                  ? `${supplier.first_name} ${supplier.last_name}`.trim() 
                                  : 'Unknown Supplier';

                                return (
                                  <div key={supplierId} className="space-y-2">
                                    {/* Supplier Name Label */}
                                    <div className="flex items-center gap-2 px-1">
                                      <div className="h-6 w-6 rounded-full bg-green-500 flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0">
                                        {supplierName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                      </div>
                                      <span className="text-[13px] font-semibold text-gray-900">{supplierName}</span>
                    </div>

                                    {/* Orders for this supplier */}
                                    <div className="space-y-2">
                                      {orders.map((order) => {
                                        const isExpanded = expandedOrderIds.has(order.id || '');
                                        const orderUnread = getOrderUnreadCount(order.id);
                                        const formatDate = (dateString: string) => {
                                          if (!dateString) return '';
                                          try {
                                            return format(new Date(dateString), 'MMM d');
                                          } catch {
                                            return '';
                                          }
                                        };

                                        return (
                                          <div 
                                            key={order.id} 
                                            className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-150 active:scale-[0.99]"
                                            style={{ WebkitTapHighlightColor: 'transparent' }}
                                          >
                                            {/* Order Card */}
                                            <div className="p-3">
                                              <div className="flex items-center gap-3">
                                                {/* Expand/Collapse Button */}
                                                <button
                                                  onClick={() => {
                                                    setExpandedOrderIds(prev => {
                                                      const newSet = new Set(prev);
                                                      if (newSet.has(order.id || '')) {
                                                        newSet.delete(order.id || '');
                                                      } else {
                                                        newSet.add(order.id || '');
                                                        // Load email thread when expanding
                                                        if (order.id) {
                                                          loadOrderEmailThread(order.id);
                                                        }
                                                      }
                                                      return newSet;
                                                    });
                                                  }}
                                                  className="flex-1 min-w-0 flex items-start gap-2.5"
                                                >
                                                  <div className={`flex-shrink-0 mt-0.5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                                                    <ChevronRight className="h-4 w-4 text-gray-400" />
                        </div>
                                                  <div className="flex-1 min-w-0 text-left">
                                                    {/* Top Row: PO number and items count */}
                                                    <div className="flex items-center gap-1.5">
                                                      <span className="text-[14px] font-semibold text-gray-900">
                                                        {order.order_number}
                                                      </span>
                                                      <span className="text-[12px] text-gray-500">
                                                        • {order.items?.length || 0} items
                                                      </span>
                                                    </div>
                                                    {/* Bottom Row: Amount and Date */}
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                      <span className="text-[13px] text-gray-900 font-medium">
                                                        ${order.total_amount.toFixed(2)}
                                                      </span>
                                                      {order.created_at && (
                                                        <>
                                                          <span className="text-gray-300">•</span>
                                                          <span className="text-[12px] text-gray-400">{formatDate(order.created_at)}</span>
                                                        </>
                                                      )}
                                                    </div>
                                                  </div>
                                                </button>

                                                {/* Right Side: Status Badge, Receive All & Email Button */}
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                  {/* Status Badge */}
                                                  <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${
                                                    order.status === 'pending' 
                                                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                                      : order.status === 'received'
                                                        ? 'bg-green-50 text-green-700 border border-green-200'
                                                        : 'bg-red-50 text-red-700 border border-red-200'
                                                  }`}>
                                                    {order.status === 'pending' ? 'Pending' : order.status === 'received' ? 'Received' : 'Cancelled'}
                                                  </span>

                                                  {/* Receive All Button - only for pending orders with items not fully received */}
                                                  {order.status === 'pending' && order.items?.some(item => item.status !== 'Received') && (
                          <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (order.id && order.order_number) {
                                                          handleReceiveAllOrderItems(order.id, order.order_number);
                                                        }
                                                      }}
                                                      disabled={receivingOrderId === order.id}
                                                      className={`text-[11px] px-2 py-1 rounded-full font-medium transition-colors ${
                                                        receivingOrderId === order.id
                                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                          : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 active:bg-green-200'
                                                      }`}
                                                    >
                                                      {receivingOrderId === order.id ? (
                                                        <span className="flex items-center gap-1">
                                                          <div className="animate-spin rounded-full h-3 w-3 border border-green-400 border-t-transparent"></div>
                                                          Receiving...
                                                        </span>
                                                      ) : (
                                                        'Receive All'
                                                      )}
                          </button>
                        )}

                                                  {/* Email Button */}
                                                  <button
                                                    onClick={() => {
                                                      if (order.id) {
                                                        setOrderEmailThreadModalId(order.id);
                                                        loadOrderEmailThread(order.id);
                                                      }
                                                    }}
                                                    className="relative h-8 w-8 flex items-center justify-center rounded-full bg-gray-50 text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors active:scale-95"
                                                    title="View Email Thread"
                                                  >
                                                    <Mail className="h-4 w-4" />
                                                    {orderUnread > 0 && (
                                                      <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                                                        {orderUnread > 9 ? '9+' : orderUnread}
                                                      </span>
                                                    )}
                                                  </button>
                        </div>
                      </div>
                    </div>

                                            {/* Expanded Items List */}
                                            {isExpanded && (
                                              <div className="border-t border-gray-100 bg-gray-50/80">
                                                {order.items && order.items.length > 0 ? (
                                                  order.items.map((item, idx) => (
                                                    <div 
                                                      key={item.id} 
                                                      className={`px-3 py-2.5 flex items-center justify-between ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                                                    >
                                                      <div className="flex-1 min-w-0">
                                                        <span className="text-[13px] font-medium text-gray-900 truncate block">{item.item_name}</span>
                                                        <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-500">
                                                          <span>×{item.quantity}</span>
                                                          {item.received_quantity !== undefined && item.received_quantity > 0 && item.received_quantity < item.quantity && (
                                                            <span className="text-blue-600">({item.received_quantity} received)</span>
                                                          )}
                                                          <span className="text-gray-300">•</span>
                                                          <span className="text-gray-700 font-medium">${item.total_price.toFixed(2)}</span>
                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-2">
                                                        {/* Receive Button - only for items not fully received */}
                                                        {item.status !== 'Received' && (
                          <button
                                                            onClick={() => {
                                                              if (item.id) {
                                                                handleReceiveOrderItem(item.id, item.item_name);
                                                              }
                                                            }}
                                                            disabled={receivingItemId === item.id}
                                                            className={`text-[10px] px-2 py-1 rounded-full font-medium transition-colors ${
                                                              receivingItemId === item.id
                                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 active:bg-green-200'
                                                            }`}
                                                          >
                                                            {receivingItemId === item.id ? (
                                                              <div className="animate-spin rounded-full h-3 w-3 border border-green-400 border-t-transparent"></div>
                                                            ) : (
                                                              'Receive'
                                                            )}
                          </button>
                        )}
                                                        {/* Status Badge */}
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                          item.status === 'Received' 
                                                            ? 'bg-green-100 text-green-700'
                                                            : item.status === 'Partially Received'
                                                              ? 'bg-blue-100 text-blue-700'
                                                              : 'bg-gray-100 text-gray-700'
                                                        }`}>
                                                          {item.status || 'Not Received'}
                                                        </span>
                      </div>
                        </div>
                                                  ))
                                                ) : (
                                                  <div className="px-3 py-4 text-center text-[12px] text-gray-500">
                                                    No items in this order
                                                  </div>
                                                )}
                                                
                                                {/* Order Notes if any */}
                                                {order.notes && (
                                                  <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
                                                    <span className="text-[11px] text-gray-500">Notes: </span>
                                                    <span className="text-[11px] text-gray-700">{order.notes}</span>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Supplier Quotes Section */}
                    <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="px-4 py-3 border-b border-gray-200/60">
                        <h3 className="text-[15px] font-semibold text-gray-900">Supplier Quotes</h3>
                        </div>
                      <div className="p-3">
                        {loadingQuoteRequests ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600"></div>
                          </div>
                        ) : quoteRequests.length === 0 ? (
                          <div className="text-center py-8">
                            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm text-gray-500">No quote requests yet</p>
                            <p className="text-xs text-gray-400 mt-1">Quote requests sent from this project will appear here</p>
                          </div>
                        ) : (() => {
                          // Group quote requests by supplier_id, then by quote_request_group_id
                          const groupedBySupplier = quoteRequests.reduce((acc, request) => {
                            const supplierId = request.supplier_id;
                            if (!acc[supplierId]) {
                              acc[supplierId] = [];
                            }
                            acc[supplierId].push(request);
                            return acc;
                          }, {} as Record<string, QuoteRequest[]>);

                          return (
                            <div className="space-y-3">
                              {Object.entries(groupedBySupplier).map(([supplierId, requests]) => {
                                const firstRequest = requests[0];
                                const supplier = firstRequest.supplier;
                                const supplierName = supplier 
                                  ? `${supplier.first_name} ${supplier.last_name}`.trim() 
                                  : 'Unknown Supplier';

                                // Group requests by quote_request_group_id within this supplier
                                const groupedByBatch = requests.reduce((acc, request) => {
                                  const groupId = request.quote_request_group_id || 'ungrouped';
                                  if (!acc[groupId]) {
                                    acc[groupId] = [];
                                  }
                                  acc[groupId].push(request);
                                  return acc;
                                }, {} as Record<string, QuoteRequest[]>);

                                return (
                                  <div key={supplierId} className="space-y-2">
                                    {/* Supplier Name Label */}
                                    <div className="flex items-center gap-2 px-1">
                                      <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0">
                                        {supplierName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                      </div>
                                      <span className="text-[13px] font-semibold text-gray-900">{supplierName}</span>
                                    </div>

                                    {/* Quote Groups for this supplier */}
                                    <div className="space-y-2">
                                      {Object.entries(groupedByBatch).map(([groupId, batchRequests]) => {
                                        const batchDate = batchRequests[0]?.created_at 
                                          ? format(new Date(batchRequests[0].created_at), 'MMM d')
                                          : null;
                                        const isGroupExpanded = expandedQuoteGroupIds.has(groupId);
                                        
                                        // Calculate group-level stats
                                        const itemCount = batchRequests.length;
                                        const hasMeasurementInGroup = batchRequests.some(r => !!r.measurement);
                                        const hasPhotosInGroup = batchRequests.some(r => r.measurement?.photos && r.measurement.photos.length > 0);
                                        const totalCost = batchRequests.reduce((sum, r) => sum + (r.estimated_cost || 0), 0);
                                        const allQuoted = batchRequests.every(r => r.status === 'quoted');
                                        const anyPending = batchRequests.some(r => r.status === 'pending_quote' || !r.status);
                                        const groupStatus = allQuoted ? 'quoted' : anyPending ? 'pending_quote' : 'pending';
                                        const unreadCount = getUnreadCountForGroup(groupId);

                                        return (
                                          <div 
                                            key={groupId} 
                                            className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-150 active:scale-[0.99]"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                                            {/* Quote Group Card */}
                                            <div className="p-3">
                                              <div className="flex items-center gap-3">
                                                {/* Expand/Collapse Button */}
                                                <button
                                                  onClick={() => {
                                                    setExpandedQuoteGroupIds(prev => {
                                                      const newSet = new Set(prev);
                                                      if (newSet.has(groupId)) {
                                                        newSet.delete(groupId);
                                                      } else {
                                                        newSet.add(groupId);
                                                        if (groupId !== 'ungrouped') {
                                                          loadEmailThread(groupId);
                                                        }
                                                      }
                                                      return newSet;
                                                    });
                                                  }}
                                                  className="flex-1 min-w-0 flex items-start gap-2.5"
                                                >
                                                  <div className={`flex-shrink-0 mt-0.5 transition-transform duration-200 ${isGroupExpanded ? 'rotate-90' : ''}`}>
                                                    <ChevronRight className="h-4 w-4 text-gray-400" />
                                                  </div>
                                                  <div className="flex-1 min-w-0 text-left">
                                                    {/* Top Row: Items count and icons */}
                                                    <div className="flex items-center gap-1.5">
                                                      <span className="text-[14px] font-semibold text-gray-900">
                                                        {itemCount} {itemCount === 1 ? 'Item' : 'Items'}
                                                      </span>
                                                      {hasMeasurementInGroup && (
                                                        <Ruler className="h-3.5 w-3.5 text-blue-500" />
                                                      )}
                                                      {hasPhotosInGroup && (
                                                        <ImageIcon className="h-3.5 w-3.5 text-blue-500" />
                                                      )}
                                                    </div>
                                                    {/* Bottom Row: Cost and Date */}
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                      <span className="text-[12px] text-gray-500">
                                                        {totalCost > 0 ? (
                                                          <span className="text-gray-900 font-medium">${totalCost.toFixed(2)}</span>
                                                        ) : (
                                                          <span className="text-amber-600 font-medium">Awaiting Quote</span>
                                                        )}
                                                      </span>
                                                      {batchDate && (
                                                        <>
                                                          <span className="text-gray-300">•</span>
                                                          <span className="text-[12px] text-gray-400">{batchDate}</span>
                                                        </>
                                                      )}
                                                    </div>
                                                  </div>
                                                </button>

                                                {/* Right Side: Status Badge & Action Buttons */}
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                  {/* Status Badge */}
                                                  <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${
                                                    groupStatus === 'pending_quote' 
                                                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                                      : groupStatus === 'quoted'
                                                        ? 'bg-green-50 text-green-700 border border-green-200'
                                                        : 'bg-gray-50 text-gray-600 border border-gray-200'
                                                  }`}>
                                                    {groupStatus === 'pending_quote' ? 'Pending' : groupStatus === 'quoted' ? 'Quoted' : 'Pending'}
                                                  </span>

                                                  {/* Convert to Order Button */}
                                                  {totalCost > 0 && (
                                                    <button
                                                      onClick={() => convertQuoteGroupToOrder(groupId, batchRequests)}
                                                      disabled={isConvertingToOrder && convertToOrderGroupId === groupId}
                                                      className="h-8 w-8 flex items-center justify-center rounded-full bg-green-50 text-green-600 hover:bg-green-100 active:bg-green-200 transition-colors active:scale-95 disabled:opacity-50"
                                                      title="Convert to Order"
                                                    >
                                                      {isConvertingToOrder && convertToOrderGroupId === groupId ? (
                                                        <div className="h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                                                      ) : (
                                                        <ShoppingCart className="h-4 w-4" />
                                                      )}
                                                    </button>
                                                  )}

                                                  {/* Email Button */}
                                                  {groupId !== 'ungrouped' && (
                                                    <button
                                                      onClick={() => {
                                                        setEmailThreadModalGroupId(groupId);
                                                        loadEmailThread(groupId);
                                                      }}
                                                      className="relative h-8 w-8 flex items-center justify-center rounded-full bg-gray-50 text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors active:scale-95"
                                                      title="View Email Thread"
                                                    >
                                                      <Mail className="h-4 w-4" />
                                                      {unreadCount > 0 && (
                                                        <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                                                          {unreadCount > 9 ? '9+' : unreadCount}
                                                        </span>
                                                      )}
                          </button>
                        )}
                      </div>
                        </div>
                      </div>

                                            {/* Expanded Items List */}
                                            {isGroupExpanded && (
                                              <div className="border-t border-gray-100 bg-gray-50/80">
                                                {batchRequests.map((request, idx) => {
                                                  const isItemExpanded = expandedQuoteRequestIds.has(request.id || '');
                                                  const hasMeasurement = !!request.measurement;
                                                  const hasPhotos = request.measurement?.photos && request.measurement.photos.length > 0;

                                                  return (
                                                    <div 
                                                      key={request.id} 
                                                      className={`${idx > 0 ? 'border-t border-gray-100' : ''}`}
                                                    >
                                                      {/* Item Row */}
                                                      <div className="px-3 py-2.5 flex items-center gap-2">
                                                        <button
                                                          onClick={() => {
                                                            if (request.id) {
                                                              setExpandedQuoteRequestIds(prev => {
                                                                const newSet = new Set(prev);
                                                                if (newSet.has(request.id!)) {
                                                                  newSet.delete(request.id!);
                                                                } else {
                                                                  newSet.add(request.id!);
                                                                }
                                                                return newSet;
                                                              });
                                                            }
                                                          }}
                                                          className="flex-1 min-w-0 flex items-center gap-2 text-left"
                                                        >
                                                          <div className={`flex-shrink-0 transition-transform duration-200 ${isItemExpanded ? 'rotate-90' : ''}`}>
                                                            <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
                    </div>
                                                          <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-1.5">
                                                              <span className="text-[13px] font-medium text-gray-900 truncate">{request.item_name}</span>
                                                              {hasMeasurement && <Ruler className="h-3 w-3 text-blue-500 flex-shrink-0" />}
                                                              {hasPhotos && <ImageIcon className="h-3 w-3 text-blue-500 flex-shrink-0" />}
                                                            </div>
                                                            <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-500">
                                                              <span>×{request.estimated_quantity}</span>
                                                              <span className="text-gray-300">•</span>
                                                              <span className={request.estimated_cost > 0 ? 'text-gray-700 font-medium' : 'text-amber-600'}>
                                                                {request.estimated_cost > 0 ? `$${request.estimated_cost.toFixed(2)}` : 'TBD'}
                                                              </span>
                                                            </div>
                                                          </div>
                                                        </button>
                                                        
                                                        {/* Edit Button */}
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingPriceRequestId(request.id || null);
                                                            const cost = request.estimated_cost > 0 ? request.estimated_cost.toString() : '';
                                                            const retailPrice = request.retail_price > 0 
                                                              ? request.retail_price.toString() 
                                                              : cost;
                                                            setEditingCost(cost);
                                                            setEditingRetailPrice(retailPrice);
                                                            if (request.id && !expandedQuoteRequestIds.has(request.id)) {
                                                              setExpandedQuoteRequestIds(prev => new Set([...prev, request.id!]));
                                                            }
                                                          }}
                                                          className="flex-shrink-0 h-7 px-2.5 text-[11px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors active:scale-95"
                                                        >
                                                          Edit
                                                        </button>
                      </div>

                                                      {/* Item Expanded Details */}
                                                      {isItemExpanded && (
                                                        <div className="px-3 pb-3 pt-2 border-t border-gray-100 bg-white space-y-3">
                                                          {/* Description */}
                                                          {request.description && (
                                                            <p className="text-[12px] text-gray-600 leading-relaxed">{request.description}</p>
                                                          )}

                                                          {/* Measurement Details - iOS Card Style */}
                                                          {request.measurement && (
                                                            <div className="bg-blue-50/80 rounded-[10px] p-3">
                                                              <div className="flex items-center gap-2 mb-2">
                                                                <Ruler className="h-3.5 w-3.5 text-blue-600" />
                                                                <span className="text-[12px] font-semibold text-blue-900">{request.measurement.name}</span>
                        </div>
                                                              
                                                              {/* Dimensions Row */}
                                                              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                                                                {request.measurement.length && (
                                                                  <span className="text-blue-700">L: <span className="font-medium">{request.measurement.length}{request.measurement.unit}</span></span>
                                                                )}
                                                                {request.measurement.width && (
                                                                  <span className="text-blue-700">W: <span className="font-medium">{request.measurement.width}{request.measurement.unit}</span></span>
                                                                )}
                                                                {request.measurement.height && (
                                                                  <span className="text-blue-700">H: <span className="font-medium">{request.measurement.height}{request.measurement.unit}</span></span>
                                                                )}
                                                                {request.measurement.area && (
                                                                  <span className="text-blue-700">Area: <span className="font-medium">{request.measurement.area}{request.measurement.unit}²</span></span>
                                                                )}
                                                                {request.measurement.custom_fields && Object.entries(request.measurement.custom_fields).map(([key, value]) => (
                                                                  <span key={key} className="text-blue-700">{key}: <span className="font-medium">{value}</span></span>
                                                                ))}
                      </div>
                                                              
                                                              {request.measurement.notes && (
                                                                <p className="mt-2 text-[11px] text-blue-600 italic">{request.measurement.notes}</p>
                                                              )}
                                                              
                                                              {/* Photos */}
                                                              {request.measurement.photos && request.measurement.photos.length > 0 && (
                                                                <div className="mt-2 pt-2 border-t border-blue-100/80 flex items-center gap-1.5">
                                                                  {request.measurement.photos.slice(0, 3).map((photo) => (
                                                                    <div
                                                                      key={photo.id}
                                                                      className="w-10 h-10 rounded-[6px] overflow-hidden bg-white border border-blue-200"
                                                                    >
                                                                      <img
                                                                        src={getMeasurementPhotoUrl(photo.file_path)}
                                                                        alt={photo.file_name}
                                                                        className="w-full h-full object-cover"
                                                                        onError={(e) => {
                                                                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3C/svg%3E';
                                                                        }}
                                                                      />
                    </div>
                                                                  ))}
                                                                  {request.measurement.photos.length > 3 && (
                                                                    <span className="text-[10px] text-blue-500 font-medium ml-0.5">+{request.measurement.photos.length - 3}</span>
                                                                  )}
                  </div>
                )}
              </div>
                                                          )}

                                                          {/* Pricing Section - iOS Style */}
                                                          <div className="bg-gray-50 rounded-[10px] p-3">
                                                            <div className="flex items-center justify-between">
                                                              <span className="text-[12px] font-semibold text-gray-700">Pricing</span>
                                                              {editingPriceRequestId !== request.id ? (
                                                                <div className="flex items-center gap-3">
                                                                  <div className="text-right">
                                                                    <div className="text-[11px] text-gray-500">Cost</div>
                                                                    <div className="text-[13px] font-semibold text-gray-900">
                                                                      {request.estimated_cost > 0 ? `$${request.estimated_cost.toFixed(2)}` : '—'}
            </div>
          </div>
                                                                  <div className="text-right">
                                                                    <div className="text-[11px] text-gray-500">Retail</div>
                                                                    <div className="text-[13px] font-semibold text-gray-900">
                                                                      {request.retail_price > 0 ? `$${request.retail_price.toFixed(2)}` : '—'}
                                                                    </div>
                                                                  </div>
                                                                </div>
                                                              ) : (
                                                                <div className="flex items-center gap-2">
                                                                  <button
                                                                    onClick={async () => {
                                                                      if (!request.id || !selectedBusiness?.id) return;
                                                                      setSavingPrice(true);
                                                                      try {
                                                                        const cost = editingCost ? parseFloat(editingCost) : 0;
                                                                        const retailPrice = editingRetailPrice ? parseFloat(editingRetailPrice) : 0;
                                                                        
                                                                        await updateQuoteRequest(request.id, {
                                                                          estimated_cost: cost,
                                                                          retail_price: retailPrice,
                                                                          status: cost > 0 || retailPrice > 0 ? 'quoted' : 'pending_quote'
                                                                        });
                                                                        
                                                                        await loadQuoteRequests();
                                                                        setEditingPriceRequestId(null);
                                                                        setEditingCost('');
                                                                        setEditingRetailPrice('');
                                                                        toast.success('Price updated');
                                                                      } catch (error) {
                                                                        console.error('Error updating price:', error);
                                                                        toast.error('Failed to update price');
                                                                      } finally {
                                                                        setSavingPrice(false);
                                                                      }
                                                                    }}
                                                                    disabled={savingPrice}
                                                                    className="text-[11px] font-medium text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
                                                                  >
                                                                    {savingPrice ? 'Saving...' : 'Save'}
                                                                  </button>
                                                                  <button
                                                                    onClick={() => {
                                                                      setEditingPriceRequestId(null);
                                                                      setEditingCost('');
                                                                      setEditingRetailPrice('');
                                                                    }}
                                                                    className="text-[11px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
                                                                  >
                                                                    Cancel
                                                                  </button>
                                                                </div>
                                                              )}
                                                            </div>
                                                            
                                                            {/* Edit Form */}
                                                            {editingPriceRequestId === request.id && (
                                                              <div className="mt-3 grid grid-cols-2 gap-3">
                                                                <div>
                                                                  <label className="block text-[10px] font-medium text-gray-500 mb-1">Cost</label>
                                                                  <div className="relative">
                                                                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[12px]">$</span>
                                                                    <input
                                                                      type="number"
                                                                      step="0.01"
                                                                      min="0"
                                                                      value={editingCost}
                                                                      onChange={(e) => {
                                                                        const newCost = e.target.value;
                                                                        setEditingCost(newCost);
                                                                        const currentCostNum = editingCost ? parseFloat(editingCost) : 0;
                                                                        const currentRetailNum = editingRetailPrice ? parseFloat(editingRetailPrice) : 0;
                                                                        if (!editingRetailPrice || currentRetailNum === currentCostNum) {
                                                                          setEditingRetailPrice(newCost);
                                                                        }
                                                                      }}
                                                                      placeholder="0.00"
                                                                      className="w-full pl-6 pr-2 py-2 text-[13px] border border-gray-200 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                                                    />
                                                                  </div>
                                                                </div>
                                                                <div>
                                                                  <label className="block text-[10px] font-medium text-gray-500 mb-1">Retail</label>
                                                                  <div className="relative">
                                                                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[12px]">$</span>
                                                                    <input
                                                                      type="number"
                                                                      step="0.01"
                                                                      min={editingCost || "0"}
                                                                      value={editingRetailPrice}
                                                                      onChange={(e) => {
                                                                        const newRetail = e.target.value;
                                                                        const costNum = editingCost ? parseFloat(editingCost) : 0;
                                                                        const retailNum = newRetail ? parseFloat(newRetail) : 0;
                                                                        if (newRetail && retailNum < costNum) {
                                                                          setEditingRetailPrice(editingCost);
                                                                        } else {
                                                                          setEditingRetailPrice(newRetail);
                                                                        }
                                                                      }}
                                                                      placeholder="0.00"
                                                                      className="w-full pl-6 pr-2 py-2 text-[13px] border border-gray-200 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                                                    />
                                                                  </div>
                                                                </div>
                                                              </div>
                                                            )}
                                                          </div>
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render using portal for proper z-index stacking
  return (
    <>
      {createPortal(modalContent, document.body)}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${project?.title || 'this project'}"? This action cannot be undone.`}
        loading={isDeleting}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Select Customer Modal */}
      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={handleCustomerSelect}
      />

      <SelectMaterialModal
        isOpen={isSelectMaterialModalOpen}
        onClose={() => setIsSelectMaterialModalOpen(false)}
        onAdd={handleAddMaterial}
        businessId={selectedBusiness?.id}
      />

      {/* Edit Customer Modal */}
      <EditCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => {
          setIsEditCustomerModalOpen(false);
          // Refresh customer data after update
          const currentProject = localProject || project;
          if (currentProject?.customer_id) {
            const refreshCustomer = async () => {
              try {
                const customerData = await getCustomer(currentProject.customer_id!);
                setCustomer(customerData);
              } catch (error) {
                console.error('Error refreshing customer:', error);
              }
            };
            refreshCustomer();
          }
        }}
        onSuccess={() => {
          setIsEditCustomerModalOpen(false);
          // Refresh customer data after update
          const currentProject = localProject || project;
          if (currentProject?.customer_id) {
            const refreshCustomer = async () => {
              try {
                const customerData = await getCustomer(currentProject.customer_id!);
                setCustomer(customerData);
              } catch (error) {
                console.error('Error refreshing customer:', error);
              }
            };
            refreshCustomer();
          }
          if (onUpdate) {
            onUpdate();
          }
        }}
        customer={customer}
      />

      {/* Create Customer Source Modal */}
      {isCreateSourceModalOpen && (
        <div 
          className="fixed inset-0 z-[100200] flex items-center justify-center p-4"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}
          onClick={() => setIsCreateSourceModalOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div 
            className="relative bg-white rounded-2xl shadow-xl w-[90%] max-w-[400px] p-5"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New Source</h3>
              <button
                type="button"
                onClick={() => {
                  setIsCreateSourceModalOpen(false);
                  setNewSourceName('');
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Source Name
              </label>
              <input
                type="text"
                value={newSourceName}
                onChange={e => setNewSourceName(e.target.value)}
                placeholder="e.g., Google Ads, Referral, Yelp"
                className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsCreateSourceModalOpen(false);
                  setNewSourceName('');
                }}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCustomerSource}
                disabled={!newSourceName.trim() || createSourceLoading}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createSourceLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Proposal Modal */}
      <CreateProposalModal
        isOpen={isCreateProposalModalOpen}
        onClose={() => setIsCreateProposalModalOpen(false)}
        onSuccess={async (createdProposal) => {
          setIsCreateProposalModalOpen(false);
          
          // Update proposal_id and customer if proposal was created
          if (createdProposal?.id && displayProject?.id) {
            try {
              // Prepare update object
              const updateData: {
                proposal_id: string;
                customer_id?: string;
                updated_at: string;
              } = {
                proposal_id: createdProposal.id,
                updated_at: new Date().toISOString()
              };

              // If project has no customer and proposal has a customer, assign the customer to the project
              if (!displayProject.customer_id && createdProposal.customer_id) {
                updateData.customer_id = createdProposal.customer_id;
              }

              // Update project in database
              const { error: updateError } = await supabase
                .from('projects')
                .update(updateData)
                .eq('id', displayProject.id);

              if (updateError) {
                console.error('Error updating project:', updateError);
                toast.error('Proposal created but failed to update project');
              } else {
                // Update local project state
                const updatedProject = {
                  ...displayProject,
                  proposal_id: createdProposal.id,
                  ...(updateData.customer_id && { customer_id: updateData.customer_id })
                };
                setLocalProject(updatedProject);

                // If customer was assigned, fetch and update customer state
                if (updateData.customer_id) {
                  try {
                    const customerData = await getCustomer(updateData.customer_id);
                    setCustomer(customerData);
                  } catch (error) {
                    console.error('Error fetching assigned customer:', error);
                  }
                }

                toast.success('Proposal created and project updated');
              }
            } catch (error) {
              console.error('Error updating project:', error);
              toast.error('Proposal created but failed to update project');
            }
          } else {
            toast.success('Proposal created successfully');
          }

          // Fetch the full proposal data and open proposal details modal
          if (createdProposal?.id) {
            try {
              const fullProposal = await getProposal(createdProposal.id);
              setProposal(fullProposal);
              setIsProposalDetailsModalOpen(true);
            } catch (error) {
              console.error('Error fetching created proposal:', error);
              // Still try to open with the created proposal data
              setProposal(createdProposal);
              setIsProposalDetailsModalOpen(true);
            }
          }

          if (onUpdate) {
            onUpdate();
          }
        }}
        initialCustomer={customer || undefined}
        initialProjectId={displayProject?.id || undefined}
      />

      {/* Proposal Details Modal */}
      {proposal && (
        <ProposalDetailsModal
          isOpen={isProposalDetailsModalOpen && proposal !== null}
          onClose={() => {
            setIsProposalDetailsModalOpen(false);
            // Refresh proposal data after update
            const currentProject = localProject || project;
            if (currentProject?.proposal_id) {
              const refreshProposal = async () => {
                try {
                  const proposalData = await getProposal(currentProject.proposal_id!);
                  setProposal(proposalData);
                } catch (error) {
                  console.error('Error refreshing proposal:', error);
                }
              };
              refreshProposal();
            }
          }}
          proposal={proposal}
          onUpdate={async (updatedProposal) => {
            setProposal(updatedProposal);
            if (onUpdate) {
              onUpdate();
            }
          }}
          readOnly={readOnly}
        />
      )}

      {/* Schedule Modal */}
      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        hideScheduleQuote={!!fieldQuote}
        hideScheduleMeasurements={!!measurementFieldQuote}
        onScheduleQuote={() => {
          setFieldQuoteForSchedule(null);
          setIsFieldQuoteMeasurementMode(false);
          setIsScheduleFieldQuoteModalOpen(true);
        }}
        onScheduleMeasurements={() => {
          setFieldQuoteForSchedule(null);
          setIsFieldQuoteMeasurementMode(true);
          setIsScheduleFieldQuoteModalOpen(true);
        }}
      />

      {/* Schedule Field Quote Modal */}
      <ScheduleFieldQuoteModal
        isOpen={isScheduleFieldQuoteModalOpen}
        onClose={() => setIsScheduleFieldQuoteModalOpen(false)}
        projectId={displayProject?.id || null}
        projectTitle={displayProject?.title || null}
        mode={fieldQuoteForSchedule ? 'edit' : 'create'}
        isMeasurement={isFieldQuoteMeasurementMode}
        fieldQuoteToEdit={
          fieldQuoteForSchedule
            ? {
                id: fieldQuoteForSchedule.id,
                start_date: fieldQuoteForSchedule.start_date,
                start_time: fieldQuoteForSchedule.start_time,
                end_date: fieldQuoteForSchedule.end_date,
                end_time: fieldQuoteForSchedule.end_time,
                field_quote_address: fieldQuoteForSchedule.field_quote_address || null,
                resource: fieldQuoteForSchedule.resource
              }
            : null
        }
        onSuccess={() => {
          // Refresh field quote and measurement cards without closing the modal
          setFieldQuoteRefreshKey(prev => prev + 1);
          onUpdate?.();
        }}
      />

      {/* Select Vehicle Modal */}
      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => {
          setIsSelectVehicleModalOpen(false);
          setCurrentVehicle(null);
        }}
        onSelect={(vehicle) => {
          setCurrentVehicle(vehicle);
          setIsSelectVehicleModalOpen(false);
          setIsSelectServiceModalOpen(true);
        }}
        vehicles={customer?.vehicles || []}
        selectedVehicles={[]}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      {/* Create Vehicle Modal */}
      <CreateVehicleModal
        isOpen={isCreateVehicleModalOpen}
        onClose={() => {
          setIsCreateVehicleModalOpen(false);
          // Refresh customer data to get new vehicle
          if (customer?.id) {
            const refreshCustomer = async () => {
              try {
                const customerData = await getCustomer(customer.id);
                setCustomer(customerData);
              } catch (error) {
                console.error('Error refreshing customer:', error);
              }
            };
            refreshCustomer();
          }
        }}
        onSuccess={async (newVehicle) => {
          setIsCreateVehicleModalOpen(false);
          // Refresh customer data to get new vehicle
          if (customer?.id) {
            try {
              const customerData = await getCustomer(customer.id);
              setCustomer(customerData);
              // Auto-select the new vehicle and open service modal
              setCurrentVehicle(newVehicle);
              setIsSelectServiceModalOpen(true);
            } catch (error) {
              console.error('Error refreshing customer:', error);
            }
          }
        }}
        customerId={customer?.id || undefined}
        businessId={selectedBusiness?.id}
      />

      {/* Select Service Modal */}
      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => {
          setIsSelectServiceModalOpen(false);
          setCurrentVehicle(null);
        }}
        onAdd={async (service, priceOption) => {
          // Check industry - for Home Services, vehicle is optional
          const isHomeServices = selectedBusiness?.industry === 'Home Services';
          
          if (!isHomeServices && !currentVehicle) {
            toast.error('No vehicle selected');
            return;
          }

          if (!displayProject?.id) {
            toast.error('No project selected');
            return;
          }
          
          try {
            // Add service to project
            await addProjectService({
              project_id: displayProject.id,
              service_id: service.id,
              vehicle_id: currentVehicle?.id || null,
              name: service.name,
              description: service.description || null,
              price: priceOption.price,
              tax_exempt: priceOption.tax_exempt || false,
              tax_rate: priceOption.tax_rate || 0,
              duration_minutes: service.duration_minutes || 60,
              price_option_id: priceOption.id || null,
            });

            // Refresh services list
            const services = await getProjectServices(displayProject.id);
            setProjectServices(services);
            
            toast.success(`Service "${service.name}" added to project`);
            setIsSelectServiceModalOpen(false);
            setCurrentVehicle(null);
            
            // Refresh project data if needed
            if (onUpdate) {
              onUpdate();
            }
          } catch (error) {
            console.error('Error adding service to project:', error);
            toast.error('Failed to add service to project');
          }
        }}
        vehicleId={currentVehicle?.id}
      />

      {/* Delete Service Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteServiceModalOpen}
        onClose={() => {
          setIsDeleteServiceModalOpen(false);
          setServiceToDelete(null);
        }}
        onConfirm={async () => {
          if (!serviceToDelete) return;
          try {
            setDeleteServiceLoading(true);
            await removeProjectService(serviceToDelete.id);
            if (displayProject?.id) {
              const services = await getProjectServices(displayProject.id);
              setProjectServices(services);
            }
            setIsDeleteServiceModalOpen(false);
            setServiceToDelete(null);
            toast.success('Service removed');
            if (onUpdate) {
              onUpdate();
            }
          } catch (error) {
            console.error('Error removing service:', error);
            toast.error('Failed to remove service');
          } finally {
            setDeleteServiceLoading(false);
          }
        }}
        title="Remove Service"
        message={`Are you sure you want to remove "${serviceToDelete?.name}" from this project? This action cannot be undone.`}
        loading={deleteServiceLoading}
        confirmText="Remove"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Add/Edit Measurement Modal */}
      <AddMeasurementModal
        isOpen={isAddMeasurementModalOpen}
        onClose={async () => {
          setIsAddMeasurementModalOpen(false);
          setEditingMeasurement(null);
          
          // Refresh measurements list when modal closes (in case AI created measurements)
          const currentProject = localProject || project;
          if (currentProject?.id) {
            try {
              const measurements = await getProjectMeasurements(currentProject.id);
              setProjectMeasurements(measurements);
            } catch (error) {
              console.error('Error refreshing measurements:', error);
            }
          }
        }}
        onSave={async (measurementData) => {
          const currentProject = localProject || project;
          if (!currentProject?.id) return;

          try {
            if (editingMeasurement) {
              // Update existing measurement
              await updateProjectMeasurement(editingMeasurement.id, measurementData as UpdateProjectMeasurementParams);
            } else {
              // Add new measurement
              await addProjectMeasurement(measurementData as AddProjectMeasurementParams);
            }

            // Refresh measurements list
            const measurements = await getProjectMeasurements(currentProject.id);
            setProjectMeasurements(measurements);
          } catch (error) {
            console.error('Error saving measurement:', error);
            throw error;
          }
        }}
        measurement={editingMeasurement}
        projectId={displayProject?.id || ''}
        businessId={selectedBusiness?.id || ''}
      />

      {/* Delete Measurement Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteMeasurementModalOpen}
        onClose={() => {
          setIsDeleteMeasurementModalOpen(false);
          setMeasurementToDelete(null);
        }}
        onConfirm={async () => {
          if (!measurementToDelete) return;
          const currentProject = localProject || project;
          
          try {
            setDeleteMeasurementLoading(true);
            await deleteProjectMeasurement(measurementToDelete.id);
            
            if (currentProject?.id) {
              const measurements = await getProjectMeasurements(currentProject.id);
              setProjectMeasurements(measurements);
            }
            
            setIsDeleteMeasurementModalOpen(false);
            setMeasurementToDelete(null);
            toast.success('Measurement deleted successfully');
          } catch (error) {
            console.error('Error deleting measurement:', error);
            toast.error('Failed to delete measurement');
          } finally {
            setDeleteMeasurementLoading(false);
          }
        }}
        title="Delete Measurement"
        message={`Are you sure you want to delete "${measurementToDelete?.name}"? This action cannot be undone.`}
        loading={deleteMeasurementLoading}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Edit Project Title Modal */}
      {isEditTitleModalOpen && project && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[999999] transition-opacity duration-300"
          onClick={() => setIsEditTitleModalOpen(false)}
        >
          <div 
            className="fixed inset-y-0 right-0 w-full md:w-[400px] bg-white transform transition-transform duration-300 ease-out translate-x-0"
            onClick={e => e.stopPropagation()}
            style={{
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            }}
          >
            <div className="h-full flex flex-col">
              <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Edit Title</h2>
                <button 
                  onClick={() => setIsEditTitleModalOpen(false)} 
                  className="text-gray-400 hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveTitle();
                }} 
                className="flex-1 overflow-y-auto p-6"
              >
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Title
                    </label>
                    <input
                      type="text"
                      required
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter project title"
                      autoFocus
                    />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setIsEditTitleModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSavingTitle || !editTitle.trim() || editTitle.trim() === displayProject.title}
                      className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50"
                    >
                      {isSavingTitle ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create Booking Modal - For Project to Booking Conversion */}
      <CreateBookingModal
        isOpen={isCreateBookingModalOpen}
        onClose={() => setIsCreateBookingModalOpen(false)}
        onSuccess={() => {
          setIsCreateBookingModalOpen(false);
          // The bookingCreated event will handle the linking
        }}
        initialProposal={convertProjectToProposalFormat()}
        initialCustomer={customer}
        initialCustomerSource={(localProject || project)?.customer_source || null}
      />

      {/* Create Booking Modal - For Project to Booking Conversion - Render in portal to ensure it's above ProjectInfoModal */}
      {createPortal(
        <CreateBookingModal
          isOpen={isCreateBookingModalOpen}
          onClose={() => setIsCreateBookingModalOpen(false)}
          onSuccess={() => {
            setIsCreateBookingModalOpen(false);
            // The bookingCreated event will handle the linking and refresh
          }}
          initialProposal={convertProjectToProposalFormat()}
          initialCustomer={customer}
          initialCustomerSource={(localProject || project)?.customer_source || null}
          initialOpportunityId={(localProject || project)?.pipeline_opportunities_id || null}
        />,
        document.body
      )}

      {createPortal(
        <CreateMobileBookingModal
          isOpen={isCreateMobileBookingModalOpen}
          onClose={() => setIsCreateMobileBookingModalOpen(false)}
          onSuccess={() => {
            setIsCreateMobileBookingModalOpen(false);
          }}
          prefilledData={convertProjectToMobilePrefill()}
          initialProjectId={(localProject || project)?.id || null}
          initialOpportunityId={(localProject || project)?.pipeline_opportunities_id || null}
        />,
        document.body
      )}

      <BookingTypeSelectionModal
        isOpen={isBookingTypeSelectionOpen}
        onClose={() => setIsBookingTypeSelectionOpen(false)}
        onSelectMobileBooking={() => {
          setIsBookingTypeSelectionOpen(false);
          setIsCreateMobileBookingModalOpen(true);
        }}
        onSelectLocationBooking={() => {
          setIsBookingTypeSelectionOpen(false);
          setIsCreateBookingModalOpen(true);
        }}
        onSelectConvertProject={() => setIsBookingTypeSelectionOpen(false)}
        showMobileBooking={showMobileBookingSetting}
        showLocationBooking={showLocationBookingSetting}
        showConvertProject={false}
      />

      {/* Request Quote Modal */}
      <RequestQuoteFromProjectModal
        isOpen={isRequestQuoteModalOpen}
        onClose={() => setIsRequestQuoteModalOpen(false)}
        projectId={project?.id || null}
        projectTitle={project?.title || null}
        onRequestQuote={() => {
          // Refresh quote requests after sending
          if (project?.id && selectedBusiness?.id) {
            loadQuoteRequests();
          }
          updateProjectStatus('Pending');
        }}
        onRequestQuoteFromMeasurements={() => {
          // Refresh quote requests after sending
          if (project?.id && selectedBusiness?.id) {
            loadQuoteRequests();
          }
          updateProjectStatus('Pending');
        }}
      />

      {/* Place Order Modal */}
      <RequestQuoteModal
        isOpen={isPlaceOrderModalOpen}
        onClose={() => setIsPlaceOrderModalOpen(false)}
        projectId={project?.id || null}
        onOrderExistingInventory={() => {
          setIsPlaceOrderModalOpen(false);
          setIsCreateOrderModalOpen(true);
        }}
        onPlaceOrderFromMeasurements={() => {
          setIsPlaceOrderModalOpen(false);
          setIsPlaceOrderFromMeasurementsModalOpen(true);
        }}
        title="Place Order"
        subtitle="Choose how you want to place an order"
        showCustomOrder={false}
        showCustomOrderFromMeasurements={false}
        showPlaceOrderFromMeasurements={true}
      />

      {/* Create Order Modal */}
      <CreateOrderModal
        isOpen={isCreateOrderModalOpen}
        onClose={() => setIsCreateOrderModalOpen(false)}
        items={stockItems}
        onOrderQuantities={onOrderQuantities}
        projectId={project?.id || null}
        onOrderCreated={() => updateProjectStatus('Pending')}
        onItemsRefresh={async () => {
          await loadOnOrderQuantities();
          await loadStockItems();
        }}
      />

      {/* Place Order From Measurements Modal */}
      <PlaceOrderFromMeasurementsModal
        isOpen={isPlaceOrderFromMeasurementsModalOpen}
        onClose={() => setIsPlaceOrderFromMeasurementsModalOpen(false)}
        projectId={project?.id || null}
        projectTitle={project?.title || null}
        onOrderCreated={() => {
          // Refresh project orders after creating order
          if (project?.id && selectedBusiness?.id) {
            loadProjectOrders();
          }
          updateProjectStatus('Pending');
        }}
      />

      {/* Email Thread Modal - Rendered in separate portal */}
      {emailThreadModalGroupId && createPortal(
        <div className="fixed inset-0 z-[100050] flex items-center justify-center p-4" style={{ zIndex: 100050, paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setEmailThreadModalGroupId(null);
              setReplyMessage('');
            }}
          />
          
          {/* Modal Content - Full Screen Mobile Optimized */}
          <div className="relative bg-white w-full h-full flex flex-col z-[100060]" style={{ zIndex: 100060 }}>
            {/* Header - Mobile Optimized */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0 bg-white safe-area-top">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900">Email Thread</h2>
              <button
                onClick={() => {
                  setEmailThreadModalGroupId(null);
                  setReplyMessage('');
                }}
                className="p-2 -mr-2 text-gray-400 hover:text-gray-600 transition-colors active:scale-95 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {/* Email Thread Content - Mobile Optimized */}
            <div 
              ref={emailThreadScrollRef} 
              className="flex-1 overflow-y-auto px-3 sm:px-6 py-4"
              style={{ scrollBehavior: 'auto', WebkitOverflowScrolling: 'touch' }}
            >
              {loadingEmailThreads[emailThreadModalGroupId] ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
                </div>
              ) : emailThreads[emailThreadModalGroupId] && emailThreads[emailThreadModalGroupId].length > 0 ? (
                <div className="space-y-4 sm:space-y-6 max-w-5xl mx-auto">
                  {emailThreads[emailThreadModalGroupId].map((email) => {
                    const isOutbound = email.direction === 'outbound';
                    const timestamp = email.sent_at || email.received_at || email.created_at;
                    
                    const isUnread = !isOutbound && email.read === 'unread' && !locallyReadEmails.has(email.id);
                    
                    return (
                      <div
                        key={email.id}
                        className={`relative rounded-lg p-3 sm:p-6 ${
                          isOutbound
                            ? 'bg-blue-50 border border-blue-200 ml-2 sm:ml-12'
                            : 'bg-gray-50 border border-gray-200 mr-2 sm:mr-12'
                        }`}
                      >
                        {/* Unread Badge - Only show for inbound unread emails */}
                        {isUnread && (
                          <button
                            onClick={() => markEmailAsRead(email.id, email.all_messages_id, email.external_id)}
                            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white text-[10px] sm:text-xs font-semibold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full transition-all duration-200 active:scale-[0.98] cursor-pointer shadow-lg z-10"
                            title="Mark as read"
                          >
                            Unread
                          </button>
                        )}
                        {/* Email Header - Mobile Optimized (Stacked on mobile) */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 sm:mb-3 gap-1 sm:gap-3">
                          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                            <span className={`text-sm sm:text-base font-semibold ${
                              isOutbound ? 'text-blue-700' : 'text-gray-700'
                            }`}>
                              {isOutbound ? 'You' : 'Supplier'}
                            </span>
                            <span className="text-xs sm:text-base text-gray-500 truncate max-w-[150px] sm:max-w-none">
                              {email.email_address}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {timestamp && (
                              <span className="text-xs sm:text-base text-gray-500 flex-shrink-0">
                                {format(new Date(timestamp), 'MMM d, h:mm a')}
                              </span>
                            )}
                          </div>
                        </div>
                        {email.subject && (
                          <div className="text-sm sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 break-words">
                            {email.subject.replace(/\[QUOTE-[^\]]+\]/gi, '').trim() || '(No Subject)'}
                          </div>
                        )}
                        <div 
                          className="text-sm sm:text-base text-gray-700 prose prose-sm sm:prose-base max-w-none break-words overflow-hidden"
                          style={{ wordBreak: 'break-word' }}
                          dangerouslySetInnerHTML={{ 
                            __html: isOutbound 
                              ? (email.body_html || email.content)
                              : stripQuotedContent(email.body_html || email.content, !!email.body_html)
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Mail className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm sm:text-base text-gray-500">No email thread yet</p>
                  </div>
                </div>
              )}
            </div>

            {/* Reply Box - Fixed at bottom - Mobile Optimized */}
            <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50 flex-shrink-0 safe-area-bottom">
              <div className="max-w-5xl mx-auto">
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply..."
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  rows={3}
                  disabled={isSendingReply}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      if (!isSendingReply && replyMessage.trim()) {
                        handleSendReply();
                      }
                    }
                  }}
                />
                <div className="flex items-center justify-between mt-2 sm:mt-3">
                  <span className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">
                    Press Cmd/Ctrl + Enter to send
                  </span>
                  <button
                    onClick={handleSendReply}
                    disabled={!replyMessage.trim() || isSendingReply}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm sm:text-base font-medium"
                  >
                    {isSendingReply ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Send Reply</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Order Email Thread Modal - Rendered in separate portal */}
      {orderEmailThreadModalId && createPortal(
        <div className="fixed inset-0 z-[100050] flex items-center justify-center p-4" style={{ zIndex: 100050, paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setOrderEmailThreadModalId(null);
              setOrderReplyMessage('');
            }}
          />
          
          {/* Modal Content - Full Screen Mobile Optimized */}
          <div className="relative bg-white w-full h-full flex flex-col z-[100060]" style={{ zIndex: 100060 }}>
            {/* Header - Mobile Optimized */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0 bg-white safe-area-top">
              <div className="min-w-0 flex-1 mr-4">
                <h2 className="text-base sm:text-xl font-semibold text-gray-900 truncate">Order Email Thread</h2>
                {(() => {
                  const order = projectOrders.find(o => o.id === orderEmailThreadModalId);
                  return order ? (
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">{order.order_number} • {order.supplier?.first_name} {order.supplier?.last_name}</p>
                  ) : null;
                })()}
              </div>
              <button
                onClick={() => {
                  setOrderEmailThreadModalId(null);
                  setOrderReplyMessage('');
                }}
                className="flex-shrink-0 p-2 -mr-2 text-gray-400 hover:text-gray-600 transition-colors active:scale-95 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {/* Email Thread Content - Mobile Optimized */}
            <div 
              ref={orderEmailThreadScrollRef} 
              className="flex-1 overflow-y-auto px-3 sm:px-6 py-4"
              style={{ scrollBehavior: 'auto', WebkitOverflowScrolling: 'touch' }}
            >
              {loadingOrderEmailThreads[orderEmailThreadModalId] ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
                </div>
              ) : orderEmailThreads[orderEmailThreadModalId] && orderEmailThreads[orderEmailThreadModalId].length > 0 ? (
                <div className="space-y-4 sm:space-y-6 max-w-5xl mx-auto">
                  {orderEmailThreads[orderEmailThreadModalId].map((email) => {
                    const isOutbound = email.direction === 'outbound';
                    const timestamp = email.sent_at || email.received_at || email.created_at;
                    const isUnread = !isOutbound && email.read === 'unread' && !locallyReadEmails.has(email.id);
                    
                    return (
                      <div
                        key={email.id}
                        className={`relative rounded-lg p-3 sm:p-6 ${
                          isOutbound
                            ? 'bg-blue-50 border border-blue-200 ml-2 sm:ml-12'
                            : 'bg-gray-50 border border-gray-200 mr-2 sm:mr-12'
                        }`}
                      >
                        {/* Unread Badge */}
                        {isUnread && (
                          <button
                            onClick={() => markEmailAsRead(email.id, email.all_messages_id, email.external_id)}
                            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white text-[10px] sm:text-xs font-semibold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full transition-all duration-200 active:scale-[0.98] cursor-pointer shadow-lg z-10"
                            title="Mark as read"
                          >
                            Unread
                          </button>
                        )}
                        {/* Email Header - Mobile Optimized (Stacked on mobile) */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 sm:mb-3 gap-1 sm:gap-3">
                          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                            <span className={`text-sm sm:text-base font-semibold ${
                              isOutbound ? 'text-blue-700' : 'text-gray-700'
                            }`}>
                              {isOutbound ? 'You' : 'Supplier'}
                            </span>
                            <span className="text-xs sm:text-base text-gray-500 truncate max-w-[150px] sm:max-w-none">
                              {email.email_address}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {timestamp && (
                              <span className="text-xs sm:text-base text-gray-500 flex-shrink-0">
                                {format(new Date(timestamp), 'MMM d, h:mm a')}
                              </span>
                            )}
                          </div>
                        </div>
                        {email.subject && (
                          <div className="text-sm sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 break-words">
                            {email.subject.replace(/\[ORDER-[^\]]+\]/gi, '').trim() || '(No Subject)'}
                          </div>
                        )}
                        <div 
                          className="text-sm sm:text-base text-gray-700 prose prose-sm sm:prose-base max-w-none break-words overflow-hidden"
                          style={{ wordBreak: 'break-word' }}
                          dangerouslySetInnerHTML={{ 
                            __html: isOutbound 
                              ? (email.body_html || email.content)
                              : stripQuotedContent(email.body_html || email.content, !!email.body_html)
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Mail className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-4" />
                  <p className="text-base sm:text-lg font-medium">No emails yet</p>
                  <p className="text-xs sm:text-sm mt-1 text-center px-4">Start a conversation by sending a message to the supplier</p>
                </div>
              )}
            </div>

            {/* Reply Box - Fixed at bottom - Mobile Optimized */}
            <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50 flex-shrink-0 safe-area-bottom">
              <div className="max-w-5xl mx-auto">
                <textarea
                  value={orderReplyMessage}
                  onChange={(e) => setOrderReplyMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  rows={3}
                  disabled={isSendingOrderReply}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      if (!isSendingOrderReply && orderReplyMessage.trim()) {
                        handleSendOrderReply();
                      }
                    }
                  }}
                />
                <div className="flex items-center justify-between mt-2 sm:mt-3">
                  <span className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">
                    Press Cmd/Ctrl + Enter to send
                  </span>
                  <button
                    onClick={handleSendOrderReply}
                    disabled={!orderReplyMessage.trim() || isSendingOrderReply}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm sm:text-base font-medium"
                  >
                    {isSendingOrderReply ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Convert Quote to Order Summary Modal */}
      {convertToOrderData && (
        <OrderSummaryPDFModal
          isOpen={!!convertToOrderData}
          onClose={() => {
            setConvertToOrderData(null);
            setConvertToOrderGroupId(null);
            lastCreatedOrderRef.current = null;
            // Refresh project orders when modal closes
            loadProjectOrders();
            loadOrderUnreadCounts();
          }}
          supplier={convertToOrderData.supplier}
          items={convertToOrderData.items.map(item => ({
            item: item.item,
            quantityToOrder: item.quantityToOrder,
            totalCost: item.totalCost,
            measurement: item.measurement
          }))}
          totalAmount={convertToOrderData.totalAmount}
          onSendOrder={handleSendConvertedOrder}
          onEmailSent={handleOrderEmailSent}
          onItemsChange={(updatedItems, newTotal) => {
            // Update the order data with new quantities
            setConvertToOrderData(prev => {
              if (!prev) return null;
              return {
                ...prev,
                items: updatedItems.map((updated, idx) => ({
                  ...prev.items[idx],
                  quantityToOrder: updated.quantityToOrder,
                  totalCost: updated.totalCost
                })),
                totalAmount: newTotal
              };
            });
          }}
        />
      )}
    </>
  );
}
